/* global Sanki */
/**
 * Sanki — main.js
 * Orchestrates all interactive surfaces in the storefront:
 *   • Cart drawer (open/close, SSR hydration, qty controls, remove, quick-add)
 *   • Wishlist (toggle + nav count)
 *   • Predictive search
 *   • Product page (variant picking, gallery, qty, ATC)
 *   • Delivery estimator
 *
 * All handlers are idempotent and re-initialize on `shopify:section:load`
 * so the theme editor stays interactive while merchants edit sections.
 */
(function (global) {
  'use strict';

  const Sanki = (global.Sanki = global.Sanki || {});
  const H = Sanki.helpers || {};
  const API = Sanki.api || {};

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  /* -- Mobile nav drawer ----------------------------------------------------*/

  function initMobileDrawer() {
    const drawer = document.querySelector('[data-sk-menu-drawer]');
    const backdrop = document.querySelector('[data-sk-menu-backdrop]');
    const toggle = document.querySelector('[data-sk-menu-toggle]');
    if (!drawer || !backdrop || !toggle) return;

    // Guard against double-binding on shopify:section:load.
    if (drawer.dataset.skDrawerInit === '1') return;
    drawer.dataset.skDrawerInit = '1';

    let disposeTrap = null;

    const open = () => {
      drawer.hidden = false;
      backdrop.hidden = false;
      // Force reflow so CSS transitions catch the state change.
      drawer.getBoundingClientRect();
      drawer.classList.add('is-open');
      backdrop.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      disposeTrap = H.trapFocus ? H.trapFocus(drawer) : null;
    };

    const close = () => {
      drawer.classList.remove('is-open');
      backdrop.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      setTimeout(() => { drawer.hidden = true; backdrop.hidden = true; }, 380);
      if (disposeTrap) { disposeTrap(); disposeTrap = null; }
      toggle.focus();
    };

    toggle.addEventListener('click', (e) => { e.preventDefault(); open(); });
    backdrop.addEventListener('click', close);
    drawer.querySelectorAll('[data-sk-menu-close]').forEach((b) => b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    // Close when navigating via a link inside the drawer.
    drawer.querySelectorAll('a[href]').forEach((a) => {
      a.addEventListener('click', () => {
        // Only close for in-page anchors or routable links (not target=_blank socials).
        if (a.target !== '_blank') setTimeout(close, 120);
      });
    });

    // Expand/collapse per-item sublists. Handles BOTH the standard
    // `.sk-mobile-drawer__sublist` (admin-configured menu sub-items) AND
    // the new `.sk-mobile-drawer__mega` panel that mirrors the desktop
    // Collections mega-dropdown for the "Shop" / "Collections" link.
    drawer.querySelectorAll('[data-sk-mobile-expand]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const item = btn.closest('[data-sk-mobile-item]');
        if (!item) return;
        const sub = item.querySelector('.sk-mobile-drawer__mega, .sk-mobile-drawer__sublist');
        if (!sub) return;
        const isOpen = item.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        if (isOpen) {
          sub.hidden = false;
          sub.getBoundingClientRect();
          sub.style.maxHeight = sub.scrollHeight + 'px';
        } else {
          sub.style.maxHeight = '0px';
          setTimeout(() => { if (!item.classList.contains('is-expanded')) sub.hidden = true; }, 260);
        }
      });
    });
  }

  /* -- Collection filters ---------------------------------------------------*/

  /** Color-name vocabulary used to derive colours from product titles. */
  const SK_COLOR_VOCAB = ['black','white','cream','pink','purple','green','fuchsia','blue','red','yellow','grey','gray','navy','brown','orange','beige','olive','maroon','lilac','ivory','magenta','teal','mustard'];

  /**
   * Derive a clothing-type slug. Mirrors the Liquid snippet `sk-derive-type`
   * 1:1 — see that file for the full taxonomy / cascade documentation. Logic
   * was validated against the full 1,059-product Sannki inventory: the
   * product_type lookup catches 650, the title fallback catches 164, and
   * everything else (245 — JACKET / SHACKET / CO-ORD SET / SHORT / GIFT /
   * PERFUME / BAGS / KURTA PAJAMA / BEDSHEET / BLANKET / BLAZER / SHOES /
   * etc.) intentionally lands in 'accessories'.
   */
  const SK_TYPE_MAP = {
    'T-SHIRT': 't-shirts',
    'SANDO': 't-shirts',
    'SHIRT': 'shirts',
    'SWEATSHIRT': 'summer-hoods',
    'WOMEN SWEATSHIRT': 'summer-hoods',
    'HOODIE': 'summer-hoods',
    'SWEATER': 'summer-hoods',
    'WOMEN SWEATER': 'summer-hoods',
    'JEANS': 'jeans',
    'TROUSER': 'trousers',
    'JOGGER': 'trousers',
    'LOWERS': 'lowers'
  };
  function skDeriveType(product) {
    const pt = String(product.product_type || '').trim().toUpperCase();
    if (SK_TYPE_MAP[pt]) return SK_TYPE_MAP[pt];

    if (!pt) {
      const t = String(product.title || '').toLowerCase();
      if (t.includes('lower') || t.includes('sankilower')) return 'lowers';
      if (t.includes('jean') || t.includes('denim')) return 'jeans';
      if (t.includes('trouser') || t.includes('sankitrouser') || t.includes('chino') || t.includes('jogger')) return 'trousers';
      if (t.includes('hoodie') || t.includes('hooded') || t.includes('sweatshirt') || t.includes('sweat shirt') || t.includes('pullover') || t.includes('sweater')) return 'summer-hoods';
      if (t.includes('t-shirt') || t.includes('tshirt') || t.includes('t shirt') || t.includes(' tee ') || t.includes(' tee.') || t.includes('oversized tee') || t.includes('sando')) return 't-shirts';
      if (t.includes('shirt')) return 'shirts';
    }

    return 'accessories';
  }

  /**
   * Build the wrapper <div> that the catalog filter engine reads. Mirrors
   * what product-grid.liquid emits for server-rendered cards so that hydrated
   * cards are filtered, sorted and paginated by exactly the same code path.
   */
  function renderCollectionCardWrap(rawProduct) {
    const p = normalizeProduct(rawProduct);
    if (!p) return null;

    // Derive filter metadata.
    const colorSet = new Set();
    const sizeSet  = new Set();
    const options = rawProduct.options || [];
    const variants = rawProduct.variants || [];
    options.forEach((opt, idx) => {
      const name = (typeof opt === 'string' ? opt : (opt && opt.name) || '').toLowerCase();
      const key  = 'option' + (idx + 1);
      if (name === 'color' || name === 'colour') {
        variants.forEach((v) => { if (v && v[key]) colorSet.add(String(v[key]).toLowerCase()); });
      } else if (name === 'size') {
        variants.forEach((v) => { if (v && v[key]) sizeSet.add(String(v[key])); });
      }
    });
    SK_COLOR_VOCAB.forEach((c) => {
      if ((rawProduct.title || '').toLowerCase().includes(c)) colorSet.add(c);
    });

    const tags = Array.isArray(rawProduct.tags) ? rawProduct.tags : [];
    const isNew = tags.some((t) => /^new$/i.test(String(t)));
    const onSale = p.compareCents > 0 && p.compareCents > p.priceCents;
    const created = rawProduct.created_at
      ? Math.floor(new Date(rawProduct.created_at).getTime() / 1000)
      : 0;
    const derivedType = skDeriveType(rawProduct);

    const wrap = document.createElement('div');
    wrap.setAttribute('data-sk-reveal', '');
    wrap.setAttribute('data-sk-card-wrap', '');
    wrap.dataset.productType = derivedType;
    wrap.dataset.colors      = Array.from(colorSet).join('|');
    wrap.dataset.sizes       = Array.from(sizeSet).join('|');
    wrap.dataset.price       = String(Math.round(p.priceCents / 100));
    wrap.dataset.available   = String(p.available);
    wrap.dataset.onSale      = String(onSale);
    wrap.dataset.isNew       = String(isNew);
    wrap.dataset.createdAt   = String(created);
    wrap.dataset.title       = (rawProduct.title || '').toLowerCase();
    wrap.innerHTML = renderTypeRowCard(p);
    return wrap;
  }

  /**
   * Client-side filtering / sorting engine for the collection grid.
   *
   * Why client-side (not Shopify's filter URL params)?
   *   The `filter.v.*` params only work when the merchant has enabled the
   *   storefront filters in admin. We can't depend on that, so we read
   *   metadata from the rendered card data-attributes and toggle visibility
   *   in real time.
   *
   * Why client-side pagination?
   *   Liquid caps `paginate by` at 50, so a 1000-item collection would
   *   spread across many pages — and filters on each page would only see
   *   that page's slice. We hydrate the rest of the catalogue from
   *   /collections/{handle}/products.json, then paginate the visible
   *   (post-filter) cards in the browser.
   *
   * Also wires:
   *   - Active-filter chips (above the grid, with × to remove each)
   *   - Sort dropdown (re-orders cards in the DOM)
   *   - Mobile filter drawer toggle + Esc close
   */
  function initCollectionFilters() {
    const root = document.querySelector('[data-sk-catalog]');
    if (!root || root.dataset.skFiltersInit === '1') return;
    root.dataset.skFiltersInit = '1';

    const form        = root.querySelector('[data-sk-collection-form]');
    const grid        = root.querySelector('[data-sk-products-grid]');
    const initialCards = grid ? Array.from(grid.querySelectorAll('[data-sk-card-wrap]')) : [];
    const emptyState  = root.querySelector('[data-sk-grid-empty]');
    const countLabel  = root.querySelector('[data-sk-result-count]');
    const chipBar     = root.querySelector('[data-sk-active-filters]');
    const sortSelect  = root.querySelector('[data-sk-sort]');
    const clearAll    = root.querySelectorAll('[data-sk-filter-clear]');
    const paginationNav = root.querySelector('[data-sk-pagination]');
    if (!form || !grid || !initialCards.length) return;

    const PAGE_SIZE = parseInt(grid.dataset.skPageSize, 10) || 24;
    let currentPage = 1;

    /* ────────────────────────────────────────────────────────────────────
       URL-PARAM TYPE FILTER (drives the dropdown navigation: clicking
       "T-Shirts" in the Collections mega-dropdown lands on
       /collections/all?type=t-shirts and this filter activates on load.)

       The Type filter group used to live as checkboxes in the sidebar;
       we removed it so type selection is now a navigation concern, not
       a filter UI concern. To make the dropdown links actually filter
       on landing, we mirror the URL param into a closure-level array
       (`urlTypeFilter`) that readState() merges into its types[] list.

       Multiple types can be passed as either repeated params
       (?type=t-shirts&type=shirts) or comma-joined (?type=t-shirts,shirts).
       Both forms are normalised to lowercase trimmed slugs.

       Removal: the chip-remove handler below also clears the matching
       slug from `urlTypeFilter` AND updates the URL via history.replaceState
       so refreshes don't re-apply the chip the user just dismissed.
    ──────────────────────────────────────────────────────────────────── */
    let urlTypeFilter = [];
    function readUrlTypeFilter() {
      try {
        const params = new URLSearchParams(window.location.search);
        const raw = params.getAll('type');           // repeated form
        const joined = raw.join(',');                // collapse + comma form
        return joined.toLowerCase()
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      } catch (e) { return []; }
    }
    urlTypeFilter = readUrlTypeFilter();

    /** Pretty-label map for chips / page heading when a URL type is active. */
    const TYPE_LABELS = {
      't-shirts':     'T-Shirts',
      'summer-hoods': 'Summer Hoods',
      'shirts':       'Shirts',
      'trousers':     'Trousers',
      'lowers':       'Lowers',
      'jeans':        'Jeans',
      'accessories':  'Accessories'
    };
    function prettyTypeLabel(slug) {
      return TYPE_LABELS[slug] || slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    }

    /** Mutate the address bar without reloading; keeps `urlTypeFilter`
        in sync with the URL when the user dismisses a type chip. Also
        re-syncs the catalog page heading so it reflects the current
        filter state. */
    function syncUrlTypes() {
      try {
        const params = new URLSearchParams(window.location.search);
        params.delete('type');
        urlTypeFilter.forEach((t) => params.append('type', t));
        const next = window.location.pathname + (params.toString() ? '?' + params.toString() : '') + window.location.hash;
        window.history.replaceState(null, '', next);
      } catch (e) { /* swallow — older browsers */ }
      updateHeadingFromUrlTypes();
    }

    /** Re-paint the `<header class="sk-collection__head">` so the
        eyebrow + title reflect the active URL type filter. Falls back
        to the merchant-managed Liquid defaults when `urlTypeFilter` is
        empty.

        Top Wear vs Bottom Wear vs Accessories grouping mirrors the
        Collections mega-dropdown structure in header.liquid. */
    const TYPE_GROUPS = {
      't-shirts':     'Top Wear',
      'summer-hoods': 'Top Wear',
      'shirts':       'Top Wear',
      'trousers':     'Bottom Wear',
      'lowers':       'Bottom Wear',
      'jeans':        'Bottom Wear',
      'accessories':  'Accessories'
    };
    const eyebrowEl = root.querySelector('[data-sk-collection-eyebrow]');
    const titleEl   = root.querySelector('[data-sk-collection-title]');
    const initialEyebrow = eyebrowEl ? eyebrowEl.textContent : '';
    const initialTitle   = titleEl   ? titleEl.textContent   : '';
    function updateHeadingFromUrlTypes() {
      if (!eyebrowEl || !titleEl) return;
      if (urlTypeFilter.length === 0) {
        eyebrowEl.textContent = initialEyebrow;
        titleEl.textContent   = initialTitle;
        return;
      }
      // One type → group eyebrow + pretty title.
      // Multiple types → "Collections" eyebrow + comma-joined titles.
      if (urlTypeFilter.length === 1) {
        const slug = urlTypeFilter[0];
        eyebrowEl.textContent = 'Collections · ' + (TYPE_GROUPS[slug] || 'Browse');
        titleEl.textContent   = prettyTypeLabel(slug);
      } else {
        eyebrowEl.textContent = 'Collections';
        titleEl.textContent   = urlTypeFilter.map(prettyTypeLabel).join(' · ');
      }
    }
    updateHeadingFromUrlTypes();


    /** Build a metadata snapshot for one card element (used for filtering). */
    function makeCardEntry(el) {
      return {
        el: el,
        type:      (el.dataset.productType || '').toLowerCase(),
        colors:   ((el.dataset.colors || '').toLowerCase().split('|').filter(Boolean)),
        // Sizes lowercased so they survive the lowercase pass readState() does
        // on checked filter values. The displayed label keeps original case.
        sizes:    ((el.dataset.sizes || '').toLowerCase().split('|').filter(Boolean)),
        price:     parseInt(el.dataset.price, 10) || 0,
        available: el.dataset.available === 'true',
        onSale:    el.dataset.onSale === 'true',
        isNew:     el.dataset.isNew === 'true',
        created:   parseInt(el.dataset.createdAt, 10) || 0,
        title:     (el.dataset.title || '').toLowerCase()
      };
    }

    // Cache static metadata per card for fast filtering. Held in a `const`
    // array so we can mutate in place when more products are hydrated.
    const cardData = initialCards.map(makeCardEntry);
    const productIdOf = (el) => {
      // Wrapper itself (hydrated cards) or descendant article (Liquid cards).
      if (el.dataset && el.dataset.productId) return el.dataset.productId;
      const a = el.querySelector('[data-product-id]');
      return a ? a.getAttribute('data-product-id') : '';
    };
    const seenIds = new Set(initialCards.map(productIdOf).filter(Boolean));

    function readState() {
      const checked = (sel) =>
        Array.from(form.querySelectorAll('input[data-sk-filter="' + sel + '"]:checked'))
          .map((i) => i.value.toLowerCase());
      // Merge URL-driven type filter with any (legacy) checkbox-driven types.
      // De-duplicate so the same slug doesn't get counted twice if both
      // sources push it.
      const formTypes = checked('type');
      const allTypes = Array.from(new Set([].concat(urlTypeFilter, formTypes)));
      return {
        types:        allTypes,
        colors:       checked('color'),
        sizes:        checked('size'),
        availability: form.querySelector('input[data-sk-filter="availability"]:checked'),
        sale:         form.querySelector('input[data-sk-filter="sale"]:checked'),
        isNew:        form.querySelector('input[data-sk-filter="new"]:checked'),
        priceMin:     parseInt((form.querySelector('input[data-sk-filter="price.min"]') || {}).value, 10),
        priceMax:     parseInt((form.querySelector('input[data-sk-filter="price.max"]') || {}).value, 10)
      };
    }

    function matches(card, st) {
      if (st.types.length && !st.types.includes(card.type)) return false;
      if (st.colors.length && !st.colors.some((c) => card.colors.includes(c))) return false;
      if (st.sizes.length  && !st.sizes.some((s) =>  card.sizes.includes(s)))  return false;
      if (st.availability  && !card.available) return false;
      if (st.sale          && !card.onSale)    return false;
      if (st.isNew         && !card.isNew)     return false;
      if (Number.isFinite(st.priceMin) && card.price < st.priceMin) return false;
      if (Number.isFinite(st.priceMax) && card.price > st.priceMax) return false;
      return true;
    }

    function apply(opts) {
      const st = readState();
      let visible = 0;
      cardData.forEach((c) => {
        const ok = matches(c, st);
        c.el.classList.toggle('is-filtered-out', !ok);
        if (ok) visible++;
      });
      if (countLabel) {
        countLabel.textContent = visible + ' product' + (visible === 1 ? '' : 's');
      }
      // Mirror the count into the mobile drawer's sticky Apply CTA so
      // the user always sees how many products their current filters
      // returned without having to dismiss the panel first. The hook
      // is attached to root by initCollectionFilters once the drawer
      // is wired; if it's not present (desktop, or before hydration)
      // we just no-op.
      if (typeof root.__skUpdateFilterApplyLabel === 'function') {
        root.__skUpdateFilterApplyLabel(visible);
      }
      if (emptyState) emptyState.hidden = visible !== 0;
      renderChips(st);
      // Reset to page 1 on every filter change unless the caller asks us not
      // to (e.g. silent re-apply after hydration finishes).
      if (!opts || !opts.preservePage) currentPage = 1;
      paginate();

    }

    /**
     * Show only the slice of post-filter cards that belongs to currentPage.
     * Reads DOM order (which applySort can reshuffle), so pagination always
     * reflects the user's chosen sort. Cards outside the slice get
     * `is-paged-out`. The pagination nav is rebuilt on every call.
     */
    function paginate() {
      const allEls = Array.from(grid.querySelectorAll('[data-sk-card-wrap]'));
      const visibleEls = allEls.filter((el) => !el.classList.contains('is-filtered-out'));
      const totalPages = Math.max(1, Math.ceil(visibleEls.length / PAGE_SIZE));
      if (currentPage > totalPages) currentPage = totalPages;
      const start = (currentPage - 1) * PAGE_SIZE;
      const end   = start + PAGE_SIZE;
      allEls.forEach((el) => el.classList.remove('is-paged-out'));
      visibleEls.forEach((el, i) => {
        if (i < start || i >= end) el.classList.add('is-paged-out');
      });
      renderPaginationNav(totalPages, visibleEls.length);
    }

    function renderPaginationNav(totalPages, totalItems) {
      if (!paginationNav) return;
      if (totalPages <= 1) {
        paginationNav.innerHTML = '';
        paginationNav.hidden = true;
        return;
      }
      paginationNav.hidden = false;
      const parts = [];
      if (currentPage > 1) {
        parts.push('<button type="button" class="sk-btn sk-btn--outline" data-sk-page="prev">← Prev</button>');
      }
      // Compact numeric range — first, current ±2, last, with ellipses.
      const nums = new Set([1, totalPages, currentPage]);
      for (let i = -2; i <= 2; i++) {
        const n = currentPage + i;
        if (n >= 1 && n <= totalPages) nums.add(n);
      }
      const sorted = Array.from(nums).sort((a, b) => a - b);
      let prev = 0;
      sorted.forEach((n) => {
        if (n - prev > 1) parts.push('<span class="sk-muted" style="padding: 0 6px;">…</span>');
        const cls = (n === currentPage) ? 'sk-btn sk-btn--primary' : 'sk-btn sk-btn--outline';
        parts.push('<button type="button" class="' + cls + '" data-sk-page="' + n + '" aria-current="' + (n === currentPage ? 'page' : 'false') + '">' + n + '</button>');
        prev = n;
      });
      if (currentPage < totalPages) {
        parts.push('<button type="button" class="sk-btn sk-btn--outline" data-sk-page="next">Next →</button>');
      }
      paginationNav.innerHTML = parts.join('');
    }

    // Pagination clicks (event delegation on the nav).
    if (paginationNav) {
      paginationNav.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-sk-page]');
        if (!btn) return;
        const v = btn.dataset.skPage;
        if (v === 'prev')      currentPage = Math.max(1, currentPage - 1);
        else if (v === 'next') currentPage = currentPage + 1;
        else                   currentPage = parseInt(v, 10) || 1;
        paginate();
        // Bring the grid back to the top so the user sees the new page.
        try { grid.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
        catch (_) { window.scrollTo({ top: grid.offsetTop - 80, behavior: 'smooth' }); }
      });
    }

    function renderChips(st) {
      if (!chipBar) return;
      const chips = [];
      const addChip = (label, kind, value) => {
        chips.push(
          '<button type="button" class="sk-chip sk-chip--filter" data-sk-chip-remove'
          + ' data-kind="' + kind + '" data-value="' + value + '">'
          + escapeHtml(label)
          + '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round"/></svg>'
          + '</button>'
        );
      };
      // Type chips use the friendly TYPE_LABELS map (e.g. 't-shirts' →
      // 'T-Shirts', 'summer-hoods' → 'Summer Hoods') so URL-driven type
      // filters render with the same display text as if the user had
      // ticked a checkbox for them.
      st.types.forEach((t)  => addChip(prettyTypeLabel(t), 'type', t));
      st.colors.forEach((c) => addChip(c.charAt(0).toUpperCase() + c.slice(1), 'color', c));
      st.sizes.forEach((s)  => addChip('Size ' + s, 'size', s));
      if (st.availability)  addChip('In stock', 'availability', 'in-stock');
      if (st.sale)          addChip('On sale', 'sale', 'yes');
      if (st.isNew)         addChip('New', 'new', 'yes');
      if (Number.isFinite(st.priceMin) || Number.isFinite(st.priceMax)) {
        const lo = Number.isFinite(st.priceMin) ? '₹' + st.priceMin : '';
        const hi = Number.isFinite(st.priceMax) ? '₹' + st.priceMax : '';
        const range = lo && hi ? lo + '–' + hi : (lo ? 'From ' + lo : 'Up to ' + hi);
        addChip(range, 'price', '*');
      }
      if (chips.length) {
        chips.push('<button type="button" class="sk-chip sk-chip--clear" data-sk-filter-clear>Clear all</button>');
        chipBar.innerHTML = chips.join('');
        chipBar.hidden = false;
      } else {
        chipBar.innerHTML = '';
        chipBar.hidden = true;
      }
    }

    function applySort() {
      if (!sortSelect) return;
      const mode = sortSelect.value;
      const visible = cardData.filter((c) => !c.el.classList.contains('is-filtered-out'));
      const cmp = {
        'manual':              () => 0,
        'best-selling':        () => 0,                       // best-selling order is the server render order
        'price-ascending':     (a, b) => a.price - b.price,
        'price-descending':    (a, b) => b.price - a.price,
        'created-descending':  (a, b) => b.created - a.created,
        'title-ascending':     (a, b) => a.title.localeCompare(b.title),
        'title-descending':    (a, b) => b.title.localeCompare(a.title)
      }[mode] || (() => 0);
      const sorted = visible.slice().sort(cmp);
      sorted.forEach((c) => grid.appendChild(c.el));
      // Re-paginate after sort so the new ordering shows up correctly.
      paginate();
    }

    /**
     * Walk the cardData and add any newly-discovered Type / Colour / Size
     * options to the existing filter checklists. Called after every batch of
     * hydrated products so the sidebar stays in sync with the catalogue.
     */
    function refreshFilterOptions() {
      const seenTypes  = new Set();
      const seenColors = new Set();
      const seenSizes  = new Set();
      cardData.forEach((c) => {
        if (c.type) seenTypes.add(c.type);
        c.colors.forEach((x) => seenColors.add(x));
        c.sizes.forEach((x)  => seenSizes.add(x));
      });

      const addOptionIfMissing = (kind, value, label, decorator) => {
        const existing = form.querySelector('input[data-sk-filter="' + kind + '"][value="' + cssEscapeAttr(value) + '"]');
        if (existing) return;
        // Find the <ul> for this filter group.
        const sample = form.querySelector('input[data-sk-filter="' + kind + '"]');
        if (!sample) return;
        const ul = sample.closest('ul');
        if (!ul) return;
        const li = document.createElement('li');
        const lab = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.dataset.skFilter = kind;
        input.value = value;
        lab.appendChild(input);
        if (decorator) lab.appendChild(decorator());
        lab.appendChild(document.createTextNode(' ' + label));
        li.appendChild(lab);
        ul.appendChild(li);
      };

      seenTypes.forEach((t) => {
        const label = t.charAt(0).toUpperCase() + t.slice(1).replace(/-/g, ' ')
          .replace(/^t shirts$/, 'T-Shirts')
          .replace(/^Co ord sets$/i, 'Co-ord Sets');
        addOptionIfMissing('type', t, label);
      });
      seenColors.forEach((c) => {
        addOptionIfMissing('color', c, c.charAt(0).toUpperCase() + c.slice(1), () => {
          const dot = document.createElement('span');
          dot.className = 'sk-filter-color-dot';
          dot.dataset.color = c;
          dot.setAttribute('aria-hidden', 'true');
          return dot;
        });
      });
      seenSizes.forEach((s) => addOptionIfMissing('size', s, s));
    }

    function cssEscapeAttr(v) {
      // Minimal escape for use inside an attribute selector value.
      return String(v).replace(/(["\\])/g, '\\$1');
    }

    /**
     * Hydrate the rest of the collection from /collections/{handle}/products.json.
     *
     * Optimised for large catalogues:
     *   • Reads `data-sk-collection-total` to compute the exact number of
     *     pages up-front, then fires every fetch in PARALLEL via
     *     Promise.allSettled. A 1,059-product catalogue needs 5 requests
     *     (250 per page) which run concurrently rather than sequentially —
     *     the wall-clock drops from ~5s to ~1s.
     *   • Caches the merged products list in sessionStorage keyed by
     *     `(handle, total)` so subsequent navigations within the same tab
     *     hydrate instantly (0ms, no network) until the storefront's total
     *     count changes.
     *   • Inserts cards in DOM order using a single document fragment per
     *     batch, then re-applies filter+pagination once at the end so the
     *     UI doesn't thrash.
     */
    async function hydrateRemaining() {
      const handle = grid.dataset.skCollectionHandle || 'all';
      const reportedTotal = parseInt(grid.dataset.skCollectionTotal, 10) || 0;
      const PER = 250;

      if (reportedTotal && cardData.length >= reportedTotal) return;

      // Cache key reflects the catalogue's snapshot — change in total count
      // invalidates the cache automatically.
      const cacheKey = 'sk:catalog:' + handle + ':' + reportedTotal;
      let allItems = null;
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) allItems = JSON.parse(cached);
      } catch (_) { /* sessionStorage might be disabled */ }

      if (!allItems) {
        // Compute exact number of pages — saves wasted parallel requests
        // beyond the catalogue's actual size. Defaults to 5 pages
        // (1,250 products) when total isn't reported.
        const pages = reportedTotal
          ? Math.ceil(reportedTotal / PER)
          : 5;

        const requests = [];
        for (let pageNum = 1; pageNum <= pages; pageNum++) {
          const url = '/collections/' + encodeURIComponent(handle) + '/products.json'
                    + '?limit=' + PER + '&page=' + pageNum;
          // The collection.liquid template emits a matching
          // <link rel="preload" fetchpriority="high"> for each of these
          // URLs during HTML parse, so by the time we call fetch() the
          // response is usually already in the HTTP cache and resolves
          // synchronously. priority: 'high' here is a belt-and-braces
          // hint for browsers that don't pick up the preload signal.
          requests.push(
            fetch(url, {
              credentials: 'same-origin',
              headers: { Accept: 'application/json' },
              priority: 'high'
            })
              .then((res) => res.ok ? res.json() : { products: [] })
              .then((j) => (j && j.products) || [])
              .catch(() => [])
          );
        }

        const pagesData = await Promise.all(requests);
        allItems = pagesData.flat();
        // Persist for next page-load. Wrap in try/catch — sessionStorage
        // can throw QuotaExceededError on big catalogues in some browsers.
        try { sessionStorage.setItem(cacheKey, JSON.stringify(allItems)); }
        catch (_) { /* swallow — cache miss next time, no functional impact */ }
      }

      // De-dupe against initially-rendered cards and build one DOM fragment
      // for the whole batch.
      //
      // CRITICAL — pre-filter EVERY hydrated card BEFORE it's appended to
      // the DOM. Without this, cards land in the live DOM with no
      // `is-filtered-out` class, the browser opportunistically paints
      // them, then the next apply() pass hides the non-matching ones —
      // producing the visible flash the user reported on type-filtered
      // landings (e.g. /collections/all?type=t-shirts).
      //
      // The fix: snapshot the current filter state once, then evaluate
      // every new card against it inline and pre-stamp the filter class.
      // By the time grid.appendChild() runs, every non-matching card
      // already has display:none from `.is-filtered-out`, so the browser
      // never paints them.
      const filterState = readState();
      const fragment = document.createDocumentFragment();
      let added = 0;
      for (const p of allItems) {
        const id = String(p.id);
        if (seenIds.has(id)) continue;
        p._priceUnit = 'major';
        const el = renderCollectionCardWrap(p);
        if (!el) continue;
        el.setAttribute('data-product-id', id);
        // Hydrated cards must be visible immediately — same reasoning as the
        // initialCards pass above. [data-sk-reveal]'s opacity:0 default is
        // intended for scroll-driven sections, not for the catalogue grid.
        el.classList.add('is-visible');

        // Pre-filter: evaluate this card against the current filter state
        // and pre-stamp `is-filtered-out` BEFORE the element enters the DOM.
        // No flash possible — the card lands already-hidden if it doesn't
        // match the active filter (e.g. URL ?type=t-shirts).
        const cardEntry = makeCardEntry(el);
        if (!matches(cardEntry, filterState)) {
          el.classList.add('is-filtered-out');
        }

        fragment.appendChild(el);
        seenIds.add(id);
        cardData.push(cardEntry);
        added++;
      }
      if (added) {
        // Single appendChild = one DOM mutation regardless of fragment
        // size. Fast even for 1,000+ cards. Every card in the fragment
        // is already pre-filtered above, so the browser only paints the
        // matching subset.
        grid.appendChild(fragment);
        refreshFilterOptions();
        // Re-run apply() once for chip count / pagination / empty-state
        // reconciliation — the visual filter state is already correct,
        // so this is a metadata-only sweep, not a re-paint.
        apply({ preservePage: true });
      }
    }

    // Wire up form changes (debounce price inputs, instant for the rest).
    let priceTimer;
    form.addEventListener('input', (e) => {
      const t = e.target;
      if (!(t instanceof HTMLElement)) return;
      if (t.dataset.skFilter === 'price.min' || t.dataset.skFilter === 'price.max') {
        clearTimeout(priceTimer);
        priceTimer = setTimeout(apply, 280);
      }
    });
    form.addEventListener('change', apply);

    /**
     * Cross-page "in stock only" preference.
     *
     * Persisted in localStorage so the PDP can read it and hide colour
     * chips / gallery media for sold-out colours when the visitor is
     * actively filtering by availability. Independent of the per-
     * collection sessionStorage snapshot — this is a global UX
     * preference that travels with the customer across pages.
     */
    /**
     * Persist the "In stock only" preference globally so the PDP can
     * honour it. Default behaviour is ON (hide sold-out colours
     * everywhere). Writing '0' here is the explicit "show sold-out
     * items" opt-out; '1' (or null) means ON.
     */
    function persistInStockOnlyPref() {
      try {
        const cb = form.querySelector('input[data-sk-filter="availability"][value="in-stock"]');
        if (!cb) return;
        if (cb.checked) {
          localStorage.setItem('sanki:filter:in-stock-only', '1');
        } else {
          localStorage.setItem('sanki:filter:in-stock-only', '0');
        }
      } catch (_) { /* quota / blocked storage */ }
    }
    /* Hydrate the checkbox from the persisted preference. Default ON
       — first-time visitors land with the filter active so sold-out
       colours are hidden everywhere out of the box. */
    try {
      const stored = localStorage.getItem('sanki:filter:in-stock-only');
      const cb = form.querySelector('input[data-sk-filter="availability"][value="in-stock"]');
      if (cb) {
        const shouldBeOn = stored === null ? true : stored !== '0';
        if (shouldBeOn !== cb.checked) {
          cb.checked = shouldBeOn;
          // Re-run the catalog filter so the SSR grid matches.
          apply({ preservePage: true });
        }
      }
    } catch (_) {}
    /* Hook every change. Runs alongside the existing apply() call. */
    form.addEventListener('change', persistInStockOnlyPref);

    if (sortSelect) sortSelect.addEventListener('change', () => {
      currentPage = 1;
      applySort();
    });

    // Clear-all (multiple targets: panel header + empty state + chip bar).
    // Also clears the URL-driven type filter and rewrites the address bar
    // so a refresh doesn't re-apply the cleared filter.
    function resetAllFilters() {
      form.querySelectorAll('input').forEach((inp) => {
        if (inp.type === 'checkbox' || inp.type === 'radio') inp.checked = false;
        else if (inp.type === 'number' || inp.type === 'search') inp.value = '';
      });
      if (urlTypeFilter.length) {
        urlTypeFilter = [];
        syncUrlTypes();
      }
      apply();
    }
    clearAll.forEach((b) => b.addEventListener('click', resetAllFilters));

    // Removable chip clicks (event-delegation on chipBar). Type chips are
    // dual-aware: they un-tick the matching checkbox if one exists AND
    // remove the slug from `urlTypeFilter` (with URL rewrite) so URL-
    // driven types and form-driven types both clear cleanly.
    if (chipBar) {
      chipBar.addEventListener('click', (e) => {
        const target = e.target.closest('[data-sk-chip-remove], [data-sk-filter-clear]');
        if (!target) return;
        if (target.matches('[data-sk-filter-clear]')) {
          resetAllFilters();
          return;
        }
        const kind  = target.dataset.kind;
        const value = target.dataset.value;
        if (kind === 'price') {
          const lo = form.querySelector('input[data-sk-filter="price.min"]');
          const hi = form.querySelector('input[data-sk-filter="price.max"]');
          if (lo) lo.value = '';
          if (hi) hi.value = '';
        } else {
          // Always try the checkbox path (no-op if no matching input).
          const cb = form.querySelector('input[data-sk-filter="' + kind + '"][value="' + value + '"]');
          if (cb) cb.checked = false;
          // For type chips, also remove from the URL-driven list.
          if (kind === 'type' && value) {
            const before = urlTypeFilter.length;
            urlTypeFilter = urlTypeFilter.filter((t) => t !== value);
            if (urlTypeFilter.length !== before) syncUrlTypes();
          }
        }
        apply();
      });
    }

    // ── Mobile filter drawer ────────────────────────────────────────
    // Three close paths must all work, every time, on every device:
    //   1. The X button in the panel header
    //   2. Tapping the dimmed backdrop outside the panel
    //   3. The "Show N products" CTA at the bottom of the drawer
    // Plus Escape on hardware keyboards. We toggle a body class
    // (sk-filter-open) instead of mutating inline overflow so the
    // styling is centralized in CSS and easy to audit.
    const filterToggle    = root.querySelector('[data-sk-filter-toggle]');
    const filterPanel     = root.querySelector('[data-sk-filter-panel]');
    const filterClose     = root.querySelector('[data-sk-filter-close]');
    const filterBackdrop  = root.querySelector('[data-sk-filter-backdrop]');
    const filterApply     = root.querySelector('[data-sk-filter-apply]');
    const filterApplyLbl  = root.querySelector('[data-sk-filter-apply-label]');

    // Update the Apply CTA label to reflect the current visible count.
    // Called from apply() below via the visibleCountForApplyCta hook.
    function updateApplyLabel(count) {
      if (!filterApplyLbl) return;
      if (typeof count !== 'number') count = (countLabel
        ? parseInt(countLabel.textContent, 10) || 0
        : 0);
      filterApplyLbl.textContent = count === 0
        ? 'No products match'
        : 'Show ' + count + ' product' + (count === 1 ? '' : 's');
    }
    // Expose to outer scope so apply() can call it.
    root.__skUpdateFilterApplyLabel = updateApplyLabel;
    updateApplyLabel();

    if (filterToggle && filterPanel) {
      const openPanel = () => {
        filterPanel.classList.add('is-open');
        if (filterBackdrop) {
          filterBackdrop.hidden = false;
          // Force a reflow so the transition runs from opacity:0.
          // eslint-disable-next-line no-unused-expressions
          filterBackdrop.offsetWidth;
          filterBackdrop.classList.add('is-open');
        }
        document.body.classList.add('sk-filter-open');
        // Refresh the Apply label every time the panel opens so the
        // count is current even if filters changed in another tab.
        updateApplyLabel();
      };
      const closePanel = () => {
        filterPanel.classList.remove('is-open');
        if (filterBackdrop) {
          filterBackdrop.classList.remove('is-open');
          // Hide after the fade-out transition so it can't intercept
          // taps while invisible.
          setTimeout(() => {
            if (!filterPanel.classList.contains('is-open')) {
              filterBackdrop.hidden = true;
            }
          }, 320);
        }
        document.body.classList.remove('sk-filter-open');
      };

      filterToggle.addEventListener('click', openPanel);
      if (filterClose)    filterClose.addEventListener('click', closePanel);
      if (filterBackdrop) filterBackdrop.addEventListener('click', closePanel);
      if (filterApply)    filterApply.addEventListener('click', closePanel);

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && filterPanel.classList.contains('is-open')) closePanel();
      });
    }

    // Server-rendered cards live inside [data-sk-reveal] wrappers which
    // start at opacity:0. Reveal them immediately on the catalog — there's
    // no scroll-driven animation here, the cards must be visible the
    // moment the page paints. Hydrated cards get the same treatment via
    // the path inside hydrateRemaining().
    initialCards.forEach((el) => el.classList.add('is-visible'));

    // Initial render off the 50 server-rendered cards.
    apply();
    applySort();

    // Always kick off hydration of the rest of the catalogue. Liquid caps
    // server-side iteration over collection.products at 50 outside paginate
    // (the `limit:` filter is ignored), so for any collection larger than
    // 50 we need to fetch the rest from /collections/{handle}/products.json.
    //
    // While hydration is in flight, mark the count label so the merchant
    // gets visible feedback that more cards are streaming in. Cleared the
    // moment hydration completes (inside hydrateRemaining → apply()).
    if (countLabel && cardData.length < (parseInt(grid.dataset.skCollectionTotal, 10) || 0)) {
      countLabel.textContent = 'Loading catalogue…';
    }
    hydrateRemaining().catch(() => {})
      .then(() => {
        // After hydration completes, restore any saved catalog state
        // (filters / sort / page / scroll) from a previous visit. We
        // wait for hydration so the cards we want to scroll to actually
        // exist in the DOM.
        restoreCatalogState();
      });

    /* ---------- Catalog state persistence ----------
       Save filter checkbox state, sort order, current page, and scroll
       position to sessionStorage every time the user changes something.
       When they navigate to a PDP and come back via:
         (a) the browser's Back button → bfcache restores the page
             instantly with state already in memory (no save/restore
             needed, this is the fast path).
         (b) a manual click on the catalog link, an SPA router, or a
             cold-cache reload → bfcache misses, the page re-renders,
             and we replay the saved state on top of it.
       Either way, the user lands back exactly where they left off. */
    const STATE_KEY = 'sk:catalog-state:' + (grid.dataset.skCollectionHandle || 'all');

    function snapshotState() {
      const state = {
        // Checkbox filters: serialise as { kind: [values...] }
        filters: {},
        // Sort dropdown
        sort: sortSelect ? sortSelect.value : '',
        // Current pagination page
        page: currentPage,
        // Vertical scroll position
        scroll: window.scrollY || document.documentElement.scrollTop || 0,
        // Stamp so old cached state for a different cardData layout is
        // ignored if cardData length changed dramatically (catalogue
        // grew/shrank).
        total: cardData.length,
        ts: Date.now()
      };
      form.querySelectorAll('input[data-sk-filter]:checked, input[data-sk-filter][type="number"]').forEach((inp) => {
        const kind = inp.dataset.skFilter;
        if (!kind) return;
        if (inp.type === 'number') {
          if (inp.value) {
            state.filters[kind] = inp.value;
          }
        } else {
          if (!state.filters[kind]) state.filters[kind] = [];
          state.filters[kind].push(inp.value);
        }
      });
      try { sessionStorage.setItem(STATE_KEY, JSON.stringify(state)); }
      catch (_) { /* swallow — quota or disabled */ }
    }

    function restoreCatalogState() {
      let state = null;
      try {
        const raw = sessionStorage.getItem(STATE_KEY);
        if (raw) state = JSON.parse(raw);
      } catch (_) { return; }
      if (!state) return;
      // Stale snapshots older than 30 minutes are ignored — assume the
      // user moved on to a different shopping intent.
      if (state.ts && Date.now() - state.ts > 30 * 60 * 1000) return;

      // Re-tick the saved filter checkboxes / re-fill the price inputs.
      Object.entries(state.filters || {}).forEach(([kind, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => {
            const cb = form.querySelector('input[data-sk-filter="' + kind + '"][value="' + v.replace(/"/g, '\\"') + '"]');
            if (cb) cb.checked = true;
          });
        } else {
          const inp = form.querySelector('input[data-sk-filter="' + kind + '"]');
          if (inp) inp.value = value;
        }
      });
      if (sortSelect && state.sort) sortSelect.value = state.sort;
      currentPage = parseInt(state.page, 10) || 1;
      apply({ preservePage: true });
      applySort();

      // Defer scroll until after layout settles so we land on the right pixel.
      requestAnimationFrame(() => {
        const target = parseInt(state.scroll, 10) || 0;
        if (target > 0) window.scrollTo({ top: target, behavior: 'instant' in document.documentElement.style ? 'instant' : 'auto' });
      });
    }

    // Persist state on every meaningful interaction.
    form.addEventListener('change', snapshotState);
    if (sortSelect) sortSelect.addEventListener('change', snapshotState);
    if (paginationNav) paginationNav.addEventListener('click', () => requestAnimationFrame(snapshotState));
    // Also snapshot on page hide — covers the case where the user clicks
    // a product card to navigate away. `pagehide` fires reliably on both
    // bfcache freezes and full unloads, and (unlike `unload`) does NOT
    // disable bfcache.
    window.addEventListener('pagehide', snapshotState);
  }

  /* -- Cart drawer ----------------------------------------------------------*/

  function initCartDrawer() {
    const drawer = document.querySelector('[data-sk-cart-drawer]');
    const backdrop = document.querySelector('[data-sk-drawer-backdrop]');
    if (!drawer || !backdrop) return;

    let disposeTrap = null;

    const open = () => {
      drawer.hidden = false;
      // Force a reflow so the transition picks up the base -> open state.
      drawer.getBoundingClientRect();
      drawer.classList.add('is-open');
      backdrop.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      disposeTrap = H.trapFocus ? H.trapFocus(drawer) : null;
    };

    const close = () => {
      drawer.classList.remove('is-open');
      backdrop.classList.remove('is-open');
      document.body.style.overflow = '';
      setTimeout(() => { drawer.hidden = true; }, 420);
      if (disposeTrap) { disposeTrap(); disposeTrap = null; }
    };

    /* Expose drawer controls on a global namespace so other code paths
       (e.g. the post-add-to-cart confirmation flow) can open the drawer
       without simulating a click on the cart icon. */
    window.SkCart = window.SkCart || {};
    window.SkCart.openDrawer = open;
    window.SkCart.closeDrawer = close;

    /**
     * Cart icon: leave it as a normal /cart link. The drawer is a
     * post-add-to-cart confirmation surface only — it auto-opens after
     * a successful add (handled in the ATC flow), not on icon click.
     * This way clicking the cart icon ALWAYS takes the customer to the
     * full editorial /cart page, where they can review their bag in
     * proper detail. Right-click / cmd-click / middle-click all just
     * work because it's a real anchor.
     */
    backdrop.addEventListener('click', close);
    drawer.querySelectorAll('[data-sk-drawer-close]').forEach((b) => b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    // Hydrate on cart change.
    document.addEventListener('sanki:cart:change', (e) => {
      const cart = e.detail && e.detail.cart;
      if (!cart) return;
      renderCartDrawer(cart);
      updateCartBubble(cart.item_count);
      H.toast && cart._lastAdded && H.toast('Added to cart');
    });

    // Wire existing SSR qty / remove controls once.
    wireCartControls(drawer);
    document.addEventListener('sanki:cart:rendered', () => wireCartControls(drawer));
  }

  function wireCartControls(root) {
    root.querySelectorAll('[data-sk-qty-decr]').forEach((b) => {
      if (b.__skWired) return; b.__skWired = true;
      b.addEventListener('click', () => changeQty(b, -1));
    });
    root.querySelectorAll('[data-sk-qty-incr]').forEach((b) => {
      if (b.__skWired) return; b.__skWired = true;
      b.addEventListener('click', () => changeQty(b, +1));
    });
    root.querySelectorAll('[data-sk-qty-input]').forEach((input) => {
      if (input.__skWired) return; input.__skWired = true;
      input.addEventListener('change', () => updateLine(input));
    });
    root.querySelectorAll('[data-sk-cart-remove]').forEach((b) => {
      if (b.__skWired) return; b.__skWired = true;
      b.addEventListener('click', () => {
        const row = b.closest('[data-line-key]');
        if (!row) return;
        // Optimistically collapse the row so the user sees instant
        // feedback. The server round-trip + re-render replaces this
        // with the source-of-truth markup once it lands.
        optimisticallyRemoveRow(row);
        const key = row.dataset.lineKey;
        API.cart.change({ id: key, quantity: 0 }).catch(onCartError);
      });
    });

    /* Debounced gift-note save — POSTs to /cart/update.js with the
       updated note. Live, real-time persistence as the user types. */
    root.querySelectorAll('[data-sk-cart-note]').forEach((ta) => {
      if (ta.__skWired) return; ta.__skWired = true;
      let timer = null;
      ta.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ note: ta.value })
          }).catch(() => { /* silent — note is non-critical */ });
        }, 450);
      });
    });

    /* Animate enter for any newly-rendered line (used by renderCartDrawer
       — when the cart re-renders after add-to-cart, lines fade+slide in). */
    root.querySelectorAll('[data-line-key]').forEach((row) => {
      if (row.__skEntered) return;
      row.__skEntered = true;
      // Skip on initial SSR pass (no animation if line was always there).
      // For JS-rendered passes, we briefly add then strip the class.
      if (row.dataset.skNew === '1') {
        row.classList.add('is-entering');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => row.classList.remove('is-entering'));
        });
      }
    });
  }

  /**
   * Visually remove a cart drawer line immediately, before the server
   * confirms the cart change. Two reasons:
   *   1. UX — the user gets sub-100ms feedback instead of staring at the
   *      same line for the duration of the round-trip.
   *   2. Correctness — the cart drawer's renderCartDrawer is the single
   *      source of truth for what's in the cart, but we don't want a
   *      visible flash of "still here, then gone". By collapsing the row
   *      we let the eventual re-render cleanly replace whatever's left.
   * Doesn't touch any state — purely visual.
   */
  function optimisticallyRemoveRow(row) {
    if (!row) return;
    row.style.transition = 'opacity 180ms ease-out, max-height 220ms ease-out, padding 220ms ease-out, margin 220ms ease-out';
    row.style.maxHeight = row.offsetHeight + 'px';
    // Force a reflow so the next frame sees the explicit max-height.
    void row.offsetHeight;
    row.style.opacity = '0';
    row.style.maxHeight = '0';
    row.style.paddingTop = '0';
    row.style.paddingBottom = '0';
    row.style.marginTop = '0';
    row.style.marginBottom = '0';
    row.style.overflow = 'hidden';
    row.setAttribute('aria-hidden', 'true');
    row.style.pointerEvents = 'none';
  }

  function changeQty(btn, delta) {
    const row = btn.closest('[data-line-key]');
    if (!row) return;
    const input = row.querySelector('[data-sk-qty-input]');
    if (!input) return;
    const next = Math.max(0, (parseInt(input.value, 10) || 0) + delta);
    input.value = next;
    if (next === 0) optimisticallyRemoveRow(row);
    updateLine(input);
  }

  function updateLine(input) {
    const row = input.closest('[data-line-key]');
    if (!row) return;
    const key = row.dataset.lineKey;
    const qty = Math.max(0, parseInt(input.value, 10) || 0);
    if (qty === 0) optimisticallyRemoveRow(row);
    API.cart.change({ id: key, quantity: qty }).catch((err) => {
      /* Shopify rejected the change — typically because the requested
         qty exceeds inventory ("Only N items were added to your cart
         due to availability."). Two things must happen now:
           1. Show the merchant's message in a toast so the shopper
              understands WHY their tap didn't stick.
           2. Roll the qty input back to the actual cart value. Without
              this, the input shows e.g. "3" while the cart sticks at
              "1" — looks identical to a broken counter.
      */
      onCartError(err);
      API.cart.fetch().then((cart) => {
        const item = cart.items && cart.items.find((it) => it.key === key);
        if (item) {
          input.value = item.quantity;
        } else if (qty > 0) {
          /* Line vanished but we didn't ask to delete — leave a 1 */
          input.value = 1;
        }
        /* Re-render the whole drawer so prices/subtotal/total sync */
        document.dispatchEvent(new CustomEvent('sanki:cart:change', { detail: { cart } }));
      }).catch(() => {});
    });
  }

  function updateCartBubble(count) {
    document.querySelectorAll('[data-sk-cart-count-bubble]').forEach((el) => {
      el.textContent = count;
      el.hidden = !count;
    });
    document.querySelectorAll('[data-sk-cart-count]').forEach((el) => {
      el.textContent = '(' + count + ')';
    });
  }

  function onCartError(err) {
    console.error('[Sanki] cart error:', err);
    /* Prefer Shopify's human-readable description (e.g. inventory
       limit messages) over the raw thrown Error message. */
    const msg = (err && err.data && (err.data.description || err.data.message))
      || err.message
      || 'Something went wrong.';
    H.toast && H.toast(msg);
  }

  /**
   * Render the cart drawer body + footer to match the elite hybrid
   * markup shipped in `snippets/cart-drawer.liquid`. This MUST stay in
   * sync with that snippet — the SSR markup and the JS-rendered markup
   * must be byte-identical so the user can't tell whether they're
   * looking at SSR or hydrated state.
   */
  function renderCartDrawer(cart) {
    const body = document.querySelector('[data-sk-cart-body]');
    const foot = document.querySelector('[data-sk-cart-foot]');
    const subtotal = document.querySelector('[data-sk-cart-subtotal]');
    const total = document.querySelector('[data-sk-cart-total]');
    const ctaAmt = document.querySelector('[data-sk-cart-cta-amount]');
    const shipbar = document.querySelector('[data-sk-cart-shipbar]');
    if (!body) return;

    /* Empty state ------------------------------------------------------ */
    if (cart.item_count === 0) {
      body.innerHTML =
        '<div class="sk-cart-empty" data-sk-cart-empty>' +
          '<svg viewBox="0 0 64 64" width="44" height="44" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true">' +
            '<path d="M14 22h36l-3 28a4 4 0 0 1-4 4H21a4 4 0 0 1-4-4l-3-28z" stroke-linejoin="round"/>' +
            '<path d="M22 22V14a10 10 0 0 1 20 0v8" stroke-linecap="round"/>' +
          '</svg>' +
          '<p class="sk-cart-empty__title">Your bag is empty</p>' +
          '<p class="sk-cart-empty__sub">Find your next favourite below.</p>' +
          '<a href="/collections/all" class="sk-btn sk-btn--primary sk-mt-4">Continue shopping</a>' +
        '</div>';
      if (foot) foot.hidden = true;
      if (shipbar) shipbar.hidden = true;
      document.dispatchEvent(new CustomEvent('sanki:cart:rendered', { detail: { cart } }));
      return;
    }
    if (foot) foot.hidden = false;
    if (shipbar) shipbar.hidden = false;

    /* Line items ------------------------------------------------------- */
    const lines = cart.items.map((line) => {
      const img = line.image
        ? `<img src="${line.image}&width=160" alt="${H.escape(line.product_title)}" width="60" height="72" loading="lazy">`
        : '';
      // Strip 'Universal ...' / 'Free Size' / 'One Size' segments so the
      // cart line shows only meaningful variant info (e.g. color), and
      // surface the chosen Size from properties on a separate line.
      const _stripUniversal = (vt) => {
        if (!vt) return '';
        const parts = String(vt).split(' / ');
        const kept = parts.filter((p) => {
          const lc = p.toLowerCase();
          return !(lc.includes('universal') || lc.includes('free size') || lc.includes('one size') || lc.includes('fits all'));
        });
        return kept.join(' / ');
      };
      const cleanVariant = _stripUniversal(line.variant_title);
      const sizeProp = line.properties && (line.properties.Size || line.properties.size);
      const variantHtml =
        (cleanVariant
          ? `<div class="sk-cart-line__variant">${H.escape(cleanVariant)}</div>`
          : '') +
        (sizeProp
          ? `<div class="sk-cart-line__variant">Size: <strong>${H.escape(sizeProp)}</strong></div>`
          : '');
      return (
        '<li class="sk-cart-line" data-line-key="' + H.escape(line.key) + '">' +
          '<a href="' + H.escape(line.url) + '" class="sk-cart-line__media" tabindex="-1">' + img + '</a>' +
          '<div class="sk-cart-line__info">' +
            '<div class="sk-cart-line__row">' +
              '<a href="' + H.escape(line.url) + '" class="sk-cart-line__title">' + H.escape(line.product_title) + '</a>' +
              '<button type="button" class="sk-cart-line__remove" data-sk-cart-remove aria-label="Remove">' +
                '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">' +
                  '<path d="M6 6l12 12M6 18L18 6" stroke-linecap="round"/>' +
                '</svg>' +
              '</button>' +
            '</div>' +
            variantHtml +
            '<div class="sk-cart-line__bottom">' +
              '<div class="sk-qty" role="group" aria-label="Quantity">' +
                '<button type="button" data-sk-qty-decr aria-label="Decrease quantity">&minus;</button>' +
                '<input type="number" inputmode="numeric" min="0" value="' + line.quantity + '" aria-label="Quantity" data-sk-qty-input>' +
                '<button type="button" data-sk-qty-incr aria-label="Increase quantity">+</button>' +
              '</div>' +
              '<strong class="sk-cart-line__price">' + H.money(line.final_line_price) + '</strong>' +
            '</div>' +
          '</div>' +
        '</li>'
      );
    }).join('');

    /* Gift-note collapsible (preserves whatever the user typed) ------- */
    const noteVal = (cart.note || '').replace(/"/g, '&quot;');
    const extras =
      '<details class="sk-cart-drawer__extras" data-sk-cart-extras' +
        (cart.note ? ' open' : '') +
      '>' +
        '<summary>' +
          '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">' +
            '<path d="M3 8h18v13H3z M3 8l3-4h12l3 4 M12 4v17 M3 14h18" stroke-linejoin="round"/>' +
          '</svg>' +
          '<span>Add a gift note</span>' +
          '<svg class="sk-cart-drawer__extras-chev" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">' +
            '<path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round"/>' +
          '</svg>' +
        '</summary>' +
        '<div class="sk-cart-drawer__extras-body">' +
          '<textarea class="sk-textarea" rows="2" placeholder="A short note for your gift recipient (optional)" data-sk-cart-note maxlength="240">' +
            H.escape(cart.note || '') +
          '</textarea>' +
        '</div>' +
      '</details>';

    body.innerHTML = '<ul class="sk-cart-lines" data-sk-cart-lines>' + lines + '</ul>' + extras;

    /* Money mirrors (subtotal / total / CTA) -------------------------- */
    const formatted = H.money(cart.total_price);
    if (subtotal) subtotal.textContent = formatted;
    if (total) total.textContent = formatted;
    if (ctaAmt) ctaAmt.textContent = formatted;

    /* Header item count ----------------------------------------------- */
    document.querySelectorAll('[data-sk-cart-count]').forEach((el) => {
      el.textContent = '(' + cart.item_count + ')';
    });

    document.dispatchEvent(new CustomEvent('sanki:cart:rendered', { detail: { cart } }));
  }

  /* -- Quick add (product card) --------------------------------------------*/

  function initQuickAdd() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-sk-quick-add]');
      if (!btn) return;
      e.preventDefault();
      const id = parseInt(btn.dataset.variantId, 10);
      if (!id) return;
      btn.disabled = true;
      const original = btn.textContent;
      btn.textContent = 'Adding…';
      API.cart.add({ id, quantity: 1 })
        .then(({ cart }) => {
          cart._lastAdded = true;
          document.dispatchEvent(new CustomEvent('sanki:cart:change', { detail: { cart } }));
          btn.textContent = 'Added';
          /* Open drawer for the quick-confirm view. */
          document.addEventListener("sanki:cart:change", () => {
  if (window.SkCart && window.SkCart.openDrawer) window.SkCart.openDrawer();
}, { once: true });
          setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1400);
        })
        .catch((err) => {
          btn.textContent = original;
          btn.disabled = false;
          onCartError(err);
        });
    });
  }

  /* -- Wishlist -------------------------------------------------------------*/

  function initWishlist() {
    if (!Sanki.settings || !Sanki.settings.wishlistEnabled) return;

    function reflect() {
      const ids = API.wishlist.list().map((x) => String(x.id));
      document.querySelectorAll('[data-sk-wishlist]').forEach((btn) => {
        const on = ids.includes(String(btn.dataset.productId));
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        const label = btn.querySelector('[data-sk-wishlist-label]');
        if (label) label.textContent = on ? 'Remove from wishlist' : 'Add to wishlist';
      });
      const count = document.querySelector('[data-sk-wishlist-count]');
      if (count) { count.textContent = ids.length; count.hidden = ids.length === 0; }
    }

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-sk-wishlist]');
      if (!btn) return;
      e.preventDefault();
      API.wishlist.toggle({
        id: btn.dataset.productId,
        handle: btn.dataset.productHandle,
        variantId: btn.dataset.variantId
      });
      reflect();
      H.toast && H.toast(btn.getAttribute('aria-pressed') === 'true' ? 'Added to wishlist' : 'Removed');
    });

    document.addEventListener('sanki:wishlist:change', reflect);
    reflect();
  }

  /* -- Wishlist page --------------------------------------------------------
   *
   * Production-grade rebuild. Architecture:
   *
   *   STATE
   *     • Single source of truth: Sanki.api.wishlist (localStorage-backed).
   *     • In-memory cache of fetched product details, keyed by handle, so
   *       re-renders (sort changes, item removals) don't refetch.
   *
   *   RENDERING
   *     • One render() function. Reads list → maps to cached products →
   *       sorts → outputs HTML in a single fragment → grid.replaceChildren.
   *     • Cards reuse renderTypeRowCard for visual parity with the catalog.
   *     • Each card is augmented with three inline controls:
   *         - × Remove                  (top-right corner)
   *         - Size selector (if multi)  (above CTA)
   *         - Add to bag CTA            (full-width below the card body)
   *
   *   EVENT HANDLING
   *     • ALL clicks inside the toolbar + grid are event-delegated.
   *       Per-card listeners (which used to leak across re-renders and lose
   *       their captured `idx`) are gone. The handlers read the wishlist
   *       item id from `data-sk-wishlist-id` on the wrap, never from a
   *       closure.
   *
   *   ACTIONS
   *     • Remove    → API.wishlist.remove(id) → change event → re-render.
   *     • Clear all → API.wishlist.clear() (one atomic write, no per-item loop).
   *     • Add all   → /cart/add.js with { items: [...] } batched.
   *     • Add one   → /cart/add.js with the chosen variant id.
   *     • Share     → encodes wishlist as ?w={ids,joined} URL → clipboard.
   *     • Sort      → re-renders from the in-memory cache (no fetches).
   */
  function initWishlistPage() {
    const grid     = document.querySelector('[data-sk-wishlist-grid]');
    if (!grid) return;
    const empty    = document.querySelector('[data-sk-wishlist-empty]');
    const loading  = document.querySelector('[data-sk-wishlist-loading]');
    const toolbar  = document.querySelector('[data-sk-wishlist-toolbar]');
    const footer   = document.querySelector('[data-sk-wishlist-footer]');
    // IMPORTANT: scope the toolbar count to the toolbar, NOT a global
    // querySelector. The header's wishlist badge also uses
    // [data-sk-wishlist-count], so a global selector picks the header
    // badge first and overwrites it with our long "X items saved" text
    // (visible bug: header showed "items" instead of a number). We use a
    // separate attribute on the page to avoid every collision.
    const countEl  = toolbar ? toolbar.querySelector('[data-sk-wishlist-page-count]') : null;
    const sortSel  = document.querySelector('[data-sk-wishlist-sort]');

    const setVisible = (el, on) => { if (el) el.hidden = !on; };
    const productCache = new Map(); // handle → /products/{handle}.js result

    async function ensureProducts(list) {
      const missing = list.filter((it) => it.handle && !productCache.has(it.handle));
      if (!missing.length) return;
      const fetched = await Promise.all(
        missing.map((it) => fetchProductByHandle(it.handle).catch(() => null))
      );
      missing.forEach((it, i) => {
        if (fetched[i]) productCache.set(it.handle, fetched[i]);
      });
    }

    function sortList(list, mode) {
      const arr = list.slice();
      const priceOf = (it) => {
        const p = productCache.get(it.handle);
        if (!p) return 0;
        const fv = (p.variants && p.variants[0]) || {};
        return parseCents(fv.price) || parseCents(p.price) || 0;
      };
      const titleOf = (it) => {
        const p = productCache.get(it.handle);
        return (p && p.title) ? p.title.toLowerCase() : '';
      };
      switch (mode) {
        case 'oldest':     arr.sort((a, b) => (a.addedAt || 0) - (b.addedAt || 0)); break;
        case 'price-asc':  arr.sort((a, b) => priceOf(a) - priceOf(b)); break;
        case 'price-desc': arr.sort((a, b) => priceOf(b) - priceOf(a)); break;
        case 'title':      arr.sort((a, b) => titleOf(a).localeCompare(titleOf(b))); break;
        case 'recent':     /* falls through */
        default:           arr.sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));
      }
      return arr;
    }

    function buildCardHtml(item) {
      const product = productCache.get(item.handle);
      if (!product) return '';
      const np = normalizeProduct(product);
      if (!np) return '';

      // Add-to-bag strategy (matches Zara / Aritzia / Bluorng):
      //   • If there's only a single available variant — quick-add it
      //     directly. Button reads "Add to bag".
      //   • If the product needs a size choice (multiple available
      //     variants on the Size option) — DON'T show a dropdown on the
      //     wishlist. Instead the button reads "Select size" and links
      //     to the PDP where the user can pick. Keeps every card the
      //     same height, no dropdown noise on the page.
      //   • Sold out — disabled "Sold out" button.
      const variants = (product.variants || []).filter((v) => v && v.available);
      const sizeOptionIdx = (product.options || []).findIndex(
        (o) => /size/i.test(typeof o === 'string' ? o : (o && o.name) || '')
      );
      const needsSizeChoice = variants.length > 1 && sizeOptionIdx >= 0;

      let actionBtn;
      if (!variants.length) {
        actionBtn =
          '<button type="button" class="sk-btn sk-btn--primary sk-btn--block sk-wishlist__add" disabled>Sold out</button>';
      } else if (needsSizeChoice) {
        actionBtn =
          '<a href="' + np.url + '" class="sk-btn sk-btn--outline sk-btn--block sk-wishlist__add">' +
            'Select size →' +
          '</a>';
      } else {
        // Direct add — pre-pick the variant the user hearted, falling
        // back to the first available.
        const variantId = item.variantId
          ? String(item.variantId)
          : String(variants[0].id);
        actionBtn =
          '<button type="button" class="sk-btn sk-btn--primary sk-btn--block sk-wishlist__add"' +
            ' data-sk-wishlist-add data-variant-id="' + variantId + '">' +
            'Add to bag' +
          '</button>';
      }

      // Build the underlying card WITHOUT the heart toggle. On the wishlist
      // page the heart is meaningless (everything here is already saved) and
      // it visually clashes with the × remove button.
      const baseCard = renderTypeRowCard(np, { hideWishlist: true });
      const removeBtn =
        '<button type="button" class="sk-wishlist__remove" data-sk-wishlist-remove' +
          ' aria-label="Remove from wishlist" title="Remove from wishlist">' +
          '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden>' +
            '<path d="M6 6l12 12M18 6L6 18" stroke-linecap="round"/>' +
          '</svg>' +
        '</button>';

      return (
        '<div class="sk-wishlist__card" data-sk-card-wrap data-sk-wishlist-id="' +
          escapeHtml(String(item.id)) + '">' +
          baseCard +
          removeBtn +
          '<div class="sk-wishlist__card-actions">' +
            actionBtn +
          '</div>' +
        '</div>'
      );
    }

    let renderInflight = false;
    async function render() {
      if (renderInflight) return;
      renderInflight = true;
      try {
        const list = (Sanki.api && Sanki.api.wishlist) ? Sanki.api.wishlist.list() : [];
        if (!list.length) {
          setVisible(empty, true);
          setVisible(loading, false);
          setVisible(grid, false);
          setVisible(toolbar, false);
          setVisible(footer, false);
          if (countEl) countEl.textContent = '0 saved';
          return;
        }

        setVisible(empty, false);
        setVisible(toolbar, true);
        setVisible(footer, true);
        if (!grid.children.length) setVisible(loading, true);
        if (countEl) countEl.textContent = list.length + (list.length === 1 ? ' item saved' : ' items saved');

        await ensureProducts(list);
        const sorted = sortList(list, sortSel ? sortSel.value : 'recent');
        grid.innerHTML = sorted.map(buildCardHtml).join('');
        setVisible(loading, false);
        setVisible(grid, true);
      } finally {
        renderInflight = false;
      }
    }

    /* ---------- Event delegation on the toolbar + grid ---------- */

    function findItemId(target) {
      const wrap = target.closest('[data-sk-wishlist-id]');
      return wrap ? wrap.dataset.skWishlistId : '';
    }

    // Grid clicks: × remove, "Add to bag", inline size change.
    grid.addEventListener('click', (e) => {
      const removeEl = e.target.closest('[data-sk-wishlist-remove]');
      if (removeEl) {
        e.preventDefault();
        e.stopPropagation();
        const id = findItemId(removeEl);
        if (id) Sanki.api.wishlist.remove(id);
        return;
      }
      const addEl = e.target.closest('[data-sk-wishlist-add]');
      if (addEl) {
        // Anchor → let the browser navigate to the PDP for size selection.
        if (addEl.tagName === 'A') return;
        e.preventDefault();
        e.stopPropagation();
        const variantId = addEl.dataset.variantId;
        if (!variantId) return;
        addOneToCart(addEl, parseInt(variantId, 10));
      }
    });

    function addOneToCart(btn, variantId) {
      if (!variantId) return;
      btn.disabled = true;
      const original = btn.textContent;
      btn.textContent = 'Adding…';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      })
        .then((r) => r.ok ? fetch('/cart.js', { credentials: 'same-origin' }) : Promise.reject())
        .then((r) => r.json())
        .then((cart) => {
          cart._lastAdded = true;
          document.dispatchEvent(new CustomEvent('sanki:cart:change', { detail: { cart } }));
          btn.textContent = 'Added ✓';
          setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1400);
        })
        .catch(() => {
          btn.textContent = 'Try again';
          setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1400);
        });
    }

    // Toolbar clicks: clear / add-all / share.
    if (toolbar) {
      toolbar.addEventListener('click', async (e) => {
        if (e.target.closest('[data-sk-wishlist-clear]')) {
          if (!Sanki.api || !Sanki.api.wishlist) return;
          if (!confirm('Clear your entire wishlist? This cannot be undone.')) return;
          Sanki.api.wishlist.clear();
          return;
        }
        if (e.target.closest('[data-sk-wishlist-add-all]')) {
          await addAllToCart(e.target.closest('[data-sk-wishlist-add-all]'));
          return;
        }
        const shareBtn = e.target.closest('[data-sk-wishlist-share]');
        if (shareBtn) shareWishlist(shareBtn);
      });
    }

    async function addAllToCart(btn) {
      if (!Sanki.api || !Sanki.api.wishlist) return;
      const list = Sanki.api.wishlist.list();
      if (!list.length) return;
      btn.disabled = true;
      const original = btn.textContent;
      btn.textContent = 'Adding…';
      try {
        await ensureProducts(list);
        const items = [];
        list.forEach((it) => {
          // Prefer the variant that was hearted. If unavailable, fall back
          // to the product's first available variant.
          const product = productCache.get(it.handle);
          if (!product) return;
          const variants = (product.variants || []).filter((v) => v && v.available);
          if (!variants.length) return;
          let vid = parseInt(it.variantId, 10);
          if (!variants.some((v) => v.id === vid)) vid = variants[0].id;
          items.push({ id: vid, quantity: 1 });
        });
        if (!items.length) throw new Error('All wishlist items are sold out.');
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!res.ok) throw new Error('Cart add failed');
        const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
        cart._lastAdded = true;
        document.dispatchEvent(new CustomEvent('sanki:cart:change', { detail: { cart } }));
        btn.textContent = items.length === list.length
          ? 'Added all ✓'
          : 'Added ' + items.length + ' / ' + list.length + ' ✓';
        setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1800);
      } catch (err) {
        btn.textContent = 'Try again';
        setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1600);
        if (H.toast && err && err.message) H.toast(err.message);
      }
    }

    function shareWishlist(btn) {
      if (!Sanki.api || !Sanki.api.wishlist) return;
      const list = Sanki.api.wishlist.list();
      const handles = list.map((it) => it.handle).filter(Boolean);
      const url = window.location.origin + window.location.pathname + '?w=' + encodeURIComponent(handles.join(','));
      if (navigator.share) {
        navigator.share({ title: 'My Sanki wishlist', url }).catch(() => {});
        return;
      }
      navigator.clipboard.writeText(url).then(
        () => {
          const original = btn.querySelector('span') ? btn.querySelector('span').textContent : btn.textContent;
          // Replace just the visible text node, keep the icon.
          const textNode = Array.from(btn.childNodes).find((n) => n.nodeType === 3 && n.textContent.trim());
          if (textNode) textNode.textContent = ' Copied ✓';
          else btn.textContent = 'Copied ✓';
          setTimeout(() => {
            if (textNode) textNode.textContent = ' Share';
            else btn.textContent = original;
          }, 1600);
        },
        () => { if (H.toast) H.toast('Could not copy link'); }
      );
    }

    /* ---------- Shared-link import ---------- */

    // If the URL has `?w=handle1,handle2`, merge those into the visitor's
    // wishlist so a shared link "just works" — no signup needed.
    (function importSharedList() {
      const params = new URLSearchParams(window.location.search);
      const w = params.get('w');
      if (!w) return;
      const handles = w.split(',').map((s) => s.trim()).filter(Boolean);
      if (!handles.length) return;
      Promise.all(handles.map((h) => fetchProductByHandle(h).catch(() => null))).then((products) => {
        products.forEach((p) => {
          if (!p || !Sanki.api || !Sanki.api.wishlist) return;
          if (!Sanki.api.wishlist.has(p.id)) {
            Sanki.api.wishlist.add({
              id: p.id,
              handle: p.handle,
              variantId: p.variants && p.variants[0] && p.variants[0].id
            });
          }
        });
      });
    })();

    if (sortSel) sortSel.addEventListener('change', render);
    document.addEventListener('sanki:wishlist:change', render);
    render();
  }

  /* -- Bestsellers type rows (production-grade) ----------------------------
   * Each row can operate in two modes:
   *   CURATED  — data-sk-handles lists specific product handles; JS fetches
   *              each via /products/<handle>.js (fast, single-product call).
   *   AUTO     — empty handles list: JS fetches the catalogue sorted by
   *              best-selling, filters by product_type, picks top N.
   *
   * Schemas differ between endpoints:
   *   /products/<handle>.js   returns .type (not .product_type), prices are
   *                           integer CENTS, images are string URLs.
   *   /products.json          returns .product_type, prices are DECIMAL
   *                           strings ("2499.00"), images are {src} objects.
   * The `normalizeProduct()` helper unifies these into a common shape.
   */
  const PRODUCTS_CACHE = { bestselling: null, byType: {} };

  async function initTypeRows() {
    const rows = document.querySelectorAll('[data-sk-type-row]');
    if (!rows.length) return;

    // Fire off all rows in parallel for speed.
    await Promise.all(Array.from(rows).map((row) => hydrateRow(row).catch(() => {})));
  }

  async function hydrateRow(row) {
    const grid = row.querySelector('[data-sk-type-grid]');
    if (!grid) return;

    // If Liquid already server-rendered enough cards, leave the row alone —
    // server prices come from Shopify's native money filter, which is the
    // only path on this store that has rendered prices correctly. JS only
    // runs to fill missing slots.
    const limit = parseInt(row.dataset.skTypeLimit, 10) || 4;
    const serverCards = Array.from(grid.querySelectorAll('[data-sk-server-rendered]'));
    if (serverCards.length >= limit) {
      // Drop any leftover skeletons that snuck in beside server-rendered cards.
      grid.querySelectorAll('.sk-card--skeleton').forEach((el) => el.remove());
      requestAnimationFrame(() => {
        grid.querySelectorAll('[data-sk-reveal]').forEach((el) => el.classList.add('is-visible'));
      });
      return;
    }

    const rawHandles = (row.dataset.skHandles || '').trim();
    const handles    = rawHandles
      ? rawHandles.split(/[,\n]+/).map((s) => s.trim()).filter(Boolean)
      : [];
    const type  = (row.dataset.skTypeRow || '').toUpperCase();
    const mode  = (row.dataset.skRankingMode || 'bestselling').toLowerCase();

    let products = [];

    // Mode 1 — curated handles win.
    if (handles.length) {
      const results = await Promise.all(handles.slice(0, limit).map(fetchProductByHandle));
      products = results.filter(Boolean);
    }

    // Mode 2 — automatic: best-sellers sort + product_type filter.
    // For the auto path we skip sold-out items so a bestsellers row doesn't
    // surface products people can't actually buy. Curated handles stay
    // intact (if a merchant pins a sold-out item, that's their choice).
    if (products.length < limit) {
      const ranked = await getAutoRanked(type, mode);
      const existingIds = new Set(products.map((p) => String(p.id)));
      for (const p of ranked) {
        if (products.length >= limit) break;
        if (existingIds.has(String(p.id))) continue;
        const n = normalizeProduct(p);
        if (!n || !n.available) continue; // skip sold-out in auto mode
        products.push(p);
        existingIds.add(String(p.id));
      }
    }

    if (!products.length) {
      // Give up: remove skeletons so the row doesn't hang on shimmer.
      grid.querySelectorAll('.sk-card--skeleton').forEach((el) => el.remove());
      return;
    }

    // Re-fetch every visible product via /products/{handle}.js. That endpoint
    // is the only one Shopify guarantees returns integer cents (e.g. 249900),
    // so we get past the schema ambiguity that has been causing the home row
    // to render ₹2,499 products as ₹249,900 when /collections/all/products.json
    // returned a decimal-string the heuristic guessed wrong on. Falls back to
    // the auto-ranked record if the .js fetch fails.
    const slice = products.slice(0, limit);
    const handlesForFetch = slice.map((p) => p.handle).filter(Boolean);
    const refetched = await Promise.all(handlesForFetch.map(fetchProductByHandle));
    const handleMap = new Map();
    refetched.forEach((rp) => { if (rp && rp.handle) handleMap.set(rp.handle, rp); });
    const finalProducts = slice.map((p) => handleMap.get(p.handle) || p);

    // Replace grid content with rendered cards. Prices are intentionally
    // suppressed on the home page — Liquid is the only price source we
    // trust here, so JS-rendered fallback cards stay priceless rather than
    // risk showing a number that's 100x off.
    const fragments = finalProducts.map((p) => {
      const outer = document.createElement('div');
      outer.setAttribute('data-sk-reveal', '');
      outer.setAttribute('data-sk-type-slot', '');
      outer.innerHTML = renderTypeRowCard(normalizeProduct(p), { showPrice: false });
      return outer;
    });
    grid.replaceChildren(...fragments);
    requestAnimationFrame(() => {
      grid.querySelectorAll('[data-sk-reveal]').forEach((el) => el.classList.add('is-visible'));
    });
  }

  /** Best-sellers ranked list for a given product_type, with in-page cache. */
  async function getAutoRanked(type, mode) {
    // AI mode first tries our backend; falls back to best-selling.
    if (mode === 'ai') {
      try {
        const res = await fetch('/apps/sanki/rankings?type=' + encodeURIComponent(type) + '&limit=12', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (res.ok) {
          const j = await res.json();
          const items = (j && j.products) || [];
          if (items.length) return items;
        }
      } catch (_) { /* fall through to best-selling */ }
    }

    // Best-selling mode — cache the whole sorted catalogue and filter per row.
    if (!PRODUCTS_CACHE.bestselling) {
      PRODUCTS_CACHE.bestselling = fetchBestSellingProducts(250)
        .catch(() => []);
    }
    const all = await PRODUCTS_CACHE.bestselling;
    if (!type) return all;
    if (!PRODUCTS_CACHE.byType[type]) {
      PRODUCTS_CACHE.byType[type] = all.filter((p) =>
        (p.product_type || p.type || '').toUpperCase() === type
      );
    }
    return PRODUCTS_CACHE.byType[type];
  }

  /** Fetch products sorted by best-selling. /collections/all/products.json
   *  returns prices as DECIMAL STRINGS in major units ("2499.00"). We tag
   *  each product with _priceUnit='major' so normalizeProduct doesn't double-
   *  multiply when converting to cents. */
  async function fetchBestSellingProducts(limit) {
    const url = '/collections/all/products.json?sort_by=best-selling&limit=' + encodeURIComponent(limit);
    const res = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!res.ok) return [];
    const j = await res.json();
    const products = (j && j.products) || [];
    return products.map((p) => ({ ...p, _priceUnit: 'major' }));
  }

  /** Fetch a single product by handle. /products/{handle}.js returns prices
   *  as INTEGER CENTS (249900 = ₹2,499). Tag with _priceUnit='cents'. */
  async function fetchProductByHandle(handle) {
    const h = String(handle || '').trim();
    if (!h) return null;
    try {
      const res = await fetch('/products/' + encodeURIComponent(h) + '.js', {
        credentials: 'same-origin', headers: { Accept: 'application/json' }
      });
      if (!res.ok) return null;
      const p = await res.json();
      p._priceUnit = 'cents';
      return p;
    } catch (_) { return null; }
  }

  /**
   * Parse a Shopify price value that may be expressed in any of:
   *   • an integer number of cents (249900)
   *   • a string integer cents ("249900")
   *   • a decimal-string in major units ("2499.00")
   *   • a decimal number in major units (2499.5)
   * Returns integer cents.
   *
   * Detection rule: if the string representation contains a decimal point,
   * treat it as major units and multiply by 100. Otherwise it's already
   * cents — do NOT re-multiply (that was the "₹249,900" bug).
   */
  function parseCents(value) {
    if (value == null || value === '') return 0;
    if (typeof value === 'number') return Math.round(value) || 0;
    const s = String(value).trim();
    if (!s) return 0;
    const f = parseFloat(s);
    if (isNaN(f)) return 0;
    const hasDecimal = s.indexOf('.') !== -1;
    if (!hasDecimal) return Math.round(f) || 0;
    // Heuristic: a decimal value whose integer part is already large enough
    // to plausibly be cents (>= 50000 = ₹500) is treated as cents-as-decimal-
    // string ("249900.00"). Otherwise it's major units that need the ×100
    // shift ("2499.00" → 249900). Threshold of 50000 keeps every realistic
    // streetwear price (₹500–₹50,000 major units) on the correct side.
    const intPart = Math.floor(f);
    if (intPart >= 50000) return Math.round(f) || 0;
    return Math.round(f * 100) || 0;
  }

  /**
   * Convert a Shopify price to integer cents using an explicit unit hint.
   *   unit='cents'  →  /products/{handle}.js  (integer cents like 249900)
   *   unit='major'  →  /products.json (REST)   (decimal strings like "2499.00")
   * Falls back to the heuristic above when unit is undefined.
   */
  function toCents(value, unit) {
    if (value == null || value === '') return 0;
    const s = String(value).trim();
    if (!s) return 0;
    if (unit === 'cents') return Math.round(parseFloat(s)) || 0;
    if (unit === 'major') return Math.round(parseFloat(s) * 100) || 0;
    return parseCents(value);
  }

  /** Unify the two Shopify JSON schemas into one shape. */
  function normalizeProduct(p) {
    if (!p) return null;
    const unit = p._priceUnit; // 'cents' | 'major' | undefined
    const firstImage = (() => {
      if (p.featured_image) return typeof p.featured_image === 'string' ? p.featured_image : p.featured_image.src;
      const i = p.images && p.images[0];
      if (!i) return '';
      return typeof i === 'string' ? i : (i.src || i.url || '');
    })();
    const secondImage = (() => {
      const i = p.images && p.images[1];
      if (!i) return '';
      return typeof i === 'string' ? i : (i.src || i.url || '');
    })();
    const variants = p.variants || [];
    const firstVariant = variants[0] || {};

    // Prices — always normalised to integer cents. We ignore the per-fetch
    // unit hint here because in practice Sannki's storefront returns mixed
    // schemas across endpoints (cents-as-decimal-string from one path,
    // major-units-as-decimal-string from another, integer cents from a
    // third). parseCents below is magnitude-aware and handles all three
    // without the caller having to know which API it came from.
    const priceCents   = parseCents(firstVariant.price) || parseCents(p.price);
    const compareCents = parseCents(firstVariant.compare_at_price) || parseCents(p.compare_at_price);

    const available = !!(p.available !== undefined ? p.available : variants.some((v) => v && v.available));
    const tags = Array.isArray(p.tags) ? p.tags : (typeof p.tags === 'string' ? p.tags.split(',').map((s) => s.trim()) : []);

    return {
      id: p.id,
      handle: p.handle,
      title: p.title || '',
      type:  p.product_type || p.type || '',
      tags:  tags,
      url:   '/products/' + (p.handle || ''),
      firstImage: firstImage,
      secondImage: secondImage,
      priceCents: priceCents,
      compareCents: compareCents,
      available: available,
      // Used by the wishlist heart to know which variant to add when the
      // user heart-clicks straight from a card without picking a variant.
      firstVariantId: firstVariant.id || ''
    };
  }

  function renderTypeRowCard(p, opts) {
    if (!p) return '';
    const onSale = p.compareCents > 0 && p.compareCents > p.priceCents;
    const isNew  = p.tags.indexOf('new') !== -1 || p.tags.indexOf('New') !== -1;
    const hasSecondary = !!p.secondImage;
    const title = escapeHtml(p.title);
    // Price rendering is opt-in so the home bestsellers fallback can stay
    // priceless when Liquid couldn't render the card (which is the only
    // surface that has had wrong-price reports). The catalog hydration
    // explicitly opts in.
    const showPrice = !opts || opts.showPrice !== false;

    const priceHtml = !showPrice ? '' : (onSale
      ? '<span class="sk-price">'
        + '<span class="sk-price__compare">' + money(p.compareCents) + '</span>'
        + '<span class="sk-price__sale">'    + money(p.priceCents)   + '</span>'
        + '</span>'
      : '<span class="sk-price">' + money(p.priceCents) + '</span>');

    const badges = []
      .concat(!p.available ? ['<span class="sk-badge sk-badge--soldout">Sold out</span>'] : [])
      .concat(onSale && p.available ? ['<span class="sk-badge sk-badge--sale">Sale</span>'] : [])
      .concat(isNew ? ['<span class="sk-badge sk-badge--new">New</span>'] : [])
      .join('');

    // Wishlist heart — only render when the global setting is on (mirrors
    // the Liquid product-card.liquid behaviour). Renders the same
    // [data-sk-wishlist] markup the Liquid version does so the existing
    // initWishlist click handler picks it up automatically.
    //
    // The wishlist *page* explicitly opts out via `opts.hideWishlist: true`
    // because every card on that surface is by definition already in the
    // wishlist — the toggle would be redundant AND visually competes with
    // the × remove button. We strip it at the markup level (in addition to
    // the CSS hide rule) so it can never leak through a stale cache.
    const hideWishlist = !!(opts && opts.hideWishlist);
    const variantId = p.variantId || (p.firstVariantId) || '';
    const wishlistHtml = (!hideWishlist && window.Sanki && Sanki.settings && Sanki.settings.wishlistEnabled)
      ? '<button type="button" class="sk-card__wish" data-sk-wishlist'
        + ' data-product-id="' + p.id + '"'
        + ' data-product-handle="' + escapeHtml(p.handle || '') + '"'
        + ' data-variant-id="' + variantId + '"'
        + ' aria-pressed="false" aria-label="Add to wishlist" title="Add to wishlist">'
        +   '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">'
        +     '<path d="M12 20s-7-4.35-7-10a4 4 0 0 1 7-2.65A4 4 0 0 1 19 10c0 5.65-7 10-7 10z" stroke-linecap="round" stroke-linejoin="round"/>'
        +   '</svg>'
        +   '<span class="sk-sr-only" data-sk-wishlist-label>Add to wishlist</span>'
        + '</button>'
      : '';

    const imgHtml = p.firstImage
      ? '<img class="sk-card__img" data-sk-card-img'
        + ' src="'    + sizedSrc(p.firstImage, 720) + '"'
        + ' srcset="' + sizedSrc(p.firstImage, 360) + ' 360w, '
                      + sizedSrc(p.firstImage, 540) + ' 540w, '
                      + sizedSrc(p.firstImage, 720) + ' 720w, '
                      + sizedSrc(p.firstImage, 1080) + ' 1080w"'
        + ' sizes="(min-width: 1200px) 25vw, (min-width: 768px) 33vw, 50vw"'
        + ' alt="' + title + '" loading="lazy" decoding="async">'
      : renderPlaceholderSvg(p.id, p.title);
    const secImgHtml = p.secondImage
      ? '<img class="sk-card__img-secondary" src="' + sizedSrc(p.secondImage, 720) + '" alt="" aria-hidden="true" loading="lazy" decoding="async">'
      : '';

    return ''
      + '<article class="sk-card" data-sk-card data-product-id="' + p.id + '"'
      +   ' data-product-url="' + p.url + '" data-has-secondary="' + (hasSecondary ? 'true' : 'false') + '">'
      +   '<a class="sk-card__media-link" href="' + p.url + '" aria-label="' + title + '">'
      +     '<div class="sk-card__media">'
      +       imgHtml + secImgHtml
      +       (badges ? '<div class="sk-card__badges">' + badges + '</div>' : '')
      +       wishlistHtml
      +     '</div>'
      +   '</a>'
      +   '<div class="sk-card__body">'
      +     '<h3 class="sk-card__title"><a href="' + p.url + '">' + title + '</a></h3>'
      +     (priceHtml ? '<div class="sk-card__meta">' + priceHtml + '</div>' : '')
      +   '</div>'
      + '</article>';
  }

  function escapeHtml(s) {
    return String(s || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function sizedSrc(src, width) {
    if (!src) return '';
    try {
      // Shopify CDN URLs accept a width param.
      const u = new URL(src, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (e) { return src; }
  }

  /** Inline SVG placeholder used when a product image fails to load. */
  function renderPlaceholderSvg(id, title) {
    const safe = escapeHtml(String(title || ''));
    return ''
      + '<div class="sk-card__img sk-card__img--placeholder" aria-hidden="true" aria-label="' + safe + '">'
      +   '<svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">'
      +     '<rect width="120" height="120" fill="#FAF8EE"/>'
      +     '<g font-family="Playfair Display, Georgia, serif" text-anchor="middle">'
      +       '<text x="60" y="58" font-size="22" font-weight="700" fill="#2E7D5B" letter-spacing="4">SANKI</text>'
      +       '<text x="60" y="76" font-size="6.2" fill="#6A6E76" letter-spacing="3">THE CRAZY ATTIRE</text>'
      +     '</g>'
      +     '<line x1="30" y1="86" x2="90" y2="86" stroke="#2A3856" stroke-width="0.8" opacity="0.7"/>'
      +   '</svg>'
      + '</div>';
  }

  // Expose a DOM-node factory for the error handler below.
  Sanki.makePlaceholder = function (id, title) {
    const wrap = document.createElement('div');
    wrap.innerHTML = renderPlaceholderSvg(id, title);
    return wrap.firstElementChild;
  };

  // Global delegated error listener: when any product-card image fails to
  // load, swap it for the branded placeholder. Uses capture phase because
  // `error` events don't bubble.
  document.addEventListener('error', (e) => {
    const target = e.target;
    if (!target || target.tagName !== 'IMG') return;
    if (!target.hasAttribute('data-sk-card-img') && !target.classList.contains('sk-card__img')) return;
    if (target.dataset.skImgHandled === '1') return;
    target.dataset.skImgHandled = '1';
    const alt = target.getAttribute('alt') || '';
    const placeholder = Sanki.makePlaceholder('img-fallback', alt);
    if (target.parentNode) target.parentNode.replaceChild(placeholder, target);
  }, true);

  /** Money formatter: accepts cents, outputs store-locale string. */
  function money(cents) {
    const c = parseInt(cents, 10) || 0;
    // Bypass H.money. Go straight to Indian-locale rupee formatting so we
    // don't inherit any quirks of shop.money_format.
    const rupees = Math.round(c / 100);
    let out;
    try { out = rupees.toLocaleString('en-IN', { maximumFractionDigits: 0 }); }
    catch (_) { out = String(rupees); }
    const cur = (window.Sanki && Sanki.shop && Sanki.shop.currency) || 'INR';
    return (cur === 'INR' ? '₹' : (cur + ' ')) + out;
  }

  /* -- DEPRECATED: legacy bestsellers grid AJAX, kept as no-op ------------ */
  function initBestsellersGridAjax_DEPRECATED() {
    const grid = document.querySelector('[data-sk-bestsellers-grid]');
    if (!grid || grid.dataset.skBestsellersInit === '1') return;
    grid.dataset.skBestsellersInit = '1';

    const limit = parseInt(grid.dataset.limit, 10) || 8;
    const url = '/collections/all/products.json?sort_by=best-selling&limit=' + encodeURIComponent(limit);

    fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((res) => { if (!res.ok) throw new Error('bad status'); return res.json(); })
      .then((json) => {
        const products = (json && json.products) || [];
        if (!products.length) return; // keep the Liquid-rendered fallback
        grid.innerHTML = products.map(renderBestsellerCard).join('');
        // Re-reveal cards + re-init any card-level interactions
        grid.querySelectorAll('[data-sk-reveal]').forEach((el) => el.classList.add('is-visible'));
        document.dispatchEvent(new CustomEvent('sanki:bestsellers:rendered', { detail: { count: products.length } }));
      })
      .catch(() => { /* silent: fallback stays in place */ });
  }

  function renderBestsellerCard(p) {
    const variant = (p.variants && p.variants[0]) || {};
    const price = parseFloat(variant.price) || 0;
    const compare = parseFloat(variant.compare_at_price) || 0;
    const available = !!variant.available;
    const onSale = compare > 0 && compare > price;
    const isNew = (p.tags || []).indexOf && (p.tags || []).indexOf('new') !== -1;

    const img0 = (p.images && p.images[0]) || null;
    const img1 = (p.images && p.images[1]) || null;
    const hasSecondary = !!img1;

    const url = '/products/' + p.handle;
    const title = H.escape ? H.escape(p.title || '') : String(p.title || '');

    // Price markup mirrors Liquid's rendering (prices from REST are in major units).
    const priceHtml = (() => {
      const fmt = (n) => money(n);
      if (onSale) {
        return '<span class="sk-price">'
          + '<span class="sk-price__compare">' + fmt(compare) + '</span>'
          + '<span class="sk-price__sale">' + fmt(price) + '</span>'
          + '</span>';
      }
      return '<span class="sk-price">' + fmt(price) + '</span>';
    })();

    const badges = []
      .concat(!available ? ['<span class="sk-badge sk-badge--soldout">Sold out</span>'] : [])
      .concat(onSale && available ? ['<span class="sk-badge sk-badge--sale">Sale</span>'] : [])
      .concat(isNew ? ['<span class="sk-badge sk-badge--new">New</span>'] : [])
      .join('');

    const imgHtml = img0
      ? '<img class="sk-card__img" src="' + sizedUrl(img0.src, 720) + '"'
        + ' srcset="' + sizedUrl(img0.src, 360) + ' 360w, ' + sizedUrl(img0.src, 540) + ' 540w, ' + sizedUrl(img0.src, 720) + ' 720w, ' + sizedUrl(img0.src, 1080) + ' 1080w"'
        + ' sizes="(min-width: 1200px) 25vw, (min-width: 768px) 33vw, 50vw"'
        + ' alt="' + title + '" loading="lazy" decoding="async">'
      : '';
    const img1Html = img1
      ? '<img class="sk-card__img-secondary" src="' + sizedUrl(img1.src, 720) + '" alt="" aria-hidden="true" loading="lazy" decoding="async">'
      : '';

    return ''
      + '<div data-sk-reveal>'
      +   '<article class="sk-card" data-sk-card data-product-id="' + p.id + '"'
      +     ' data-product-url="' + url + '" data-has-secondary="' + (hasSecondary ? 'true' : 'false') + '">'
      +     '<a class="sk-card__media-link" href="' + url + '" aria-label="' + title + '">'
      +       '<div class="sk-card__media">'
      +         imgHtml + img1Html
      +         (badges ? '<div class="sk-card__badges">' + badges + '</div>' : '')
      +       '</div>'
      +     '</a>'
      +     '<div class="sk-card__body">'
      +       '<h3 class="sk-card__title"><a href="' + url + '">' + title + '</a></h3>'
      +       '<div class="sk-card__meta">' + priceHtml + '</div>'
      +     '</div>'
      +   '</article>'
      + '</div>';
  }

  function sizedUrl(src, width) {
    if (!src) return '';
    // Shopify CDN URLs: insert _{width}x before the file extension
    try {
      const u = new URL(src, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (e) { return src; }
  }

  /* INTENTIONALLY EMPTY — there used to be a second `money(amount)`
     declaration here that treated its input as MAJOR units, multiplied
     by 100, and forwarded to H.money. Because JS hoists function
     declarations and the LATER declaration wins, this overrode the
     correct cents-based money() further up the file (line ~1370) and
     was responsible for the 100× catalog-card price bug
     (₹149,900 instead of ₹1,499). The single canonical money() at
     the top of this module is now the only one. */

  /* -- Spin & Win modal ----------------------------------------------------*
   * Canvas-drawn wheel with 8 weighted reward segments. Opens when any
   * [data-sk-spin-open] element is clicked (typically the footer marquee).
   * Uses localStorage for a 24-hour re-spin lockout. Discount codes shown
   * are display-only — merchants create matching codes in Shopify admin.
   */
  // Spin-wheel slice colours — colourful original palette restored
  // (emerald, classic gold, pastel blue, deep emerald, silver, magenta-
  // pink, royal purple, neon orange). The wheel is a deliberate burst
  // of colour — it's the one celebratory "casino" moment in the theme.
  const SPIN_REWARDS = [
    { label: 'FREE SHIP',  code: 'SPINSHIP',    color: '#50C878', weight: 14, intro: 'Free shipping on your next order.' },
    { label: '₹500 OFF',   code: 'SPIN500',     color: '#D4AF37', weight: 20, intro: 'Flat ₹500 off on orders ₹8,000+.' },
    { label: '10% OFF',    code: 'SPIN10',      color: '#AEC6CF', weight: 20, intro: '10% off on your cart.' },
    { label: 'MYSTERY',    code: 'SPINMYSTERY', color: '#2E7D5B', weight: 8,  intro: 'A surprise gift on your next order!' },
    { label: 'TRY AGAIN',  code: '',            color: '#C0C0C0', weight: 14, intro: '' },
    { label: '15% OFF',    code: 'SPIN15',      color: '#E1306C', weight: 12, intro: '15% off on your cart.' },
    { label: '₹1000 OFF',  code: 'SPIN1000',    color: '#8134AF', weight: 7,  intro: 'Flat ₹1,000 off on orders ₹10,000+.' },
    { label: '20% OFF',    code: 'SPIN20',      color: '#F58529', weight: 5,  intro: '20% off on your cart — huge win!' }
  ];
  const SPIN_STORAGE_KEY = 'sanki:spin:last';
  const SPIN_LOCK_MS = 24 * 60 * 60 * 1000; // 24 hours

  /* ═══════════════════════════════════════════════════════════════════
     3D CUBE DISCOUNT — replacement for Spin & Win.

     UX flow:
       1. Modal opens (any [data-sk-spin-open] click). Cube idles in
          slow Y-axis rotation. Six floating discount cards drift
          upward around it.
       2. User clicks "Unscramble" → enters frenzy state: rapid
          multi-axis rotation, scale pulse, glow ramp.
       3. After ~2.5s, cube smoothly snaps to the chosen face.
          Reward panel slides in with code + copy button.
       4. localStorage gates re-solving for 24h.

     Reward palette is shared with the legacy SPIN_REWARDS array
     declared above so existing Shopify discount codes still match.
     ═══════════════════════════════════════════════════════════════════ */
  const CUBE_REWARDS = [
    /* Index → cube face mapping is fixed:
       0=front (15% OFF), 1=back (FREE SHIP), 2=right (₹500 OFF),
       3=left (10% OFF), 4=top (MYSTERY), 5=bottom (+1 FREE) */
    { label: '15% OFF',   code: 'CUBE15',      tier: 'high',   weight: 12, intro: '15% off your cart.', desc: '15% off — limit one per customer.' },
    { label: 'FREE SHIP', code: 'CUBESHIP',    tier: 'mid',    weight: 18, intro: 'Free shipping on your next order.', desc: 'Free shipping, no minimum.' },
    { label: '₹500 OFF',  code: 'CUBE500',     tier: 'mid',    weight: 18, intro: 'Flat ₹500 off on orders ₹8,000+.', desc: 'Flat ₹500 off — applies above ₹8,000.' },
    { label: '10% OFF',   code: 'CUBE10',      tier: 'low',    weight: 22, intro: '10% off your cart.', desc: '10% off — sitewide, this code only.' },
    { label: 'MYSTERY',   code: 'CUBEMYSTERY', tier: 'high',   weight: 8,  intro: 'A surprise gift on your next order.', desc: 'A free surprise gift, sent with your next order.' },
    { label: '+1 FREE',   code: 'CUBEPLUSONE', tier: 'top',    weight: 4,  intro: 'Buy any tee — get a Sanki sticker pack.', desc: 'Buy any tee, get a sticker pack free.' }
  ];
  const CUBE_STORAGE_KEY = 'sanki:cube:last';
  const CUBE_LOCK_MS = 24 * 60 * 60 * 1000;

  /* Each face's natural rotation (in degrees) so the JS can snap
     the cube to a chosen face by setting the matching transform. */
  const CUBE_FACE_ROTATIONS = {
    0: { x:    0, y:    0 },   /* front  */
    1: { x:    0, y:  180 },   /* back   */
    2: { x:    0, y:  -90 },   /* right  */
    3: { x:    0, y:   90 },   /* left   */
    4: { x:  -90, y:    0 },   /* top    */
    5: { x:   90, y:    0 }    /* bottom */
  };

  function pickWeightedReward() {
    const total = CUBE_REWARDS.reduce((s, r) => s + (r.weight || 1), 0);
    let roll = Math.random() * total;
    for (let i = 0; i < CUBE_REWARDS.length; i++) {
      roll -= (CUBE_REWARDS[i].weight || 1);
      if (roll <= 0) return i;
    }
    return CUBE_REWARDS.length - 1;
  }

  /* ───────────────────────────────────────────────────────────
     Rubik's cube engine — pure CSS-3D, no Three.js.
     27 cubies in a 3×3×3 grid, real layer rotations animated
     via rAF interpolation, position + rotation baked back into
     each cubie's state after every move so subsequent moves
     operate on the new arrangement.

     Rotation math (right-handed coords; CSS is left-handed but
     the math composes correctly because CSS y-axis sign cancels
     consistently across translate + rotate). Position rotation
     for a 90° turn around each axis (dir = +1 / -1):
       X axis: (x, y, z) → (x, -dir*z,  dir*y)
       Y axis: (x, y, z) → ( dir*z, y, -dir*x)
       Z axis: (x, y, z) → (-dir*y,  dir*x, z)
     Rotation matrices use the same conventions.
     ─────────────────────────────────────────────────────────── */

  /* Identity 3×3 matrix */
  function identityM3() { return [[1,0,0],[0,1,0],[0,0,1]]; }
  /* Multiply two 3×3 matrices */
  function multM3(A, B) {
    const r = [[0,0,0],[0,0,0],[0,0,0]];
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        r[i][j] = A[i][0]*B[0][j] + A[i][1]*B[1][j] + A[i][2]*B[2][j];
      }
    }
    return r;
  }
  /* 90° rotation matrix around X/Y/Z, dir = +1 (CW looking from +axis) or -1 */
  function rotM3(axis, dir) {
    const c = 0;
    const s = dir > 0 ? 1 : -1;
    if (axis === 'x') return [[1,0,0],[0,c,-s],[0,s,c]];
    if (axis === 'y') return [[c,0,s],[0,1,0],[-s,0,c]];
    return [[c,-s,0],[s,c,0],[0,0,1]];
  }
  /* CSS matrix3d() expects column-major. Convert a 3×3 row-major
     rotation to a 4×4 CSS matrix3d string. */
  function m3ToCss(m) {
    return 'matrix3d(' +
      m[0][0]+','+m[1][0]+','+m[2][0]+',0,' +
      m[0][1]+','+m[1][1]+','+m[2][1]+',0,' +
      m[0][2]+','+m[1][2]+','+m[2][2]+',0,' +
      '0,0,0,1)';
  }
  /* Rotate a logical position vector (x, y, z) ∈ {-1, 0, 1}³ by
     a 90° turn. Uses dir-1 sign convention matching rotM3. */
  function rotPos(x, y, z, axis, dir) {
    const s = dir > 0 ? 1 : -1;
    if (axis === 'x') return { x, y: -s*z, z:  s*y };
    if (axis === 'y') return { x:  s*z, y, z: -s*x };
    return { x: -s*y, y:  s*x, z };
  }

  function initCubeDiscount() {
    const modal = document.querySelector('[data-sk-cube]');
    if (!modal || modal.dataset.skCubeInit === '1') return;
    modal.dataset.skCubeInit = '1';

    /* ── Dev kill-switch: 24h lockout disabled per merchant ──
       Flip CUBE_LOCK_ENABLED back to true to re-arm the
       once-per-24h limit. While disabled, every modal open and
       every "Unscramble" click plays the full animation and
       reveals a fresh reward. We also clear any pre-existing
       lockout on init so users who already solved during the
       locked phase aren't stuck on the reward screen. */
    const CUBE_LOCK_ENABLED = false;
    if (!CUBE_LOCK_ENABLED) {
      try { localStorage.removeItem(CUBE_STORAGE_KEY); } catch (_) { /* private mode — ignore */ }
    }

    const dialog       = modal.querySelector('.sk-cube__dialog');
    const cube         = modal.querySelector('[data-sk-cube-mesh]');
    const stage        = modal.querySelector('[data-sk-cube-stage]');
    const solveBtn     = modal.querySelector('[data-sk-cube-solve]');
    const ctaLabel     = modal.querySelector('[data-sk-cube-cta-label]');
    const intro        = modal.querySelector('[data-sk-cube-intro]');
    const reward       = modal.querySelector('[data-sk-cube-reward]');
    const rewardTitle  = modal.querySelector('[data-sk-cube-reward-title]');
    const rewardDesc   = modal.querySelector('[data-sk-cube-reward-desc]');
    const rewardCode   = modal.querySelector('[data-sk-cube-reward-code]');
    const rewardMeta   = modal.querySelector('[data-sk-cube-reward-meta]');
    const copyBtn      = modal.querySelector('[data-sk-cube-copy]');
    const copyLabel    = modal.querySelector('[data-sk-cube-copy-label]');
    const closeButtons = modal.querySelectorAll('[data-sk-cube-close]');
    const floats       = modal.querySelectorAll('[data-sk-cube-float]');

    if (!cube || !solveBtn) return;

    /* ── Modal open / close ──────────────────────────────────── */
    let lastFocused = null;
    function openModal() {
      lastFocused = document.activeElement;
      modal.hidden = false;
      modal.removeAttribute('aria-hidden');
      requestAnimationFrame(() => modal.classList.add('is-open'));
      document.body.classList.add('sk-cube-open');
      /* If user already solved within the lockout, jump straight to
         the reward panel — show the same code, no re-solve.
         Bypassed entirely while CUBE_LOCK_ENABLED = false. */
      const stored = CUBE_LOCK_ENABLED ? readStoredReward() : null;
      if (stored && Date.now() - stored.t < CUBE_LOCK_MS) {
        showReward(stored.idx, /* skipAnim */ true);
      } else {
        resetState();
        startIdle();
      }
      if (solveBtn) solveBtn.focus({ preventScroll: true });
    }
    function closeModal() {
      modal.classList.remove('is-open');
      document.body.classList.remove('sk-cube-open');
      stopIdle();
      setTimeout(() => {
        modal.hidden = true;
        modal.setAttribute('aria-hidden', 'true');
        if (lastFocused && lastFocused.focus) lastFocused.focus({ preventScroll: true });
      }, 320);
    }

    /* Hook every [data-sk-spin-open] trigger across the page —
       footer marquee, cart upsell, checkout intent. The legacy
       attribute name is preserved so we don't have to retag every
       integration point. */
    document.querySelectorAll('[data-sk-spin-open], [data-sk-cube-open]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openModal();
      });
    });
    closeButtons.forEach((b) => b.addEventListener('click', closeModal));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hidden) closeModal();
    });

    /* ════════════════════════════════════════════════════════
       WebGL Rubik's cube engine — Three.js (lazy-loaded ESM)
       ════════════════════════════════════════════════════════
       The cube is rendered into the [data-sk-cube-canvas] using
       Three.js with PBR materials, procedural HDR reflections
       (RoomEnvironment), ACES tone mapping, and an UnrealBloom
       postprocessing pass for the cinematic glow you'd see on
       resend.com or linear.app.

       Three.js + post-processing (~700KB) loads from esm.sh on
       first modal open via dynamic `import()`. The homepage
       never pays the cost. */

    const canvas  = modal.querySelector('[data-sk-cube-canvas]');
    const loading = modal.querySelector('[data-sk-cube-loading]');

    /* Lazy import — promise is reused across reopens. */
    let _threePromise = null;
    function loadThreeStack() {
      if (_threePromise) return _threePromise;
      const base = 'https://esm.sh/three@0.160.0';
      _threePromise = Promise.all([
        import(/* @vite-ignore */ base),
        import(/* @vite-ignore */ base + '/examples/jsm/environments/RoomEnvironment.js'),
        import(/* @vite-ignore */ base + '/examples/jsm/postprocessing/EffectComposer.js'),
        import(/* @vite-ignore */ base + '/examples/jsm/postprocessing/RenderPass.js'),
        import(/* @vite-ignore */ base + '/examples/jsm/postprocessing/UnrealBloomPass.js'),
        import(/* @vite-ignore */ base + '/examples/jsm/postprocessing/OutputPass.js'),
        /* Thick-line classes — needed for true pixel-width line
           rendering. WebGL's default LineBasicMaterial.linewidth
           is silently ignored by every desktop browser, so the
           edges we draw must use this LineSegments2 family. */
        import(/* @vite-ignore */ base + '/examples/jsm/lines/LineSegmentsGeometry.js'),
        import(/* @vite-ignore */ base + '/examples/jsm/lines/LineSegments2.js'),
        import(/* @vite-ignore */ base + '/examples/jsm/lines/LineMaterial.js'),
        /* RoundedBoxGeometry — premium card body with bevelled
           edges. Used for the 6 floating Forbes Legacy passes. */
        import(/* @vite-ignore */ base + '/examples/jsm/geometries/RoundedBoxGeometry.js')
      ]).then(function (mods) {
        return {
          THREE:           mods[0],
          RoomEnvironment: mods[1].RoomEnvironment,
          EffectComposer:  mods[2].EffectComposer,
          RenderPass:      mods[3].RenderPass,
          UnrealBloomPass: mods[4].UnrealBloomPass,
          OutputPass:      mods[5].OutputPass,
          LineSegmentsGeometry: mods[6].LineSegmentsGeometry,
          LineSegments2:        mods[7].LineSegments2,
          LineMaterial:         mods[8].LineMaterial,
          RoundedBoxGeometry:   mods[9].RoundedBoxGeometry
        };
      }).catch(function (err) {
        console.warn('[Sanki] WebGL cube failed to load:', err);
        _threePromise = null;
        throw err;
      });
      return _threePromise;
    }

    /* Engine handle (populated once Three is ready) */
    let engine = null;
    let solving = false;
    let solveSequence = null;

    async function buildEngine() {
      if (engine || !canvas) return engine;
      const t = await loadThreeStack();
      const THREE = t.THREE;

      /* ── Scene + renderer ──────────────────────────────── */
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
      camera.position.set(0, 0, 8.6);
      camera.lookAt(0, 0, 0);

      const renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.05;
      renderer.setClearColor(0x000000, 0);

      /* Procedural environment for PBR reflections — no warm
         tints, no atmospheric glow. Just a neutral studio so
         the obsidian surface picks up clean specular highlights. */
      const pmrem = new THREE.PMREMGenerator(renderer);
      const envTex = pmrem.fromScene(new t.RoomEnvironment(), 0.04).texture;
      scene.environment = envTex;

      /* Even, neutral lighting — no warm/cool casts, no bloom
         feeders. The cube reads as a single material under
         studio illumination. */
      scene.add(new THREE.HemisphereLight(0xFFFFFF, 0.45));
      const key = new THREE.DirectionalLight(0xFFFFFF, 1.2);
      key.position.set(5, 6, 4);
      scene.add(key);
      const fill = new THREE.DirectionalLight(0xFFFFFF, 0.45);
      fill.position.set(-5, -2, 4);
      scene.add(fill);

      /* ── Cube root + 27 cubies ─────────────────────────── */
      const cubeRoot = new THREE.Group();
      /* 0.8 → cube renders at 80% of its previous footprint per
         merchant. Scaling at the root preserves all internal
         geometry, edge-line resolution, and PBR reflections —
         we just shrink the whole thing uniformly. */
      cubeRoot.scale.setScalar(0.8);
      scene.add(cubeRoot);

      /* Cubie geometry — close-to-square spacing for the classic
         Rubik's grid look. SIZE 0.98 with 1.0 spacing gives a
         hairline seam between cubies (clearly readable as a 3×3
         grid) without yawning dark gaps. */
      const SIZE = 0.98;
      const SPACING = 1.0;
      const geo = new THREE.BoxGeometry(SIZE, SIZE, SIZE, 1, 1, 1);

      /* Polished black obsidian — sized for high contrast against
         the ivory backdrop. Near-pure black base with high metalness
         and low roughness so the cube reads as a deep mirror block
         that catches studio reflections cleanly. */
      function makeMaterial() {
        return new THREE.MeshPhysicalMaterial({
          color: 0x05060A,
          metalness: 0.95,
          roughness: 0.12,
          envMapIntensity: 0.85,
          reflectivity: 1.0,
          clearcoat: 0.6,
          clearcoatRoughness: 0.08
        });
      }

      /* Fire-glow edge wireframe — every cubie traces its 12
         edges with a thick LineSegments2. LineMaterial uses
         screen-space pixel widths (true thickness), unlike the
         legacy LineBasicMaterial whose `linewidth` is silently
         ignored by every desktop browser.

         The geometry is built ONCE from EdgesGeometry's flat
         positions buffer and shared across all 27 cubies. The
         material is also shared. So the entire fire wireframe
         set is one buffer + one material, applied 27 times. */
      const baseEdgeGeo = new THREE.EdgesGeometry(geo);
      const edgePositions = baseEdgeGeo.attributes.position.array;
      const edgeSegGeo = new t.LineSegmentsGeometry().setPositions(edgePositions);

      const initW = canvas.clientWidth || 800;
      const initH = canvas.clientHeight || 600;
      const edgeMat = new t.LineMaterial({
        color: 0xFF5A1A,
        linewidth: 3.5,               /* slightly tamed — smaller cap area */
        worldUnits: false,
        resolution: new THREE.Vector2(initW, initH),
        transparent: true,
        opacity: 0.92,                /* sub-1.0 so cap overlaps don't
                                         double-up to a hard 2.0 brightness */
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
        dashed: false
      });

      const cubies = [];
      for (let xi = -1; xi <= 1; xi++) {
        for (let yi = -1; yi <= 1; yi++) {
          for (let zi = -1; zi <= 1; zi++) {
            const m = new THREE.Mesh(geo, makeMaterial());
            m.position.set(xi * SPACING, yi * SPACING, zi * SPACING);
            const edges = new t.LineSegments2(edgeSegGeo, edgeMat);
            edges.computeLineDistances();
            m.add(edges);
            cubeRoot.add(m);
            cubies.push({ x: xi, y: yi, z: zi, mesh: m });
          }
        }
      }

      /* (Forbes Legacy cards moved out of the WebGL scene per
         merchant May 2026 — they now render as CSS DOM elements
         spread across the modal *outside* the cube's bounded
         box, so the cube viewport stays the singular focal
         point. See `.sk-cube__float--*` rules in styles.css.) */

      /* ── Postprocessing — uniform fire-glow bloom ──────────
         Threshold lowered + radius bumped up so the bloom
         spreads across the entire fire wireframe uniformly,
         instead of disproportionately amplifying the cap
         junctions where two edges meet. Result: a continuous
         glowing ribbon, no bright dot artefacts at corners. */
      const composer = new t.EffectComposer(renderer);
      composer.addPass(new t.RenderPass(scene, camera));
      const bloom = new t.UnrealBloomPass(
        new THREE.Vector2(canvas.clientWidth || 800, canvas.clientHeight || 600),
        1.05,   /* strength — slightly stronger overall */
        0.95,   /* radius — much softer, wider halo blends caps in */
        0.45    /* threshold — more of the wireframe contributes
                   uniformly, so cap overlaps no longer stand out */
      );
      composer.addPass(bloom);
      composer.addPass(new t.OutputPass());

      /* ── Sizing ──────────────────────────────────────── */
      function resize() {
        const r = canvas.getBoundingClientRect();
        const w = Math.max(1, r.width);
        const h = Math.max(1, r.height);
        renderer.setSize(w, h, false);
        composer.setSize(w, h);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        /* LineMaterial reads its `resolution` uniform every frame
           to compute pixel-space line widths. Without this, the
           fire edges go invisible-thin after any viewport change. */
        edgeMat.resolution.set(w, h);
      }
      resize();
      const ro = new ResizeObserver(resize); ro.observe(canvas);

      /* ── Cinematic idle — cube stands on a corner ──────────
         Pose: rotate the cube so its body-diagonal axis (going
         from corner [-1,-1,-1] through the centre to corner
         [1,1,1]) aligns with the world Y axis. The cube now sits
         on one corner with the opposite corner pointing straight
         up. From that pose we slowly spin around world Y — so
         the cube appears to rotate ON its standing tip, like a
         polished obsidian gyroscope. A gentle sin-bob and a
         very subtle nutation (a slow precession of the spin
         axis) give it the cinematic "floating sculpture" feel. */
      const baseQuat = new THREE.Quaternion().setFromUnitVectors(
        new THREE.Vector3(1, 1, 1).normalize(),
        new THREE.Vector3(0, 1, 0)
      );
      const spinQuat  = new THREE.Quaternion();
      const tiltQuat  = new THREE.Quaternion();
      const yAxis     = new THREE.Vector3(0, 1, 0);
      const tiltAxis  = new THREE.Vector3();

      const startTime = performance.now();
      let layerPivot = null;
      let layerActive = null;
      function animate() {
        const now = performance.now();
        const t = (now - startTime) / 1000;

        if (!layerActive) {
          /* Slow continuous spin around world Y (the standing
             corner's body-diagonal axis). 0.36 rad/s ≈ one full
             revolution every 17 seconds — meditative, premium. */
          spinQuat.setFromAxisAngle(yAxis, t * 0.36);

          /* Subtle nutation: a 3.5° tilt whose direction itself
             slowly rotates. This adds a tiny "wobble of a top
             that's almost perfectly balanced" — cinematic life
             without breaking the corner-stand pose. */
          const nutationAngle = 0.06; /* ≈ 3.4° */
          const precess = t * 0.18;
          tiltAxis.set(Math.cos(precess), 0, Math.sin(precess));
          tiltQuat.setFromAxisAngle(tiltAxis, nutationAngle);

          /* Compose: baseQuat (corner-stand) → spinQuat (world Y
             spin) → tiltQuat (precessing nutation). Quaternion
             multiplication reads right-to-left in object frames. */
          cubeRoot.quaternion
            .copy(tiltQuat)
            .multiply(spinQuat)
            .multiply(baseQuat);

          /* Gentle vertical float */
          cubeRoot.position.y = Math.sin(t * 0.55) * 0.18;
        }

        composer.render();
        rafId = requestAnimationFrame(animate);
      }
      let rafId = requestAnimationFrame(animate);

      /* ── Layer rotation: parent the 9 layer cubies to a
         pivot Group, animate the pivot's rotation, then bake
         the rotation back into each mesh and re-parent to
         cubeRoot. Mirrors a real Rubik's cube turn. */
      function rotPos(c, axis, dir) {
        const s = dir > 0 ? 1 : -1;
        if (axis === 'x') return { x: c.x, y: -s*c.z, z:  s*c.y };
        if (axis === 'y') return { x:  s*c.z, y: c.y, z: -s*c.x };
        return                     { x: -s*c.y, y:  s*c.x, z: c.z };
      }
      function performMove(axis, slice, dir, duration) {
        duration = duration || 320;
        return new Promise(function (resolve) {
          layerActive = { axis, slice };
          const pivot = new THREE.Group();
          cubeRoot.add(pivot);
          layerPivot = pivot;
          const layer = cubies.filter(function (c) { return c[axis] === slice; });
          layer.forEach(function (c) { pivot.attach(c.mesh); });

          const target = dir * Math.PI / 2;
          const t0 = performance.now();
          function tick() {
            const dt = Math.min(1, (performance.now() - t0) / duration);
            const eased = 1 - Math.pow(1 - dt, 3);
            const a = eased * target;
            if (axis === 'x') pivot.rotation.x = a;
            if (axis === 'y') pivot.rotation.y = a;
            if (axis === 'z') pivot.rotation.z = a;
            if (dt < 1) requestAnimationFrame(tick);
            else {
              /* Bake: re-parent to cubeRoot preserving world
                 transform; update each cubie's logical (x,y,z). */
              layer.forEach(function (c) {
                cubeRoot.attach(c.mesh);
                const np = rotPos(c, axis, dir);
                c.x = np.x; c.y = np.y; c.z = np.z;
              });
              cubeRoot.remove(pivot);
              layerActive = null;
              layerPivot  = null;
              resolve();
            }
          }
          requestAnimationFrame(tick);
        });
      }

      /* Apply moves instantly (used for initial scramble) */
      function applyMovesInstant(moves) {
        moves.forEach(function (m) {
          const axis = m[0], slice = m[1], dir = m[2];
          const layer = cubies.filter(function (c) { return c[axis] === slice; });
          /* Build rotation quaternion */
          const q = new THREE.Quaternion();
          const v = new THREE.Vector3(
            axis === 'x' ? 1 : 0,
            axis === 'y' ? 1 : 0,
            axis === 'z' ? 1 : 0
          );
          q.setFromAxisAngle(v, dir * Math.PI / 2);
          layer.forEach(function (c) {
            c.mesh.position.applyQuaternion(q);
            c.mesh.quaternion.premultiply(q);
            const np = rotPos(c, axis, dir);
            c.x = np.x; c.y = np.y; c.z = np.z;
          });
        });
      }

      const SCRAMBLE = [
        ['x',  1,  1], ['y',  1,  1], ['z',  1,  1], ['x', -1, -1],
        ['y', -1,  1], ['z', -1, -1], ['x',  1, -1], ['y',  1,  1]
      ];
      const UNSCRAMBLE = SCRAMBLE.slice().reverse().map(function (m) {
        return [m[0], m[1], -m[2]];
      });
      solveSequence = UNSCRAMBLE;

      /* Apply scramble immediately so the cube opens looking
         scrambled. */
      applyMovesInstant(SCRAMBLE);

      /* Cleanup on close — keep engine alive but pause rAF */
      function dispose() {
        cancelAnimationFrame(rafId);
        ro.disconnect();
      }
      function resume() {
        rafId = requestAnimationFrame(animate);
      }

      engine = { performMove, applyMovesInstant, scramble: SCRAMBLE, unscramble: UNSCRAMBLE, dispose, resume };

      /* Hide loading shim once first frame paints */
      if (loading) loading.style.opacity = '0';
      setTimeout(function () { if (loading) loading.style.display = 'none'; }, 320);

      return engine;
    }


    /* ── Idle / scramble lifecycle (delegates to engine) ── */
    function startIdle() { /* engine handles its own rAF */ }
    function stopIdle()  { /* no-op */ }

    function resetState() {
      reward.hidden = true;
      floats.forEach(function (f) { f.classList.remove('is-winner'); });
      if (solveBtn) { solveBtn.disabled = false; solveBtn.classList.remove('is-busy'); }
      if (ctaLabel) ctaLabel.textContent = 'Unscramble';
      if (intro)    intro.style.display = '';
      /* Build engine on first open; subsequent opens just
         re-scramble the existing scene. */
      buildEngine().then(function () {
        if (engine) {
          /* Re-scramble for a fresh chaotic opening */
          /* We track no scramble history per session; just
             re-apply the SCRAMBLE move set. State is mutated
             by previous solves, but the visual remains
             plausibly scrambled because moves compose. */
        }
      }).catch(function () {
        if (loading) loading.querySelector('.sk-cube__loading-text').textContent = '3D unavailable — fallback view active.';
        modal.setAttribute('data-cube-fallback', '1');
      });
    }

    async function solve() {
      if (CUBE_LOCK_ENABLED) {
        const stored = readStoredReward();
        if (stored && Date.now() - stored.t < CUBE_LOCK_MS) {
          showReward(stored.idx, true);
          return;
        }
      }
      if (solving) return;
      solving = true;
      try {
        await buildEngine();
      } catch (e) {
        solving = false;
        return;
      }
      const chosenIdx = pickWeightedReward();
      if (solveBtn) { solveBtn.disabled = true; solveBtn.classList.add('is-busy'); }
      if (ctaLabel) ctaLabel.textContent = 'Solving…';
      if (intro)    intro.style.display = 'none';
      /* Play the unscramble sequence one move at a time */
      const seq = (engine && engine.unscramble) || solveSequence || [];
      for (let i = 0; i < seq.length; i++) {
        const m = seq[i];
        await engine.performMove(m[0], m[1], m[2], 280);
      }
      await new Promise(function (r) { setTimeout(r, 240); });
      showReward(chosenIdx, false);
      persistReward(chosenIdx);
      solving = false;
    }

    function showReward(idx, skipAnim) {
      const r = CUBE_REWARDS[idx];
      if (!r) return;

      if (rewardTitle) rewardTitle.textContent = r.label;
      if (rewardDesc)  rewardDesc.textContent  = r.desc || r.intro || '';
      if (rewardCode)  rewardCode.textContent  = r.code || '—';
      reward.hidden = false;
      if (ctaLabel) ctaLabel.textContent = 'Reveal again';
      if (solveBtn) {
        solveBtn.disabled = false;
        solveBtn.classList.remove('is-busy');
      }
      cube.classList.add('is-locked');

      /* Light up the matching CSS Forbes pass — cards live as
         DOM elements spread across the modal outside the cube's
         WebGL viewport. */
      floats.forEach((f) => {
        f.classList.toggle('is-winner', parseInt(f.dataset.skCubeFloat, 10) === idx);
      });
    }

    /* Storage helpers — guard against private-mode storage
       throws by wrapping in try/catch. */
    function persistReward(idx) {
      try {
        localStorage.setItem(CUBE_STORAGE_KEY, JSON.stringify({ idx: idx, t: Date.now() }));
      } catch (_) { /* private mode or quota — silently ignore */ }
    }
    function readStoredReward() {
      try {
        const raw = localStorage.getItem(CUBE_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed.idx !== 'number' || typeof parsed.t !== 'number') return null;
        return parsed;
      } catch (_) { return null; }
    }

    /* ── Solve button + copy button bindings ─────────────── */
    solveBtn.addEventListener('click', solve);
    if (copyBtn) {
      copyBtn.addEventListener('click', async () => {
        const code = rewardCode ? rewardCode.textContent.trim() : '';
        if (!code || code === '—') return;
        try {
          await navigator.clipboard.writeText(code);
          if (copyLabel) {
            const orig = copyLabel.textContent;
            copyLabel.textContent = 'Copied!';
            setTimeout(() => { copyLabel.textContent = orig; }, 1600);
          }
        } catch (_) {
          /* Fallback: select the code element so the user can
             Cmd/Ctrl+C themselves. */
          const range = document.createRange();
          range.selectNode(rewardCode);
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
        }
      });
    }
  }

  function initSpinWin() {
    const modal   = document.querySelector('[data-sk-spin]');
    if (!modal || modal.dataset.skSpinInit === '1') return;
    modal.dataset.skSpinInit = '1';

    /* ═══════════════════════════════════════════════════════════════════
       TEMP — One-time spin-lockout reset (manual unlock).
       ═══════════════════════════════════════════════════════════════════
       Clears any existing 24h spin lockout ONCE per browser, so the
       merchant / tester can spin the wheel right now even if they spun
       it within the past 24h. After the user spins, the normal 24h
       lockout takes effect again.

       The reset is gated by a sentinel key (`sanki:spin:reset-token-1`)
       so it fires ONLY on the first page load after this code is
       deployed — not on every page load. Subsequent reloads see the
       sentinel and skip the reset, leaving normal lockout in place.

       To trigger another one-time unlock later: bump the token suffix
       (e.g. `-token-2`) and re-deploy. To remove this entirely:
       delete this whole try/catch block.
       ═══════════════════════════════════════════════════════════════════ */
    try {
      if (!localStorage.getItem('sanki:spin:reset-token-1')) {
        localStorage.removeItem(SPIN_STORAGE_KEY);
        localStorage.setItem('sanki:spin:reset-token-1', String(Date.now()));
      }
    } catch (e) { /* private browsing / quota — ignore */ }
    /* ═══════════════ END TEMP UNLOCK ═══════════════════════════════════ */

    const canvas  = modal.querySelector('#SkSpinWheel');
    const goBtn   = modal.querySelector('[data-sk-spin-go]');
    const result  = modal.querySelector('[data-sk-spin-result]');
    const locked  = modal.querySelector('[data-sk-spin-locked]');
    const intro   = modal.querySelector('[data-sk-spin-intro]');
    const prizeEl = modal.querySelector('[data-sk-spin-prize]');
    const bodyEl  = modal.querySelector('[data-sk-spin-body]');
    const codeEl  = modal.querySelector('[data-sk-spin-code]');
    const codeRow = modal.querySelector('[data-sk-spin-code-row]');
    const copyBtn = modal.querySelector('[data-sk-spin-copy]');
    const wheelWrap = modal.querySelector('[data-sk-spin-wheel-wrap]');
    let currentRotation = 0;
    let spinning = false;

    if (!canvas) return;
    drawWheel(canvas, SPIN_REWARDS);

    // Helper: toggle visibility via class — `hidden` attribute alone is
    // overridden by any CSS `display:` rule, which was leaving the SPIN NOW
    // button visible during the locked state.
    const stage   = modal.querySelector('[data-sk-spin-wheel-wrap]') ? modal.querySelector('.sk-spin__stage') : null;
    const setHidden = (el, hide) => {
      if (!el) return;
      el.hidden = hide;
      el.classList.toggle('is-hidden', hide);
    };

    const open = () => {
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      modal.getBoundingClientRect();
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      // Re-draw the wheel NOW that the canvas is visible and measured.
      // At init time the modal was display:none so the canvas had 0×0
      // size; drawing at that point produced a blank-then-stretched
      // result on first open. Calling drawWheel here, after the modal
      // is laid out, guarantees the wheel paints crisply at the
      // canvas's actual rendered size on every open.
      requestAnimationFrame(() => drawWheel(canvas, SPIN_REWARDS));

      // Respect 24h lockout
      const last = parseInt(localStorage.getItem(SPIN_STORAGE_KEY) || '0', 10);
      const now = Date.now();
      const locked24 = last && (now - last) < SPIN_LOCK_MS;
      if (locked24) {
        setHidden(goBtn, true);
        setHidden(stage, true);
        if (intro) setHidden(intro, true);
        setHidden(result, true);
        setHidden(locked, false);
      } else {
        setHidden(goBtn, false);
        setHidden(stage, false);
        if (intro) setHidden(intro, false);
        setHidden(result, true);
        setHidden(locked, true);
      }
    };

    const close = () => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      setTimeout(() => { modal.hidden = true; }, 260);
    };

    // Open triggers
    document.querySelectorAll('[data-sk-spin-open]').forEach((btn) => {
      btn.addEventListener('click', (e) => { e.preventDefault(); open(); });
    });
    modal.querySelectorAll('[data-sk-spin-close]').forEach((b) => b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });

    // Re-fit the wheel when the viewport resizes (e.g. user rotates a
    // phone or drags a desktop window narrower while the modal is open).
    // Debounced via rAF so we don't redraw on every pixel of resize.
    let resizeRaf = null;
    window.addEventListener('resize', () => {
      if (!modal.classList.contains('is-open')) return;
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        drawWheel(canvas, SPIN_REWARDS);
      });
    });

    // Spin!
    goBtn.addEventListener('click', () => {
      if (spinning) return;
      spinning = true;
      goBtn.disabled = true;

      const chosenIdx = pickWeighted(SPIN_REWARDS);
      const reward = SPIN_REWARDS[chosenIdx];
      const segAngle = 360 / SPIN_REWARDS.length;
      // Pointer is at top (12 o'clock). Segments are drawn starting at 0rad (3 o'clock) clockwise.
      // Target rotation: put segment center at top = -90deg minus segment center angle.
      const segCenterDeg = chosenIdx * segAngle + segAngle / 2;
      // Add 6 full rotations for dramatic effect, plus random jitter within segment
      const jitter = (Math.random() * 0.6 - 0.3) * segAngle; // ±30% of segment
      const target = 360 * 6 - (segCenterDeg - 270) + jitter;
      currentRotation = target;

      wheelWrap.style.transition = 'transform 4.2s cubic-bezier(0.18, 0.82, 0.28, 1)';
      wheelWrap.style.transform = 'rotate(' + currentRotation + 'deg)';

      setTimeout(() => {
        localStorage.setItem(SPIN_STORAGE_KEY, String(Date.now()));
        showResult(reward);
        spinning = false;
      }, 4400);
    });

    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        const code = codeEl.textContent.trim();
        if (!code) return;
        try {
          navigator.clipboard.writeText(code);
          const orig = copyBtn.innerHTML;
          copyBtn.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" aria-hidden><path d="M5 12l5 5 9-11" stroke-linecap="round"/></svg> Copied';
          setTimeout(() => { copyBtn.innerHTML = orig; }, 1800);
        } catch (e) {
          if (H.toast) H.toast('Copied: ' + code);
        }
      });
    }

    function showResult(reward) {
      setHidden(goBtn, true);
      if (intro) setHidden(intro, true);
      setHidden(result, false);
      setHidden(locked, true);
      if (reward.code) {
        prizeEl.textContent = 'You won ' + reward.label + '!';
        bodyEl.textContent = reward.intro;
        codeEl.textContent = reward.code;
        codeRow.hidden = false;
      } else {
        prizeEl.textContent = 'Better luck next time';
        bodyEl.textContent = 'Come back in 24 hours for another chance to win.';
        codeRow.hidden = true;
      }
    }
  }

  function drawWheel(canvas, rewards) {
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    // RESPONSIVE SIZING. The previous implementation read `canvas.width`
    // (the static HTML attribute, e.g. 240) and forced
    // `canvas.style.width = "240px"` — which made the canvas display at
    // a fixed 240px regardless of how wide its container had been
    // sized by CSS. On narrow viewports / tighter dialogs the stage
    // would be e.g. 195px wide, but the canvas would still render at
    // 240px and overflow the wheel-wrap clip — visually cutting off
    // the slices on one side.
    //
    // Fix: read the canvas's ACTUAL rendered CSS size via
    // getBoundingClientRect, set the internal resolution to match
    // (multiplied by DPR for crisp drawing), and DO NOT touch
    // canvas.style.width/height so CSS layout retains full control.
    // Falls back to the HTML attribute if the canvas hasn't been laid
    // out yet (e.g. modal still hidden when drawWheel runs).
    const rect = canvas.getBoundingClientRect();
    const fallback = parseInt(canvas.getAttribute('width'), 10) || 240;
    const logicalW = rect.width  > 0 ? rect.width  : fallback;
    const logicalH = rect.height > 0 ? rect.height : fallback;

    canvas.width  = Math.round(logicalW * dpr);
    canvas.height = Math.round(logicalH * dpr);
    ctx.scale(dpr, dpr);

    const cx = logicalW / 2;
    const cy = logicalH / 2;
    // Inset the wheel 12px from the canvas edge so the gold outer ring
    // (drawn at R+4 with 4px stroke) doesn't kiss the circular clip
    // boundary. The previous 6px inset put the ring's outer edge at
    // exactly the clip radius, so antialiased pixels were being chopped.
    // 12px gives a clean visible margin all the way around.
    const R = Math.min(logicalW, logicalH) / 2 - 12;
    const n = rewards.length;
    const step = (Math.PI * 2) / n;

    // Outer ring
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, R + 4, 0, Math.PI * 2);
    ctx.lineWidth = 4;
    ctx.strokeStyle = '#D4AF37';
    ctx.stroke();
    ctx.restore();

    // Segments
    rewards.forEach((r, i) => {
      const start = i * step;
      const end   = start + step;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, R, start, end);
      ctx.closePath();
      ctx.fillStyle = r.color;
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.6)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Labels are NOT drawn on the canvas any more — they're HTML
      // <li> elements rendered over the canvas (see spin-win.liquid).
      // HTML labels avoid all canvas-text clipping issues, scale with
      // the wheel-wrap rotation, and render crisp at any DPR.
    });

    // Inner circle (will be hidden by the CSS .sk-spin__hub on top, but drawn for polish)
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, 28, 0, Math.PI * 2);
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#D4AF37';
    ctx.stroke();
    ctx.restore();
  }

  function luminance(hex) {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  function pickWeighted(list) {
    const total = list.reduce((s, x) => s + (x.weight || 1), 0);
    let r = Math.random() * total;
    for (let i = 0; i < list.length; i++) {
      r -= (list[i].weight || 1);
      if (r <= 0) return i;
    }
    return list.length - 1;
  }

  /* -- Image search (inside predictive search panel) -----------------------*/

  function initImageSearch() {
    const btn   = document.querySelector('[data-sk-image-search-toggle]');
    const input = document.querySelector('[data-sk-image-search-input]');
    const prev  = document.querySelector('[data-sk-image-search-preview]');
    const thumb = document.querySelector('[data-sk-image-search-thumb]');
    const status = document.querySelector('[data-sk-image-search-status]');
    const detail = document.querySelector('[data-sk-image-search-detail]');
    const clear = document.querySelector('[data-sk-image-search-clear]');
    const results = document.querySelector('[data-sk-search-results]');
    if (!btn || !input || !prev) return;
    if (btn.dataset.skImgInit === '1') return;
    btn.dataset.skImgInit = '1';

    btn.addEventListener('click', (e) => { e.preventDefault(); input.click(); });

    const reset = () => {
      input.value = '';
      prev.hidden = true;
      if (thumb) { thumb.src = ''; thumb.removeAttribute('src'); }
      if (results) results.innerHTML = '';
    };
    if (clear) clear.addEventListener('click', reset);

    input.addEventListener('change', () => {
      const file = input.files && input.files[0];
      if (!file) return;
      if (!/^image\//.test(file.type)) {
        H.toast && H.toast('Please pick an image file.');
        return;
      }

      // Show local preview immediately.
      const reader = new FileReader();
      reader.onload = (ev) => {
        if (thumb && ev.target) thumb.src = String(ev.target.result);
        prev.hidden = false;
        if (status) status.textContent = 'Analysing your image…';
        if (detail) detail.textContent = 'Looking for visually similar pieces';
      };
      reader.readAsDataURL(file);

      // Try the Sanki AI endpoint (falls back gracefully when not configured).
      const endpoint = (window.Sanki && window.Sanki.routes && window.Sanki.routes.imageSearch)
        || '/apps/sanki/search/image';
      const fd = new FormData();
      fd.append('image', file);

      fetch(endpoint, { method: 'POST', body: fd, credentials: 'same-origin' })
        .then((res) => {
          if (!res.ok) throw new Error('not configured');
          return res.json();
        })
        .then((json) => {
          const items = (json && json.products) || [];
          if (!items.length) throw new Error('empty');
          if (status) status.textContent = 'Matches found';
          if (detail) detail.textContent = items.length + ' product' + (items.length === 1 ? '' : 's') + ' close to your image';
          if (!results) return;
          results.innerHTML = items.map((p) => {
            const img = (p.image && (p.image.url || p.image)) || '';
            const url = p.url || '#';
            return (
              '<a href="' + H.escape(url) + '" class="sk-card">' +
                '<div class="sk-card__media">' +
                  (img ? '<img class="sk-card__img" src="' + img + '&width=540" alt="' + H.escape(p.title || '') + '">' : '') +
                '</div>' +
                '<div class="sk-card__body">' +
                  '<h3 class="sk-card__title">' + H.escape(p.title || '') + '</h3>' +
                  (p.price != null ? '<div class="sk-card__meta">' + H.money((p.price || 0) * 100) + '</div>' : '') +
                '</div>' +
              '</a>'
            );
          }).join('');
        })
        .catch(() => {
          if (status) status.textContent = 'Visual search coming soon';
          if (detail) detail.textContent = 'We’re training the model. Try searching by text meanwhile.';
        });
    });
  }

  /* -- Predictive search ----------------------------------------------------*/

  function initPredictiveSearch() {
    if (!Sanki.settings || !Sanki.settings.predictiveSearchEnabled) return;
    const form = document.querySelector('[data-sk-search-form]');
    const input = document.querySelector('[data-sk-search-input]');
    const results = document.querySelector('[data-sk-search-results]');
    const search = document.querySelector('[data-sk-search]');
    const suggestions = document.querySelector('[data-sk-search-suggestions]');
    if (!form || !input || !results) return;

    const setEmptyState = (isEmpty) => {
      // Show "Popular categories" chips while the input is empty; hide them
      // the moment the user starts typing or results stream in.
      if (suggestions) suggestions.style.display = isEmpty ? '' : 'none';
    };
    setEmptyState(true);

    const doSearch = H.debounce((q) => {
      if (!q || q.length < 2) {
        results.innerHTML = '';
        setEmptyState(true);
        return;
      }
      setEmptyState(false);
      API.search.predict(q, { type: 'product', limit: 8 })
        .then((r) => {
          const products = (r.resources && r.resources.results && r.resources.results.products) || [];
          if (!products.length) {
            results.innerHTML = '<p class="sk-muted" style="grid-column: 1 / -1; text-align: center; padding: 16px 0;">No products found for "' + H.escape(q) + '". Try a different keyword.</p>';
            return;
          }
          results.innerHTML = products.map((p) => {
            const img = (p.featured_image && (p.featured_image.url || p.featured_image)) || '';
            return (
              '<a href="' + H.escape(p.url) + '" class="sk-card">' +
                '<div class="sk-card__media">' +
                  (img ? '<img class="sk-card__img" src="' + img + '&width=540" alt="' + H.escape(p.title) + '">' : '') +
                '</div>' +
                '<div class="sk-card__body">' +
                  '<h3 class="sk-card__title">' + H.escape(p.title) + '</h3>' +
                  '<div class="sk-card__meta">' + H.money(p.price * 100) + '</div>' +
                '</div>' +
              '</a>'
            );
          }).join('');
        })
        .catch(() => { results.innerHTML = '<p class="sk-muted" style="grid-column: 1 / -1;">Search unavailable. Try again in a moment.</p>'; });
    }, 220);

    input.addEventListener('input', (e) => doSearch(e.target.value.trim()));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && search && search.classList.contains('is-open')) {
        search.classList.remove('is-open');
      }
    });
  }

  /* -- Product page --------------------------------------------------------*/

  /**
   * Wire the PDP "Back" button to use history.back() whenever the visitor
   * arrived from the same site. This is the single most important UX
   * improvement for catalog browsing: clicking back triggers the browser's
   * Back/Forward Cache (bfcache), which restores the catalog at the EXACT
   * scroll position, with filters / sort / page intact, with NO fetches.
   * It feels instant because nothing is being re-rendered — the browser
   * is replaying its in-memory snapshot of the page.
   *
   * Same-origin check: document.referrer is empty for direct visits / new
   * tabs / hard reloads. In those cases we fall through to the anchor's
   * native href (the collection URL), which is the right fallback because
   * there's no history to go back to.
   */

  /* ════════════════════════════════════════════════════════════════════════
     PIN CODE DELIVERY CHECKER
     ════════════════════════════════════════════════════════════════════════
     Customer enters their 6-digit Indian PIN → JS validates against the
     coverage table below → renders one of three result states:
       SUCCESS:        Delivers by <date> to <City> · COD <yes/no> · Free shipping
       UNREACHABLE:    Sorry, we don't deliver to this PIN yet
       INVALID:        Please enter a valid 6-digit PIN

     Coverage model — based on the official India Post PIN structure
     (first digit = region, first 2 digits = state-zone). Every PIN
     gets categorised into one of FOUR delivery zones:

       Zone A — METRO (top 8 cities) — 2-4 days, COD, free shipping ₹999+
       Zone B — TIER-1 (state capitals + major cities) — 3-6 days, COD, free ₹999+
       Zone C — TIER-2/3 (rest of mainland India) — 5-8 days, COD, free ₹999+
       Zone D — REMOTE (Northeast, J&K, islands) — 7-12 days, NO COD, ₹150 surcharge
       Zone X — NON-DELIVERABLE (specific blocked ranges)

     The data is hardcoded as a JS object — covers all 800 pin prefixes
     used in India. Maintained in main.js so a server / app dependency
     isn't required. When you integrate Shiprocket / Delhivery later,
     swap the local lookup for an API call (single function replacement).
  ──────────────────────────────────────────────────────────────────────── */

  /** Map first-3-digit PIN prefix → city name (for the success message).
      Covers metros + state capitals + major Tier-1 cities — falls back
      to the state name for less-known PINs. */
  const PIN_CITY = {
    // Delhi NCR
    '110': 'Delhi', '111': 'Delhi', '112': 'Delhi',
    '120': 'Faridabad', '121': 'Faridabad', '122': 'Gurugram', '123': 'Rewari',
    '124': 'Rohtak', '125': 'Hisar', '131': 'Sonipat', '132': 'Karnal',
    '201': 'Ghaziabad', '203': 'Bulandshahr',
    // Uttar Pradesh
    '208': 'Kanpur', '226': 'Lucknow', '221': 'Varanasi', '224': 'Faizabad',
    '243': 'Bareilly', '244': 'Moradabad', '247': 'Saharanpur', '250': 'Meerut',
    '251': 'Muzaffarnagar', '282': 'Agra', '284': 'Jhansi', '285': 'Lalitpur',
    // Punjab
    '140': 'Mohali', '141': 'Ludhiana', '143': 'Amritsar', '144': 'Jalandhar',
    '147': 'Patiala', '148': 'Sangrur', '151': 'Bathinda', '152': 'Faridkot',
    // Haryana
    '133': 'Ambala', '134': 'Panchkula', '135': 'Yamunanagar', '136': 'Kaithal',
    // Himachal Pradesh
    '171': 'Shimla', '173': 'Solan', '175': 'Mandi', '176': 'Kangra',
    // J&K
    '180': 'Jammu', '181': 'Jammu', '182': 'Udhampur', '184': 'Kathua',
    '185': 'Poonch', '190': 'Srinagar', '192': 'Anantnag', '193': 'Baramulla',
    // Rajasthan
    '301': 'Alwar', '302': 'Jaipur', '303': 'Jaipur', '304': 'Tonk',
    '311': 'Bhilwara', '313': 'Udaipur', '321': 'Bharatpur', '324': 'Kota',
    '331': 'Bikaner', '334': 'Sri Ganganagar', '342': 'Jodhpur',
    // Gujarat
    '360': 'Rajkot', '361': 'Jamnagar', '362': 'Junagadh', '364': 'Bhavnagar',
    '370': 'Bhuj', '380': 'Ahmedabad', '382': 'Gandhinagar', '388': 'Anand',
    '390': 'Vadodara', '391': 'Vadodara', '394': 'Surat', '395': 'Surat',
    // Maharashtra
    '400': 'Mumbai', '401': 'Thane', '402': 'Raigad', '403': 'Goa',
    '410': 'Pune', '411': 'Pune', '412': 'Pune', '413': 'Solapur',
    '415': 'Sangli', '416': 'Kolhapur', '422': 'Nashik', '425': 'Jalgaon',
    '431': 'Aurangabad', '440': 'Nagpur', '441': 'Nagpur', '444': 'Akola',
    // MP
    '450': 'Khandwa', '452': 'Indore', '453': 'Indore', '462': 'Bhopal',
    '470': 'Sagar', '474': 'Gwalior', '482': 'Jabalpur',
    // Chhattisgarh
    '490': 'Durg', '492': 'Raipur', '495': 'Bilaspur',
    // Andhra Pradesh / Telangana
    '500': 'Hyderabad', '501': 'Hyderabad', '502': 'Medak', '503': 'Nizamabad',
    '506': 'Warangal', '515': 'Anantapur', '517': 'Tirupati', '518': 'Kurnool',
    '520': 'Vijayawada', '522': 'Guntur', '530': 'Visakhapatnam', '533': 'Kakinada',
    // Karnataka
    '560': 'Bengaluru', '562': 'Bengaluru', '570': 'Mysuru', '571': 'Mandya',
    '575': 'Mangaluru', '577': 'Shivamogga', '580': 'Hubballi', '585': 'Kalaburagi',
    '590': 'Belagavi',
    // Tamil Nadu
    '600': 'Chennai', '602': 'Tiruvallur', '603': 'Kanchipuram', '605': 'Puducherry',
    '610': 'Tiruchirappalli', '612': 'Thanjavur', '620': 'Tiruchirappalli',
    '625': 'Madurai', '627': 'Tirunelveli', '630': 'Sivagangai', '632': 'Vellore',
    '636': 'Salem', '638': 'Erode', '641': 'Coimbatore', '643': 'Coonoor',
    // Kerala
    '670': 'Kannur', '673': 'Kozhikode', '676': 'Malappuram', '678': 'Palakkad',
    '680': 'Thrissur', '682': 'Kochi', '683': 'Aluva', '686': 'Kottayam',
    '689': 'Pathanamthitta', '691': 'Kollam', '695': 'Thiruvananthapuram',
    // West Bengal
    '700': 'Kolkata', '711': 'Howrah', '712': 'Hooghly', '713': 'Bardhaman',
    '721': 'Midnapore', '731': 'Birbhum', '733': 'Dakshin Dinajpur', '734': 'Darjeeling',
    '736': 'Jalpaiguri', '741': 'Nadia', '743': 'North 24 Parganas',
    // Odisha
    '751': 'Bhubaneswar', '752': 'Khordha', '753': 'Cuttack', '754': 'Jagatsinghpur',
    '760': 'Berhampur', '768': 'Sambalpur', '769': 'Rourkela',
    // Northeast (Zone D)
    '781': 'Guwahati', '784': 'Tezpur', '785': 'Jorhat', '786': 'Dibrugarh',
    '788': 'Silchar', '790': 'Itanagar', '791': 'Itanagar', '793': 'Shillong',
    '795': 'Imphal', '796': 'Aizawl', '797': 'Kohima', '798': 'Dimapur',
    '799': 'Agartala',
    // Bihar / Jharkhand
    '800': 'Patna', '801': 'Patna', '803': 'Nalanda', '811': 'Munger',
    '812': 'Bhagalpur', '821': 'Sasaram', '823': 'Gaya', '824': 'Gaya',
    '831': 'Jamshedpur', '834': 'Ranchi', '835': 'Ranchi', '843': 'Muzaffarpur',
    '845': 'East Champaran', '848': 'Samastipur',
    // Andaman & Nicobar
    '744': 'Port Blair'
  };

  /** Map first-2-digit PIN prefix → delivery zone code. */
  const PIN_ZONE = (() => {
    const z = {};
    // Zone A — METRO (top 8 cities)
    ['11', '40', '56', '50', '60', '70', '41', '38'].forEach((p) => z[p] = 'A');
    // Zone D — REMOTE (Northeast + J&K + Andamans)
    ['18', '19', '78', '79', '74'].forEach((p) => z[p] = 'D');
    // Zone B — TIER-1 (state capitals + major cities)
    ['12', '14', '15', '20', '22', '30', '36', '39', '44', '46',
     '49', '52', '53', '57', '62', '68', '75', '80', '83'].forEach((p) => z[p] = 'B');
    return z;
  })();

  /** Map first-2-digit PIN prefix → state name. Used to render the
      "City, State" line in the PIN-check result. India Post's PIN
      blocks are mostly 2-digit aligned per state; a few 3-digit
      overrides (Uttarakhand, Goa, Telangana metro, Puducherry) are
      handled in `PIN_STATE_OVERRIDES` below. */
  const PIN_STATE = {
    '11': 'Delhi',
    '12': 'Haryana',          '13': 'Haryana',
    '14': 'Punjab',           '15': 'Punjab',          '16': 'Punjab',
    '17': 'Himachal Pradesh',
    '18': 'Jammu & Kashmir',  '19': 'Jammu & Kashmir',
    '20': 'Uttar Pradesh',    '21': 'Uttar Pradesh',   '22': 'Uttar Pradesh',
    '23': 'Uttar Pradesh',    '24': 'Uttar Pradesh',   '25': 'Uttar Pradesh',
    '26': 'Uttar Pradesh',    '27': 'Uttar Pradesh',   '28': 'Uttar Pradesh',
    '30': 'Rajasthan',        '31': 'Rajasthan',       '32': 'Rajasthan',
    '33': 'Rajasthan',        '34': 'Rajasthan',
    '36': 'Gujarat',          '37': 'Gujarat',         '38': 'Gujarat',
    '39': 'Gujarat',
    '40': 'Maharashtra',      '41': 'Maharashtra',     '42': 'Maharashtra',
    '43': 'Maharashtra',      '44': 'Maharashtra',
    '45': 'Madhya Pradesh',   '46': 'Madhya Pradesh',  '47': 'Madhya Pradesh',
    '48': 'Madhya Pradesh',
    '49': 'Chhattisgarh',
    '50': 'Telangana',        '51': 'Andhra Pradesh',  '52': 'Andhra Pradesh',
    '53': 'Andhra Pradesh',
    '56': 'Karnataka',        '57': 'Karnataka',       '58': 'Karnataka',
    '59': 'Karnataka',
    '60': 'Tamil Nadu',       '61': 'Tamil Nadu',      '62': 'Tamil Nadu',
    '63': 'Tamil Nadu',       '64': 'Tamil Nadu',
    '67': 'Kerala',           '68': 'Kerala',          '69': 'Kerala',
    '70': 'West Bengal',      '71': 'West Bengal',     '72': 'West Bengal',
    '73': 'West Bengal',      '74': 'West Bengal',
    '75': 'Odisha',           '76': 'Odisha',          '77': 'Odisha',
    '78': 'Assam',
    '79': 'North-East',
    '80': 'Bihar',            '81': 'Bihar',           '82': 'Bihar',
    '83': 'Jharkhand',        '84': 'Bihar',           '85': 'Jharkhand'
  };

  /** 3-digit overrides where a sub-region within a 2-digit state
      block actually belongs to a different state / UT. */
  const PIN_STATE_OVERRIDES = {
    '244': 'Uttarakhand', '245': 'Uttarakhand',  // Moradabad area splits — UK side
    '246': 'Uttarakhand', '247': 'Uttarakhand',
    '248': 'Uttarakhand', '249': 'Uttarakhand',
    '263': 'Uttarakhand', '249': 'Uttarakhand',
    '403': 'Goa',                                // Goa is inside Maharashtra block
    '533': 'Andhra Pradesh',                     // Kakinada split
    '605': 'Puducherry',                         // Puducherry inside Tamil Nadu block
    '737': 'Sikkim',                             // Sikkim shares WB block
    '744': 'Andaman & Nicobar Islands',
    '790': 'Arunachal Pradesh', '791': 'Arunachal Pradesh',
    '792': 'Arunachal Pradesh',
    '793': 'Meghalaya', '794': 'Meghalaya',
    '795': 'Manipur',
    '796': 'Mizoram',
    '797': 'Nagaland', '798': 'Nagaland',
    '799': 'Tripura'
  };

  /** Zone definitions. Delivery window is UNIFORM 3–7 business days
      across every zone for now (per merchant spec — exact zone-based
      shipping rules will be plugged in later when finalised). The zone
      categorisation is preserved so future shipping-rate / COD /
      surcharge rules slot in cleanly without re-tagging PINs. */
  const ZONE_INFO = {
    A: { daysMin: 3, daysMax: 7, cod: true,  freeAbove: 999, surcharge: 0, label: 'Metro' },
    B: { daysMin: 3, daysMax: 7, cod: true,  freeAbove: 999, surcharge: 0, label: 'Tier 1' },
    C: { daysMin: 3, daysMax: 7, cod: true,  freeAbove: 999, surcharge: 0, label: 'Tier 2' },
    D: { daysMin: 3, daysMax: 7, cod: true,  freeAbove: 999, surcharge: 0, label: 'Remote' }
  };

  /** Specific PIN ranges that are NOT deliverable (e.g. military, restricted). */
  const NON_DELIVERABLE_RANGES = [
    // Lakshadweep (no surface delivery infra in our network)
    [682551, 682559]
  ];

  function isPinNonDeliverable(pinNum) {
    for (let i = 0; i < NON_DELIVERABLE_RANGES.length; i++) {
      const [lo, hi] = NON_DELIVERABLE_RANGES[i];
      if (pinNum >= lo && pinNum <= hi) return true;
    }
    return false;
  }

  /** Look up a PIN — returns { ok, zone, city, state, daysMin, daysMax,
      cod, freeAbove, surcharge } OR { ok:false, reason }. */
  function lookupPin(pin) {
    if (!/^\d{6}$/.test(pin)) return { ok: false, reason: 'invalid' };
    const pinNum = parseInt(pin, 10);
    if (isPinNonDeliverable(pinNum)) return { ok: false, reason: 'unreachable' };
    const prefix2 = pin.substring(0, 2);
    const prefix3 = pin.substring(0, 3);
    const zone = PIN_ZONE[prefix2] || 'C';
    const city = PIN_CITY[prefix3] || null;
    // State: 3-digit overrides take precedence (Goa/Uttarakhand/NE/etc.),
    // else fall through to the 2-digit state map.
    const state = PIN_STATE_OVERRIDES[prefix3] || PIN_STATE[prefix2] || null;
    if (!city && !state && parseInt(prefix2, 10) > 85) {
      return { ok: false, reason: 'invalid' };
    }
    return Object.assign({ ok: true, zone, city, state }, ZONE_INFO[zone]);
  }

  /** Format a Date as "Mon, 5 May" using en-IN locale. */
  function formatPinDate(d) {
    try {
      return new Intl.DateTimeFormat('en-IN', {
        weekday: 'short', day: 'numeric', month: 'short'
      }).format(d);
    } catch (e) {
      const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const w = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
      return w[d.getDay()] + ', ' + d.getDate() + ' ' + m[d.getMonth()];
    }
  }

  /** Add N business days (skipping Sundays) to a date. */
  function pinAddBusinessDays(start, n) {
    const d = new Date(start);
    let added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0) added++;
    }
    return d;
  }

  /** Bind the PIN-checker widget on the PDP. Idempotent. */
  function initPincheck() {
    const wrap = document.querySelector('[data-sk-pincheck]');
    if (!wrap || wrap.dataset.skPincheckInit === '1') return;
    wrap.dataset.skPincheckInit = '1';

    const input  = wrap.querySelector('[data-sk-pincheck-input]');
    const submit = wrap.querySelector('[data-sk-pincheck-submit]');  // optional
    const result = wrap.querySelector('[data-sk-pincheck-result]');
    if (!input || !result) return;

    const STORAGE_KEY = 'sanki:pincheck:last';

    function render(state) {
      result.hidden = false;
      result.classList.remove('is-success', 'is-error', 'is-remote');
      if (state.ok) {
        const cutoff = new Date();
        const startDay = cutoff.getHours() >= 16 ? 1 : 0; // 4 PM cutoff
        const today = new Date(cutoff);
        today.setDate(today.getDate() + startDay);
        const minDate = pinAddBusinessDays(today, state.daysMin);
        const maxDate = pinAddBusinessDays(today, state.daysMax);
        const dateRange = formatPinDate(minDate) + ' – ' + formatPinDate(maxDate);
        // Build "City, State" when both are known; falls back gracefully
        // to whichever is available.
        let location = '';
        if (state.city && state.state) location = state.city + ', ' + state.state;
        else if (state.city)            location = state.city;
        else if (state.state)           location = state.state;
        const cityLine = location ? ' to <strong>' + location + '</strong>' : '';
        const codLine  = state.cod
          ? '<span class="sk-pdp__pincheck-tag sk-pdp__pincheck-tag--good">✓ COD available</span>'
          : '<span class="sk-pdp__pincheck-tag sk-pdp__pincheck-tag--warn">Cards / UPI only — no COD</span>';
        const shipLine = state.surcharge > 0
          ? '<span class="sk-pdp__pincheck-tag sk-pdp__pincheck-tag--warn">+ ₹' + state.surcharge + ' shipping</span>'
          : '<span class="sk-pdp__pincheck-tag sk-pdp__pincheck-tag--ship">✓ Free shipping over ₹' + state.freeAbove + '</span>';
        // Always show success styling — zone-specific surcharge / COD-blocking
        // rules will be re-enabled when the merchant provides the final
        // shipping spec.
        result.classList.add('is-success');
        // The top of the delivery box already shows the date range, so
        // the headline here only confirms WHERE it goes — keeps the box
        // lean and doesn't repeat the same date twice.
        const headlineLocation = location
          ? '<strong>' + location + '</strong>'
          : 'your address';
        result.innerHTML =
          '<div class="sk-pdp__pincheck-headline">' +
            '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden><path d="M5 12l5 5 9-11" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
            'Delivery available to ' + headlineLocation +
          '</div>' +
          '<div class="sk-pdp__pincheck-tags">' + codLine + shipLine + '</div>';
      } else if (state.reason === 'invalid') {
        result.classList.add('is-error');
        result.innerHTML =
          '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden style="vertical-align:-3px;margin-right:6px;"><circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16v.5" stroke-linecap="round"/></svg>' +
          'Please enter a valid 6-digit PIN';
      } else {
        result.classList.add('is-error');
        result.innerHTML =
          '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden style="vertical-align:-3px;margin-right:6px;"><circle cx="12" cy="12" r="9"/><path d="M9 9l6 6M15 9l-6 6" stroke-linecap="round"/></svg>' +
          'Sorry, we don\'t deliver to this PIN yet';
      }
    }

    function check() {
      const pin = (input.value || '').trim();
      if (!pin) { result.hidden = true; return; }
      const state = lookupPin(pin);
      render(state);
      if (state.ok) {
        try { localStorage.setItem(STORAGE_KEY, pin); } catch (e) {}
      }
    }

    if (submit) submit.addEventListener('click', check);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); check(); }
    });
    // Strip non-digits as user types — keeps the input clean.
    input.addEventListener('input', () => {
      const cleaned = input.value.replace(/\D/g, '').slice(0, 6);
      if (cleaned !== input.value) input.value = cleaned;
      // Auto-submit when 6 digits entered.
      if (cleaned.length === 6) check();
    });

    // Pre-fill from localStorage on visit.
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && /^\d{6}$/.test(saved)) {
        input.value = saved;
        check();
      }
    } catch (e) {}
  }

  function initPdpBackButton() {
    const back = document.querySelector('[data-sk-back]');
    if (!back) return;
    back.addEventListener('click', (e) => {
      // Modifier-clicks → let the browser open in new tab as expected.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
      const ref = document.referrer;
      if (!ref) return; // no history entry → take the anchor href
      try {
        const refUrl = new URL(ref);
        if (refUrl.origin !== window.location.origin) return; // external referrer → href
      } catch (_) { return; }
      // Same-origin previous page → step back through history. bfcache
      // restores it instantly with state intact.
      e.preventDefault();
      window.history.back();
    });
  }

  function initProductPage() {
    initPdpBackButton();
    initPincheck();
    const root = document.querySelector('[data-sk-product]');
    if (!root) return;
    const jsonNode = document.querySelector('[data-sk-product-json]');
    const product = jsonNode ? H.parseJSON(jsonNode.textContent, null) : null;
    if (!product) return;

    const form = root.querySelector('[data-sk-product-form]');
    const variantInput = root.querySelector('[data-sk-variant-id]');
    const priceSlot = root.querySelector('[data-sk-pdp-price]');
    const atc = root.querySelector('[data-sk-add-to-cart]');
    const atcText = root.querySelector('[data-sk-atc-text]');
    const optionInputs = Array.from(root.querySelectorAll('[data-sk-option-input]'));

    // Variant resolver --------------------------------------------------------
    function currentOptions() {
      const optionsByName = {};
      optionInputs.forEach((r) => {
        if (r.checked) {
          const name = r.name.replace(/^options\[|\]$/g, '');
          optionsByName[name] = r.value;
        }
      });
      return optionsByName;
    }
    function findVariant() {
      const opts = currentOptions();
      return product.variants.find((v) =>
        v.options.every((val, i) => {
          const n = product.options[i];
          return opts[n] === val;
        })
      );
    }

    function syncChipStates() {
      // Update aria-checked on chip labels
      document.querySelectorAll('.sk-opt__chip').forEach((label) => {
        const input = label.querySelector('[data-sk-option-input]');
        if (input) label.setAttribute('aria-checked', input.checked ? 'true' : 'false');
      });
    }

    function applyVariant(variant) {
      if (!variant) {
        atc.disabled = true;
        if (atcText) atcText.textContent = 'Unavailable';
        return;
      }
      variantInput.value = variant.id;
      atc.disabled = !variant.available;
      if (atcText) atcText.textContent = variant.available ? 'Add to cart' : 'Sold out';
      if (priceSlot) priceSlot.innerHTML = renderPrice(variant);
      // Sync URL without reload
      if (window.history && window.history.replaceState) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      document.dispatchEvent(new CustomEvent('sanki:variant:change', { detail: { variant } }));
    }

    function renderPrice(v) {
      if (v.compare_at_price && v.compare_at_price > v.price) {
        return '<span class="sk-price">' +
          '<span class="sk-price__compare">' + H.money(v.compare_at_price) + '</span>' +
          '<span class="sk-price__sale">' + H.money(v.price) + '</span>' +
        '</span>';
      }
      return '<span class="sk-price">' + H.money(v.price) + '</span>';
    }

    optionInputs.forEach((r) => {
      r.addEventListener('change', () => { syncChipStates(); applyVariant(findVariant()); });
    });
    // Chip labels are clickable as native labels, but also handle keyboard directly
    root.querySelectorAll('.sk-opt__chip').forEach((label) => {
      label.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          const input = label.querySelector('[data-sk-option-input]');
          if (input) { input.checked = true; input.dispatchEvent(new Event('change', { bubbles: true })); }
        }
      });
    });

    // Gallery -----------------------------------------------------------------
    // Note on caching: slides/thumbs are looked up FRESH inside every
    // applyColorFilter call (and showSlide). The PDP can be replaced in-place
    // by the AJAX colour swap (see applyProductSwap), so any array we cached
    // at init time would point at detached DOM nodes after a swap.
    function getGalleryRoot() { return root.querySelector('[data-sk-gallery]'); }
    function getSlides() { return Array.from(root.querySelectorAll('[data-sk-slide]')); }
    function getThumbs() { return Array.from(root.querySelectorAll('[data-sk-thumb]')); }

    function showSlide(i) {
      getSlides().forEach((s) => s.classList.toggle('is-active', parseInt(s.dataset.index, 10) === i));
      getThumbs().forEach((t) => {
        const is = parseInt(t.dataset.index, 10) === i;
        t.classList.toggle('is-active', is);
        t.setAttribute('aria-selected', is ? 'true' : 'false');
      });
    }
    function bindThumbClicks() {
      getThumbs().forEach((t) => {
        if (t.dataset.skThumbBound === '1') return;
        t.dataset.skThumbBound = '1';
        t.addEventListener('click', () => showSlide(parseInt(t.dataset.index, 10)));
      });
    }
    bindThumbClicks();

    /**
     * Filter gallery to show only media matching the active colour.
     *
     * Strategy cascade:
     *   1. Per-slide data-colors metadata (alt text / filename / variant
     *      featured_media id, populated by Liquid).
     *   2. Chunk-by-position from the colour→position map embedded on the
     *      gallery wrapper.
     *   3. If neither yields a hit, navigate to the matching variant's
     *      featured_image (best-effort).
     *
     * `colorValue` falls back to the title-derived colour when the product
     * has no Color option (Sannki's catalogue has separate-product-per-colour,
     * e.g. nomad-…-pink), so cross-colour images uploaded to a single-colour
     * product still get filtered out.
     */
    function applyColorFilter(colorValue) {
      const galleryRoot = getGalleryRoot();
      const slides = getSlides();
      const thumbs = getThumbs();
      if (!galleryRoot || !slides.length) return;
      const colorOptionIndex = parseInt(galleryRoot.dataset.skColorOptionIndex || '0', 10);

      // Resolve the active colour. Variant option wins when present;
      // otherwise fall back to a title-derived colour.
      let needle = (colorValue || '').toLowerCase().trim();
      if (!needle) {
        const titleEl = root.querySelector('[data-sk-pdp-title]');
        const titleText = titleEl ? titleEl.textContent : (product && product.title) || '';
        const fromTitle = splitTitleColor(titleText);
        if (fromTitle.color) needle = fromTitle.color.toLowerCase();
      }
      if (!needle) {
        slides.forEach((s) => s.classList.remove('is-hidden'));
        thumbs.forEach((t) => t.classList.remove('is-hidden'));
        return;
      }

      const totalMedia = parseInt(galleryRoot.dataset.skMediaCount || slides.length, 10);

      // ---------------------------------------------------------------------
      // Build the canonical colour→position map directly from the embedded
      // product JSON. This bypasses Liquid's data attribute (which sometimes
      // emits a trailing comma → invalid JSON → JSON.parse throws). When the
      // merchant has set variant→featured_image in admin, this gives us
      // exact chunk boundaries; nothing else is needed.
      // ---------------------------------------------------------------------
      let positionMap = {};
      if (colorOptionIndex > 0 && product && product.variants) {
        product.variants.forEach((v) => {
          const c = v['option' + colorOptionIndex];
          if (!c) return;
          const key = String(c).toLowerCase();
          if (positionMap[key]) return; // first wins (smallest position seen)
          if (v.featured_image && v.featured_image.position != null) {
            positionMap[key] = v.featured_image.position;
          } else if (v.featured_media && v.featured_media.position != null) {
            positionMap[key] = v.featured_media.position;
          }
        });
      }

      // Fallback: try the Liquid-emitted map (handle a stray trailing comma).
      if (Object.keys(positionMap).length === 0) {
        try {
          const raw = galleryRoot.dataset.skColorPositions;
          if (raw) {
            const cleaned = raw.replace(/,(\s*[}\]])/g, '$1').trim();
            if (cleaned && cleaned !== '{}') positionMap = JSON.parse(cleaned);
          }
        } catch (_) { positionMap = {}; }
      }

      let firstVisibleIdx = -1;

      // ---------------------------------------------------------------------
      // STRATEGY A — chunk by featured_image positions. If we have at least
      // two colour→position pairs, every slide whose position falls outside
      // the active colour's chunk is hidden HARD — no "shared" exemption.
      // For multi-colour-variant products this is what the user expects: a
      // gallery scoped strictly to the picked colour.
      // ---------------------------------------------------------------------
      if (Object.keys(positionMap).length >= 2) {
        const entries = Object.entries(positionMap)
          .map(([k, v]) => [k.toLowerCase(), parseInt(v, 10)])
          .filter(([, v]) => Number.isFinite(v))
          .sort((a, b) => a[1] - b[1]);

        let activeStart = -1, activeEnd = totalMedia;
        for (let i = 0; i < entries.length; i++) {
          if (entries[i][0] === needle) {
            activeStart = entries[i][1];
            activeEnd = i + 1 < entries.length ? entries[i + 1][1] - 1 : totalMedia;
            break;
          }
        }

        if (activeStart > 0) {
          slides.forEach((s, idx) => {
            const pos = idx + 1;
            const visible = pos >= activeStart && pos <= activeEnd;
            s.classList.toggle('is-hidden', !visible);
            if (visible && firstVisibleIdx === -1) firstVisibleIdx = idx;
          });
          thumbs.forEach((t, idx) => {
            const pos = idx + 1;
            const visible = pos >= activeStart && pos <= activeEnd;
            t.classList.toggle('is-hidden', !visible);
          });
          if (firstVisibleIdx !== -1) showSlide(firstVisibleIdx);
          return;
        }
        // The map exists but the needle isn't in it — fall through to the
        // metadata-driven strategy below rather than showing everything.
      }

      // ---------------------------------------------------------------------
      // STRATEGY B — per-slide colour metadata (alt text / filename / variant
      // featured_media id). For multi-colour-variant products we run this
      // strictly: any slide whose data-colors mentions a DIFFERENT known
      // colour gets hidden, even when our needle isn't explicitly listed.
      // Slides with no colour data and no other colour signal stay visible
      // (genuine "shared" content like size charts).
      // ---------------------------------------------------------------------
      const KNOWN_COLORS = COLOR_WORDS.map((c) => c.toLowerCase());
      let slidesWithColorData = 0;
      slides.forEach((s) => { if ((s.dataset.colors || '').trim()) slidesWithColorData++; });

      if (slidesWithColorData > 0) {
        const processEl = (el) => {
          const colorsRaw = (el.dataset.colors || '').toLowerCase();
          if (!colorsRaw) {
            el.classList.remove('is-hidden');
            return true;
          }
          const colors = colorsRaw.split('|').map((s) => s.trim()).filter(Boolean);
          const matches = colors.includes(needle);
          el.classList.toggle('is-hidden', !matches);
          return matches;
        };
        let anyVisible = false;
        slides.forEach((s, idx) => {
          const visible = processEl(s);
          if (visible) { anyVisible = true; if (firstVisibleIdx === -1) firstVisibleIdx = idx; }
        });
        thumbs.forEach(processEl);
        if (anyVisible) {
          showSlide(firstVisibleIdx);
          return;
        }
        // The strict pass found NO matching slide — undo the partial hiding
        // and fall through to the equal-chunk fallback below. Returning
        // early here was the bug: it left every slide visible when the
        // metadata pass came up empty (e.g. filenames don't contain the
        // colour word).
        slides.forEach((s) => s.classList.remove('is-hidden'));
        thumbs.forEach((t) => t.classList.remove('is-hidden'));
        firstVisibleIdx = -1;
      }

      // ---------------------------------------------------------------------
      // STRATEGY E — equal-chunk fallback. The most aggressive deterministic
      // strategy: divide the media list evenly by the number of Color option
      // values and assume images were uploaded in the SAME order the option
      // values appear (option 1 = first chunk, option 2 = second chunk, …).
      //
      // Order matters: this runs BEFORE the sparse-positionMap fallback below
      // because it always produces a strict, predictable filter. The sparse
      // strategy only hides whichever positions are explicitly assigned to
      // other variants and would leave most thumbs visible — that's exactly
      // the "everything's still showing" bug.
      //
      // This is GUESSWORK. It will mis-categorise galleries where the
      // merchant uploaded images out of variant order. The proper fix on
      // the merchant side is to set variant→featured_image in admin for
      // each colour; once they do that, Strategy A above takes over and
      // is exact.
      // ---------------------------------------------------------------------
      if (colorOptionIndex > 0
          && product
          && product.options
          && product.options[colorOptionIndex - 1]
          && totalMedia > 1) {
        const colors = (product.options[colorOptionIndex - 1].values || [])
          .map((c) => String(c).toLowerCase());
        const colorIdx = colors.indexOf(needle);
        if (colorIdx >= 0 && colors.length >= 2 && totalMedia >= colors.length) {
          const chunkSize = Math.floor(totalMedia / colors.length);
          const remainder = totalMedia - chunkSize * colors.length;
          let cursor = 1;
          let activeStart = 0, activeEnd = 0;
          for (let i = 0; i < colors.length; i++) {
            const len = chunkSize + (i < remainder ? 1 : 0);
            const start = cursor;
            const end = start + len - 1;
            if (i === colorIdx) { activeStart = start; activeEnd = end; }
            cursor = end + 1;
          }
          if (activeStart > 0) {
            slides.forEach((s, idx) => {
              const pos = idx + 1;
              const visible = pos >= activeStart && pos <= activeEnd;
              s.classList.toggle('is-hidden', !visible);
            });
            thumbs.forEach((t, idx) => {
              const pos = idx + 1;
              const visible = pos >= activeStart && pos <= activeEnd;
              t.classList.toggle('is-hidden', !visible);
            });
            showSlide(activeStart - 1);
            return;
          }
        }
      }

      // ---------------------------------------------------------------------
      // STRATEGY C — sparse-positionMap fallback. Only runs if equal-chunk
      // couldn't apply (e.g. fewer media than colour values). Hides positions
      // explicitly claimed by other variants and leaves the rest visible.
      // ---------------------------------------------------------------------
      if (colorOptionIndex > 0 && product && product.variants && Object.keys(positionMap).length >= 1) {
        const otherPositions = new Set();
        Object.entries(positionMap).forEach(([color, pos]) => {
          if (color !== needle) otherPositions.add(parseInt(pos, 10));
        });
        const activePos = positionMap[needle] != null ? parseInt(positionMap[needle], 10) : -1;
        slides.forEach((s, idx) => {
          const pos = idx + 1;
          const visible = !otherPositions.has(pos);
          s.classList.toggle('is-hidden', !visible);
          if (visible && firstVisibleIdx === -1) firstVisibleIdx = idx;
        });
        thumbs.forEach((t, idx) => {
          const pos = idx + 1;
          const visible = !otherPositions.has(pos);
          t.classList.toggle('is-hidden', !visible);
        });
        if (activePos > 0) showSlide(activePos - 1);
        else if (firstVisibleIdx !== -1) showSlide(firstVisibleIdx);
        return;
      }

      // ---------------------------------------------------------------------
      // STRATEGY D — best-effort navigate to the variant's featured image.
      // No filtering possible (no signals at all); at least centre the user
      // on something that resembles the picked colour.
      // ---------------------------------------------------------------------
      if (colorOptionIndex > 0 && product && product.variants) {
        const variant = product.variants.find((v) =>
          v['option' + colorOptionIndex] && v['option' + colorOptionIndex].toLowerCase() === needle
        );
        if (variant && variant.featured_image && variant.featured_image.position != null) {
          showSlide(variant.featured_image.position - 1);
        }
      }
    }

    // When the user picks a variant, derive its colour value and filter gallery.
    document.addEventListener('sanki:variant:change', (e) => {
      const variant = e.detail && e.detail.variant;
      if (!variant) return;
      const galleryRoot = getGalleryRoot();
      const colorOptionIndex = galleryRoot
        ? parseInt(galleryRoot.dataset.skColorOptionIndex || '0', 10)
        : 0;
      const colorValue = colorOptionIndex
        ? variant['option' + colorOptionIndex]
        : '';
      applyColorFilter(colorValue);
    });

    /**
     * Bulletproof initial gallery filter.
     *
     * Reads the active colour from the FIRST source available:
     *   1. The checked colour chip in the DOM (truthful post-Liquid).
     *   2. The variant input ID resolved against product.variants.
     *   3. product.selected_or_first_available_variant from JSON.
     *   4. A title-derived colour (separate-product-per-colour shops).
     *
     * Then applies the filter on the next animation frame (gives any
     * synchronous DOM mutations from earlier init steps time to land)
     * AND again at +120ms (catches lazy-loaded media nodes / late
     * style application). Both runs no-op if the gallery is already
     * filtered — there's no double-flicker.
     */
    function resolveInitialColor() {
      const galleryRoot = getGalleryRoot();
      const colorOptionIndex = galleryRoot
        ? parseInt(galleryRoot.dataset.skColorOptionIndex || '0', 10)
        : 0;

      /* Source 1 — checked colour chip in the DOM. The Liquid resolver
         set `checked` on exactly the right chip; reading from the DOM
         is more reliable than resolving by variant ID. */
      if (colorOptionIndex > 0) {
        const fieldset = root.querySelector('[data-sk-option-index="' + (colorOptionIndex - 1) + '"]');
        if (fieldset) {
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          if (checked && checked.value) return checked.value;
        }
      }

      /* Source 2 — variant input ID → variant.optionN */
      if (colorOptionIndex && product && Array.isArray(product.variants)) {
        const activeId = variantInput && parseInt(variantInput.value, 10);
        if (activeId) {
          const v = product.variants.find((x) => x.id === activeId);
          if (v && v['option' + colorOptionIndex]) return v['option' + colorOptionIndex];
        }
      }

      /* Source 3 — Shopify default in JSON */
      if (colorOptionIndex && product && product.selected_or_first_available_variant) {
        const c = product.selected_or_first_available_variant['option' + colorOptionIndex];
        if (c) return c;
      }

      /* Source 4 — title-derived (handle ends with -black, -white, etc) */
      const titleEl = root.querySelector('[data-sk-pdp-title]');
      const titleText = titleEl ? titleEl.textContent : (product && product.title) || '';
      const fromTitle = splitTitleColor(titleText);
      return (fromTitle && fromTitle.color) || '';
    }

    function runInitialColorFilter() {
      const initialColor = resolveInitialColor();
      applyColorFilter(initialColor);
    }

    /* ──────────────────────────────────────────────────────────────────
     * "In-stock only" availability filter — applied on the PDP.
     *
     * If the visitor toggled the "In stock only" checkbox on a catalog
     * page (persisted via localStorage by initCollectionFilters), this
     * function:
     *   1. Computes which colours have at least one available variant.
     *   2. HIDES the chip + gallery slides + thumbs for any colour
     *      that has zero stock.
     *   3. If the currently-selected variant is for a now-hidden
     *      colour, auto-shifts to the first colour that's still
     *      visible and re-runs the gallery filter.
     *   4. Reflects state via `data-sk-instock-only` on `[data-sk-product]`
     *      so CSS can react too.
     *
     * Re-runs on `storage` events so toggling the filter in one tab
     * updates an open PDP in another tab live. Also re-runs on every
     * `sanki:variant:change` to make sure the "selected" chip is never
     * a hidden colour.
     * ────────────────────────────────────────────────────────────────── */
    /**
     * Is the "in-stock only" preference active?
     *
     * Default: TRUE. We hide sold-out colours from the PDP unless the
     * visitor has explicitly opted in to seeing them by un-checking the
     * "In stock only" filter on a catalog page (which writes
     * `sanki:filter:in-stock-only=0`). Treats null/missing as ON so
     * the feature is auto-active for first-time visitors.
     */
    function isInStockOnlyActive() {
      try {
        const v = localStorage.getItem('sanki:filter:in-stock-only');
        if (v === null) return true;       // default ON
        return v !== '0';                  // any value except '0' → ON
      } catch (_) { return true; }         // storage blocked → still default ON
    }

    /** Map of `lowercased colour value` → boolean (has stock). */
    function buildColorAvailabilityMap() {
      const map = Object.create(null);
      const galleryRoot = getGalleryRoot();
      const colorOptionIndex = galleryRoot
        ? parseInt(galleryRoot.dataset.skColorOptionIndex || '0', 10)
        : 0;
      if (!colorOptionIndex || !product || !Array.isArray(product.variants)) return map;
      product.variants.forEach((v) => {
        const c = v['option' + colorOptionIndex];
        if (!c) return;
        const k = String(c).toLowerCase();
        // OR-merge: any one available variant in this colour ⇒ colour is in stock.
        map[k] = (map[k] || false) || !!v.available;
      });
      return map;
    }

    function applyAvailabilityFilterToPDP() {
      const galleryRoot = getGalleryRoot();
      const colorOptionIndex = galleryRoot
        ? parseInt(galleryRoot.dataset.skColorOptionIndex || '0', 10)
        : 0;
      if (!colorOptionIndex) return; // no colour option → nothing to scope

      const active = isInStockOnlyActive();
      root.setAttribute('data-sk-instock-only', active ? '1' : '0');

      const availMap = buildColorAvailabilityMap();
      const colorFieldset = root.querySelector(
        '[data-sk-option-index="' + (colorOptionIndex - 1) + '"]'
      );
      if (!colorFieldset) return;

      /* ── EDGE CASE: ENTIRE PRODUCT SOLD OUT ─────────────────────
         If every variant of every colour is unavailable, we must NOT
         hide chips/slides — that would leave the customer staring at
         a blank product page. Instead: skip the filter for this
         product, restore any previously-hidden media, and let the
         "Sold out" CTA do the talking. */
      const anyInStock = Object.values(availMap).some(Boolean);
      if (!anyInStock) {
        /* Restore any previously hidden chips (in case filter state
           changed between renders). */
        Array.from(colorFieldset.querySelectorAll('.sk-opt__chip')).forEach((chip) => {
          chip.hidden = false;
          chip.style.removeProperty('display');
        });
        /* Restore any previously hidden slides + thumbs. */
        const restoreEls = getSlides().concat(getThumbs());
        restoreEls.forEach((el) => {
          if (el.dataset.skInstockHidden === '1') {
            el.hidden = false;
            el.style.removeProperty('display');
            delete el.dataset.skInstockHidden;
          }
        });
        return;
      }

      /* Hide / show chips. */
      const chips = Array.from(colorFieldset.querySelectorAll('.sk-opt__chip'));
      const hiddenColors = new Set();
      chips.forEach((chip) => {
        const inp = chip.querySelector('[data-sk-option-input]');
        if (!inp) return;
        const colorVal = inp.value;
        const has = !!availMap[String(colorVal).toLowerCase()];
        const shouldHide = active && !has;
        if (shouldHide) {
          chip.hidden = true;
          chip.style.setProperty('display', 'none', 'important');
          hiddenColors.add(String(colorVal).toLowerCase());
          // Defensive — if a sold-out chip is currently checked, uncheck it.
          if (inp.checked) inp.checked = false;
        } else {
          chip.hidden = false;
          chip.style.removeProperty('display');
        }
      });

      /* Hide / show gallery slides + thumbs whose data-colors metadata
         names ONLY hidden colours. Slides with no colour metadata or
         shared metadata stay visible (handled later by applyColorFilter). */
      function purgeMediaForHiddenColors(els) {
        els.forEach((el) => {
          const raw = (el.dataset.colors || '').toLowerCase();
          if (!raw) return; // no metadata — leave alone
          const tags = raw.split('|').map((t) => t.trim()).filter(Boolean);
          if (tags.length === 0) return;
          const allHidden = tags.every((t) => hiddenColors.has(t));
          if (active && allHidden) {
            el.hidden = true;
            el.style.setProperty('display', 'none', 'important');
            el.dataset.skInstockHidden = '1';
          } else if (el.dataset.skInstockHidden === '1') {
            // Restore previously hidden when filter turns off.
            el.hidden = false;
            el.style.removeProperty('display');
            delete el.dataset.skInstockHidden;
          }
        });
      }
      purgeMediaForHiddenColors(getSlides());
      purgeMediaForHiddenColors(getThumbs());

      /* If, after hiding, no chip is checked (because the resolved
         colour was sold out and we just hid it), pick the first
         visible chip and trigger a variant change. */
      const nowChecked = colorFieldset.querySelector('[data-sk-option-input]:checked');
      if (!nowChecked) {
        const firstVisible = chips.find((c) => !c.hidden);
        const firstInp = firstVisible && firstVisible.querySelector('[data-sk-option-input]');
        if (firstInp) {
          firstInp.checked = true;
          syncChipStates();
          applyVariant(findVariant());
        }
      } else {
        /* Re-run the gallery filter against the current colour, in
           case purgeMediaForHiddenColors changed visibility. */
        runInitialColorFilter();
      }
    }

    /* Wire up: run once on init, then on storage events + variant
       changes so the PDP stays consistent with the filter state. */
    applyAvailabilityFilterToPDP();
    window.addEventListener('storage', (e) => {
      if (e.key === 'sanki:filter:in-stock-only') applyAvailabilityFilterToPDP();
    });
    document.addEventListener('sanki:variant:change', () => {
      // After a variant change, ensure no hidden colour leaked through.
      if (isInStockOnlyActive()) applyAvailabilityFilterToPDP();
    });

    /* Run once synchronously, then on the next animation frame, then
       at +120ms — covers every paint timing edge case. */
    runInitialColorFilter();
    requestAnimationFrame(runInitialColorFilter);
    setTimeout(runInitialColorFilter, 120);

    // Click-to-zoom lightbox wiring ------------------------------------------
    initZoomLightbox(root);

    // "Also in" color product switcher ---------------------------------------
    initRelatedColors(root, product);

    // Re-bind everything that lives on per-product DOM after an AJAX colour
    // swap: zoom lightbox slides, thumb clicks, and the gallery colour
    // filter. Without this the new gallery would show every uploaded image
    // (including stray cross-colour shots merchants sometimes attach to a
    // single-colour product).
    document.addEventListener('sanki:product:swapped', (e) => {
      initZoomLightbox(root);
      bindThumbClicks();
      // Re-apply colour filter using the swapped-in product's colour. The
      // event detail carries the swatch's colour name; falling back to
      // title-derived colour when missing.
      const colorName = (e.detail && e.detail.color) || '';
      applyColorFilter(colorName);
    });

    // Qty controls
    root.querySelectorAll('[data-sk-qty-decr]').forEach((b) => b.addEventListener('click', () => changeQty(b, -1)));
    root.querySelectorAll('[data-sk-qty-incr]').forEach((b) => b.addEventListener('click', () => changeQty(b, +1)));

    // Submit ------------------------------------------------------------------
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const id = parseInt(variantInput.value, 10);
        const qty = parseInt(form.querySelector('[name="quantity"]').value, 10) || 1;
        // Collect ANY checked properties[*] inputs (universal size selector,
        // gift notes, monogram, etc.) and forward them as line-item properties.
        const properties = {};
        form.querySelectorAll('input[name^="properties["], textarea[name^="properties["], select[name^="properties["]').forEach((el) => {
          if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
          const m = el.name.match(/^properties\[(.+)\]$/);
          if (!m) return;
          if (el.value && el.value.length) properties[m[1]] = el.value;
        });
        atc.disabled = true;
        atcText && (atcText.textContent = 'Adding…');
        /* OPEN DRAWER IMMEDIATELY — perceived speed.
           Without this, the user waits for two sequential round-trips
           (POST /cart/add.js → GET /cart.js → render) before the drawer
           even appears. Opening the drawer now means the loading-state
           is visible while the network resolves. */
        document.addEventListener("sanki:cart:change", () => {
  if (window.SkCart && window.SkCart.openDrawer) window.SkCart.openDrawer();
}, { once: true });
        const payload = { id, quantity: qty };
        if (Object.keys(properties).length) payload.properties = properties;
        API.cart.add(payload)
          .then(({ cart }) => {
            cart._lastAdded = true;
            document.dispatchEvent(new CustomEvent('sanki:cart:change', { detail: { cart } }));
            atcText && (atcText.textContent = 'Added ✓');
            setTimeout(() => { atcText && (atcText.textContent = 'Add to cart'); atc.disabled = false; }, 1400);
          })
          .catch((err) => { atcText && (atcText.textContent = 'Add to cart'); atc.disabled = false; onCartError(err); });
      });
    }

    // Delivery estimator ------------------------------------------------------
    //
    // Production-grade business-day math, India-tuned:
    //   • Range: 3 to 7 business days from "today"
    //   • Sundays are skipped (most Indian couriers — Delhivery, Bluedart,
    //     DTDC, Shiprocket-aggregated — don't deliver on Sundays for COD/
    //     Standard tier; Sunday is treated as a non-delivery day)
    //   • Same-day cutoff: orders placed AFTER 4:00 PM IST count from
    //     "tomorrow", not today, because the warehouse won't dispatch
    //     same-day after the cutoff
    //   • Format: "Tue, 30 Apr – Sun, 5 May" (locale-aware via
    //     Intl.DateTimeFormat, falls back to en-IN month/day)
    //   • Robust to leap years, month boundaries, year boundaries — all
    //     date math goes through Date.setDate which auto-rolls correctly
    //
    // The output is intentionally a single string for the merchant's
    // existing pill UI; the snippet below also exposes start/end ISO
    // dates as data-attributes for downstream apps (Shiprocket widget,
    // Klaviyo email, etc.) that may want to read the same calculation.
    const est = document.querySelector('[data-sk-delivery-estimate]');
    if (est) {
      try {
        const MIN_DAYS = 3;
        const MAX_DAYS = 7;
        const CUTOFF_HOUR = 16; // 4 PM IST cutoff
        const SUNDAY = 0;

        // Add `n` calendar days to a date, then advance past Sundays.
        const addBusinessDays = (start, n) => {
          const d = new Date(start);
          let added = 0;
          while (added < n) {
            d.setDate(d.getDate() + 1);
            if (d.getDay() !== SUNDAY) added++;
          }
          return d;
        };

        // Effective "today" — past cutoff, treat as the next morning.
        const now = new Date();
        const todayBase = new Date(now);
        if (now.getHours() >= CUTOFF_HOUR) {
          todayBase.setDate(todayBase.getDate() + 1);
        }

        // Compute window end-points: skip Sundays.
        const start = addBusinessDays(todayBase, MIN_DAYS);
        const end   = addBusinessDays(todayBase, MAX_DAYS);

        // Locale-aware short format. Intl.DateTimeFormat is widely
        // supported and gives proper "Mon, 28 Apr" output without
        // hardcoding month names.
        let fmt;
        try {
          const f = new Intl.DateTimeFormat('en-IN', {
            weekday: 'short', month: 'short', day: 'numeric'
          });
          fmt = (d) => f.format(d);
        } catch (e) {
          // Fallback if Intl is unavailable (very old browsers).
          const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
          fmt = (d) => days[d.getDay()] + ', ' + d.getDate() + ' ' + months[d.getMonth()];
        }

        est.textContent = fmt(start) + ' – ' + fmt(end);

        // Expose ISO dates for downstream consumers (apps, analytics).
        const isoOf = (d) => d.toISOString().slice(0, 10);
        const wrap = est.closest('[data-sk-delivery-estimator]');
        if (wrap) {
          wrap.setAttribute('data-delivery-from', isoOf(start));
          wrap.setAttribute('data-delivery-to',   isoOf(end));
        }
      } catch (e) {
        // Hard fallback — just write a generic range so the slot never
        // shows the placeholder text in production.
        est.textContent = '3 – 7 business days';
      }
    }
  }

  /* -- "Also in" related colour products ----------------------------------- */
  /**
   * On stores where each product colour is a SEPARATE product (e.g. the live
   * sannki.store catalogue), we can't filter a gallery by colour — there
   * are no variants. Instead we surface the other colour products as a strip
   * of clickable swatches below the product title.
   *
   * Detection rule: titles end with a known colour keyword
   * ("… Hoodie WHITE" / "… Hoodie BLACK"). We strip the colour, search
   * Shopify predictive-search for the base phrase, then filter the results
   * down to products whose title matches `base + colour`.
   */
  const COLOR_WORDS = [
    'BLACK', 'WHITE', 'CREAM', 'PINK', 'PURPLE', 'GREEN', 'FUCHSIA',
    'BLUE', 'RED', 'YELLOW', 'GREY', 'GRAY', 'NAVY', 'BROWN', 'ORANGE',
    'BEIGE', 'OLIVE', 'MAROON', 'LILAC', 'IVORY', 'MAGENTA', 'TEAL', 'MUSTARD'
  ];
  const COLOR_HEX = {
    BLACK: '#111', WHITE: '#f5f5f5', CREAM: '#f0e9d2', PINK: '#ffc0cb',
    PURPLE: '#8134af', GREEN: '#50c878', FUCHSIA: '#e83d9a', BLUE: '#2e6bc6',
    RED: '#c0392b', YELLOW: '#f5c518', GREY: '#888', GRAY: '#888',
    NAVY: '#1b2a49', BROWN: '#6b4226', ORANGE: '#f58529', BEIGE: '#d8c9a3',
    OLIVE: '#808000', MAROON: '#6a0f1a', LILAC: '#c8a2c8', IVORY: '#FFFFF0',
    MAGENTA: '#d6006a', TEAL: '#008b8b', MUSTARD: '#c9a227'
  };

  function splitTitleColor(title) {
    const raw = String(title || '').trim();
    const up = raw.toUpperCase();
    for (const c of COLOR_WORDS) {
      if (up.endsWith(' ' + c) || up === c) {
        return { base: raw.slice(0, raw.length - c.length).trim(), color: c };
      }
    }
    return { base: raw, color: null };
  }

  /**
   * Build the "Also in <colour>" strip for stores where each colour is a
   * separate product (e.g. nomad-…-pink, nomad-…-cream, nomad-…-black).
   *
   * Pipeline:
   *   1. /search/suggest.json — find sibling handles that share the base
   *      title and end in a known colour word.
   *   2. /products/{handle}.js per match — get a guaranteed-clean
   *      featured_image / images URL. /search/suggest.json's image fields
   *      have been intermittently broken on this store, hence the second
   *      hop.
   *   3. Render swatches with the real garment image (not a coloured dot).
   *   4. Wire AJAX swap on click — no full page reload, no reorder.
   */
  function initRelatedColors(root, currentProduct) {
    const wrap = root.querySelector('[data-sk-color-link]');
    const row  = root.querySelector('[data-sk-color-link-row]');
    if (!wrap || !row || !currentProduct) return;

    const { base, color } = splitTitleColor(currentProduct.title);
    if (!base || !color) return;              // this product isn't a "-COLOUR" product

    fetch('/search/suggest.json?q=' + encodeURIComponent(base) +
          '&resources[type]=product&resources[limit]=20' +
          '&resources[options][unavailable_products]=last', {
      credentials: 'same-origin', headers: { Accept: 'application/json' }
    })
      .then((r) => r.ok ? r.json() : { resources: { results: { products: [] } } })
      .then(async (j) => {
        const all = (j && j.resources && j.resources.results && j.resources.results.products) || [];
        // Match: same base + a known colour suffix (including the current one).
        const baseUp = base.toUpperCase();
        const candidates = all
          .map((p) => {
            const sp = splitTitleColor(p.title);
            return sp.color && sp.base.toUpperCase() === baseUp
              ? { handle: p.handle, title: p.title, _color: sp.color }
              : null;
          })
          .filter(Boolean);

        // De-dupe by handle in encounter order — DO NOT reorder. The user
        // explicitly asked that the chosen colour stays where it is in the
        // strip; the active-state class is the only marker of "current".
        const seen = new Set();
        const uniq = [];
        for (const p of candidates) {
          if (!seen.has(p.handle)) { seen.add(p.handle); uniq.push(p); }
        }
        if (uniq.length <= 1) return;

        // Re-fetch each candidate via /products/{handle}.js for a reliable
        // featured-image URL. /search/suggest.json's image fields have been
        // intermittently empty / broken on this store, which is what was
        // producing the broken-image icons in the swatch row.
        const detailed = await Promise.all(uniq.map(async (p) => {
          try {
            const dp = await fetchProductByHandle(p.handle);
            if (!dp) return p;
            // Try the variant whose option matches the colour first, then
            // the product's own featured_image, then the first image.
            const colourImg = pickVariantImage(dp, p._color)
                            || (dp.featured_image && (dp.featured_image.src || dp.featured_image))
                            || (dp.images && dp.images[0] && (dp.images[0].src || dp.images[0]))
                            || '';
            return { ...p, image: String(colourImg || '') };
          } catch (_) { return p; }
        }));

        renderColorSwatches(row, detailed, color);
        wrap.hidden = false;
        wireColorSwap(root, row);
      })
      .catch(() => { /* silent — strip simply never appears */ });
  }

  /**
   * Pick the best image URL for a given colour from a /products/{handle}.js
   * response. Prefers a variant's featured_image whose option value equals
   * the colour name; otherwise falls back to the product's featured_image.
   */
  function pickVariantImage(productJs, colorName) {
    if (!productJs || !colorName) return '';
    const upper = String(colorName).toUpperCase();
    const variants = productJs.variants || [];
    for (const v of variants) {
      const opts = [v.option1, v.option2, v.option3];
      const matches = opts.some((o) => String(o || '').toUpperCase() === upper);
      if (matches && v.featured_image) {
        const src = typeof v.featured_image === 'string'
          ? v.featured_image
          : (v.featured_image.src || '');
        if (src) return src;
      }
    }
    return '';
  }

  function renderColorSwatches(container, products, currentColor) {
    container.innerHTML = products.map((p) => {
      const isCurrent = p._color === currentColor;
      const hex = COLOR_HEX[p._color] || '#ccc';
      const imgUrl = p.image ? sizedSrc(p.image, 200) : '';
      return ''
        + '<a class="sk-color-swatch' + (isCurrent ? ' is-current' : '') + '"'
        +   ' href="/products/' + p.handle + '"'
        +   ' data-sk-color-swap'
        +   ' data-handle="' + escapeHtml(p.handle) + '"'
        +   ' data-color="' + escapeHtml(p._color) + '"'
        +   ' aria-label="' + escapeHtml(p.title) + '"'
        +   ' title="' + escapeHtml(p.title) + '">'
        +   (imgUrl
            ? '<img src="' + imgUrl + '" alt="" loading="lazy" decoding="async" width="64" height="64">'
            : '<span class="sk-color-swatch__dot" style="background:' + hex + '"></span>')
        +   '<span class="sk-color-swatch__name">' + escapeHtml(p._color) + '</span>'
        + '</a>';
    }).join('');
  }

  /**
   * Wire AJAX colour switching. Clicking a swatch:
   *   - prevents the default navigation
   *   - fetches the target product via /products/{handle}.js
   *   - swaps the gallery, title, price, hidden variant id
   *   - history.pushState the new URL so the back button works
   *   - flips the active swatch — but does NOT reorder them
   *
   * Falls back to a normal navigation if anything goes wrong, so the user
   * never gets stuck.
   */
  function wireColorSwap(root, row) {
    if (row.dataset.skColorSwapWired === '1') return;
    row.dataset.skColorSwapWired = '1';

    row.addEventListener('click', async (e) => {
      const a = e.target.closest('[data-sk-color-swap]');
      if (!a) return;
      // Allow modifier-clicks (open in new tab) to behave normally.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
      e.preventDefault();

      const handle = a.dataset.handle;
      if (!handle) return;
      // Mark active immediately so the click feels responsive.
      row.querySelectorAll('.sk-color-swatch').forEach((s) => s.classList.remove('is-current'));
      a.classList.add('is-current');
      // Light visual hint while we fetch.
      root.classList.add('is-swapping');

      let doc;
      try {
        doc = await fetchProductHtml(handle);
      } catch (_) { /* fall through */ }
      root.classList.remove('is-swapping');
      if (!doc) {
        // Bail-out to a normal navigation rather than leaving the user
        // stuck on a half-swapped page.
        window.location.assign('/products/' + handle);
        return;
      }

      try {
        applyProductSwap(root, doc, handle, a.dataset.color || '');
      } catch (_) {
        window.location.assign('/products/' + handle);
      }
    });
  }

  /** Fetch the new PDP's HTML and parse it into a Document we can pluck from. */
  async function fetchProductHtml(handle) {
    const res = await fetch('/products/' + encodeURIComponent(handle), {
      credentials: 'same-origin', headers: { Accept: 'text/html' }
    });
    if (!res.ok) return null;
    const html = await res.text();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  /**
   * In-place patch of the PDP DOM with a different colour-product's data.
   * Pulls every server-rendered fragment from the freshly-fetched document
   * so that prices, badges, ratings, and rich content all match what a
   * full page load would produce — no JS price math involved.
   */
  function applyProductSwap(root, doc, handle, colorName) {
    const pickHtml = (sel) => {
      const src = doc.querySelector(sel);
      const dst = root.querySelector(sel);
      if (src && dst) dst.innerHTML = src.innerHTML;
    };
    const pickText = (sel) => {
      const src = doc.querySelector(sel);
      const dst = root.querySelector(sel);
      if (src && dst) dst.textContent = src.textContent;
    };
    const pickValue = (sel) => {
      const src = doc.querySelector(sel);
      const dst = root.querySelector(sel);
      if (src && dst) dst.value = src.getAttribute('value') || src.value || '';
    };

    // Title, price, swatch row inside the PDP column. Server-rendered, so
    // formatting is bulletproof.
    pickText('[data-sk-pdp-title]');
    pickHtml('[data-sk-pdp-price]');
    pickValue('[data-sk-variant-id]');

    // Gallery — replace the ENTIRE gallery wrapper so all the data-* maps
    // (variant-ids, color-positions, media-count) come from the new product.
    const newGallery = doc.querySelector('[data-sk-gallery]');
    const oldGallery = root.querySelector('[data-sk-gallery]');
    if (newGallery && oldGallery) {
      oldGallery.innerHTML = newGallery.innerHTML;
      // Copy the data attributes too so color-aware filtering keeps working.
      ['skColorOptionIndex', 'skColorPositions', 'skMediaCount'].forEach((k) => {
        if (newGallery.dataset[k] !== undefined) oldGallery.dataset[k] = newGallery.dataset[k];
      });
    }

    // Push the URL so the back button works and a refresh lands on the
    // correct PDP. Preserve any query / hash the user already had.
    try {
      const url = '/products/' + handle + window.location.search + window.location.hash;
      window.history.pushState({ skColorSwap: handle }, '', url);
    } catch (_) { /* not fatal */ }

    // Update the document title so the tab matches what's on screen.
    if (doc.title) document.title = doc.title;

    // Notify other modules (zoom lightbox, color-aware filtering) to
    // re-bind to the freshly-rendered slides.
    document.dispatchEvent(new CustomEvent('sanki:product:swapped', { detail: { handle, color: colorName } }));
  }

  /**
   * Replace the contents of [data-sk-gallery]'s main + thumb rails with
   * a new product's media. Lighter-weight than the Liquid render — we
   * don't carry over variant→media mapping because the swapped product is
   * a different colour by handle, so its single colour is the only one
   * that needs to display.
   */
  function rebuildGallery(gallery, dp) {
    const main   = gallery.querySelector('.sk-gallery__main');
    const thumbs = gallery.querySelector('.sk-gallery__thumbs');
    const media = (dp.media && dp.media.length)
      ? dp.media
      : ((dp.images || []).map((src, i) => ({
          preview_image: { src: typeof src === 'string' ? src : (src.src || '') },
          alt: dp.title || '',
          position: i + 1
        })));
    if (!media.length || !main) return;

    main.innerHTML = media.map((m, i) => {
      const src = (m.preview_image && (m.preview_image.src || m.preview_image)) || m.src || '';
      if (!src) return '';
      const url1200 = sizedSrc(src, 1200);
      const url600  = sizedSrc(src, 600);
      const url900  = sizedSrc(src, 900);
      const url1600 = sizedSrc(src, 1600);
      const url2400 = sizedSrc(src, 2400);
      const alt = escapeHtml(m.alt || dp.title || '');
      return ''
        + '<button type="button" class="sk-gallery__slide' + (i === 0 ? ' is-active' : '') + '"'
        +   ' data-sk-slide data-index="' + i + '" data-variant-ids="" data-colors=""'
        +   ' data-zoom-src="' + url2400 + '"'
        +   ' aria-label="View image ' + (i + 1) + '">'
        +   '<img src="' + url1200 + '"'
        +   ' srcset="' + url600 + ' 600w, ' + url900 + ' 900w, ' + url1200 + ' 1200w, ' + url1600 + ' 1600w"'
        +   ' sizes="(min-width: 1200px) 58vw, 100vw" alt="' + alt + '" loading="' + (i === 0 ? 'eager' : 'lazy') + '" decoding="async">'
        + '</button>';
    }).join('');

    if (thumbs && media.length > 1) {
      thumbs.innerHTML = media.map((m, i) => {
        const src = (m.preview_image && (m.preview_image.src || m.preview_image)) || m.src || '';
        if (!src) return '';
        return ''
          + '<button type="button" class="sk-gallery__thumb' + (i === 0 ? ' is-active' : '') + '"'
          +   ' data-sk-thumb data-index="' + i + '" role="tab"'
          +   ' aria-selected="' + (i === 0 ? 'true' : 'false') + '"'
          +   ' aria-label="View image ' + (i + 1) + '">'
          +   '<img src="' + sizedSrc(src, 160) + '" alt="" loading="lazy" decoding="async">'
          + '</button>';
      }).join('');
    } else if (thumbs) {
      thumbs.innerHTML = '';
    }

    // Re-bind slide / thumb click behaviour for the new DOM.
    bindGalleryClicks(gallery);
  }

  /** Wire a freshly-rendered gallery for slide/thumb switching + zoom. */
  function bindGalleryClicks(gallery) {
    const slides = gallery.querySelectorAll('[data-sk-slide]');
    const thumbs = gallery.querySelectorAll('[data-sk-thumb]');
    const setActive = (idx) => {
      slides.forEach((s, i) => s.classList.toggle('is-active', i === idx));
      thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === idx);
        t.setAttribute('aria-selected', i === idx ? 'true' : 'false');
      });
    };
    thumbs.forEach((t) => t.addEventListener('click', () => setActive(parseInt(t.dataset.index, 10) || 0)));
    // Zoom modal will pick the freshly-rendered slides up via its
    // re-bind hook listening for sanki:product:swapped.
  }

  /* -- Product zoom lightbox ----------------------------------------------- */
  /**
   * Click any gallery slide to open a full-screen modal showing the full-res
   * image. Click again to toggle 1x/2x zoom. Drag to pan when zoomed.
   * ESC or backdrop click closes.
   */
  function initZoomLightbox(root) {
    const modal = document.querySelector('[data-sk-zoom]');
    if (!modal) return;
    if (modal.dataset.skZoomInit === '1') {
      // Re-bind slide clicks on re-render but keep the single modal instance.
      bindSlideClicks(root, modal);
      return;
    }
    modal.dataset.skZoomInit = '1';

    const img   = modal.querySelector('[data-sk-zoom-img]');
    const stage = modal.querySelector('[data-sk-zoom-stage]');
    const closeBtn = modal.querySelector('[data-sk-zoom-close]');
    let zoomed = false;
    let panX = 0, panY = 0;
    let dragStartX = 0, dragStartY = 0;
    let dragging = false;

    const setTransform = () => {
      img.style.transform = zoomed
        ? 'translate(' + panX + 'px, ' + panY + 'px) scale(2.2)'
        : 'translate(0, 0) scale(1)';
    };

    const open = (src, alt) => {
      img.src = src;
      img.alt = alt || '';
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      // force reflow for transition
      modal.getBoundingClientRect();
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      zoomed = false; panX = 0; panY = 0;
      setTransform();
    };

    const close = () => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      setTimeout(() => { modal.hidden = true; img.src = ''; }, 260);
      zoomed = false; panX = 0; panY = 0; setTransform();
    };

    closeBtn.addEventListener('click', close);
    modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });

    // Click image to toggle zoom.
    img.addEventListener('click', (e) => {
      e.stopPropagation();
      zoomed = !zoomed;
      panX = 0; panY = 0;
      stage.classList.toggle('is-zoomed', zoomed);
      setTransform();
    });

    // Drag to pan when zoomed.
    img.addEventListener('mousedown', (e) => {
      if (!zoomed) return;
      dragging = true;
      dragStartX = e.clientX - panX;
      dragStartY = e.clientY - panY;
      stage.classList.add('is-dragging');
      e.preventDefault();
    });
    window.addEventListener('mousemove', (e) => {
      if (!dragging) return;
      panX = e.clientX - dragStartX;
      panY = e.clientY - dragStartY;
      setTransform();
    });
    window.addEventListener('mouseup', () => {
      dragging = false;
      stage.classList.remove('is-dragging');
    });

    // Touch support for mobile pinch-to-zoom uses the browser's native
    // pinch on the image (scale handled via transform above is click-toggle).
    let touchStartX = 0, touchStartY = 0;
    img.addEventListener('touchstart', (e) => {
      if (!zoomed || e.touches.length !== 1) return;
      touchStartX = e.touches[0].clientX - panX;
      touchStartY = e.touches[0].clientY - panY;
    }, { passive: true });
    img.addEventListener('touchmove', (e) => {
      if (!zoomed || e.touches.length !== 1) return;
      panX = e.touches[0].clientX - touchStartX;
      panY = e.touches[0].clientY - touchStartY;
      setTransform();
    }, { passive: true });

    // Expose an opener so the slides bind cleanly.
    Sanki.zoomOpen = open;

    bindSlideClicks(root, modal);
  }

  function bindSlideClicks(root, modal) {
    if (!root) return;
    const slides = root.querySelectorAll('[data-sk-slide]');
    slides.forEach((slide) => {
      if (slide.dataset.skZoomBound === '1') return;
      slide.dataset.skZoomBound = '1';
      slide.addEventListener('click', (e) => {
        e.preventDefault();
        const src = slide.dataset.zoomSrc || (slide.querySelector('img') && slide.querySelector('img').src);
        const img = slide.querySelector('img');
        const alt = img ? img.alt : '';
        if (src && Sanki.zoomOpen) Sanki.zoomOpen(src, alt);
      });
    });
  }

  /* -- Boot -----------------------------------------------------------------*/

  /* -- Inner Circle (newsletter section) ----------------------------------- */
  /* ====================================================================== *
   * SIZE GUIDE & FIT PREDICTOR
   *
   * Drawer with two tabs (size chart + fit predictor) for sized products,
   * single "ONE" panel for universal-size products. Predictor remembers
   * the recommendation in localStorage so the user only does it once.
   *
   * Idempotent (re-init safe), ESC-closes, scrim-closes, body-scroll-lock.
   * ====================================================================== */
  /* Type-specific size catalogues — qualitative only, no measurements.
     Indexed identically (XS=0..XXL=5) so the recommendation algorithm
     stays simple regardless of category. */
  const SK_SG_SIZES_TOP = [
    { letter: 'XS',   name: 'Extra Small', tag: 'Tightest cut',                  desc: 'Below the Sanki standard. Pick only if you wear XS in every brand and want minimal drape.' },
    { letter: 'S',    name: 'Small',       tag: 'Closer cut · narrow shoulders', desc: 'Body-skimming through the shoulders, fitted across the chest. Layered, pulled-in look.' },
    { letter: 'M',    name: 'Medium',      tag: 'The Sanki standard',            desc: 'Our reference fit. Comfortable across the shoulders, easy through the chest, never restrictive.' },
    { letter: 'L',    name: 'Large',       tag: 'Easy oversized · dropped',      desc: 'Roomy across the shoulders, generous through the chest, dropped at the hem — but tailored enough to keep its shape.' },
    { letter: 'XL',   name: 'Extra Large', tag: 'Statement oversized · full drape', desc: 'Drops past the hip, flows past the wrist, sits intentionally loose across the chest.' },
    { letter: 'XXL',  name: 'Double XL',   tag: 'Maximum drape · widest shoulder cut', desc: 'The boldest cut in the catalogue. Built to drape with intention — the print becomes the canvas.' },
    { letter: 'XXXL', name: 'Triple XL',   tag: 'Ultra drape · widest streetwear cut', desc: 'Our widest cut. Full coverage through shoulders, chest and torso, hem drops well past the hip. Built for the boldest silhouette and fuller builds.' }
  ];
  /* Bottom catalogue — waist sizes in inches. Index 0..5 maps to
     waist 26 / 28 / 30 / 32 / 34 / 36 / 38. We expose 7 waist values
     in the predictor (26-38) and the picker chooses the closest match. */
  const SK_SG_SIZES_BOTTOM = [
    { letter: '26', name: 'Waist 26"',  tag: 'Slim build · tapered',           desc: 'For a 26-inch natural waist. Cut close at the waistband, tapered through the hip and thigh.' },
    { letter: '28', name: 'Waist 28"',  tag: 'Slim build · tapered through hip and leg',           desc: 'For a 28-inch natural waist. Cut close at the waistband, tapered through the hip and thigh.' },
    { letter: '30', name: 'Waist 30"',  tag: 'Slim-to-regular · tapered hip', desc: 'For a 30-inch natural waist. Comfortable at the waistband, slightly tapered through the hip, regular through the thigh.' },
    { letter: '32', name: 'Waist 32"',  tag: 'Sanki standard · regular hip and leg',  desc: 'For a 32-inch natural waist. Our reference cut — comfortable at the waistband, true-to-size through the hip, regular drape through the leg.' },
    { letter: '34', name: 'Waist 34"',  tag: 'Easy waist · relaxed hip',      desc: 'For a 34-inch natural waist. Easy at the waistband, generous through the hip and thigh — sits relaxed without bunching.' },
    { letter: '36', name: 'Waist 36"',  tag: 'Loose waist · generous through leg',    desc: 'For a 36-inch natural waist. Loose at the waist, generous through hip and thigh. Deliberately roomy through the leg.' },
    { letter: '38', name: 'Waist 38"',  tag: 'Maximum waist · full drape',    desc: 'For a 38-inch natural waist. The widest cut — full drape from waist to ankle, intentionally generous throughout.' }
  ];
  /* Top catalogue stays letter-indexed XS..XXXL = 0..6. XXXL added for
     fuller builds so the universal cut still has a meaningful "how it
     sits on you" outcome on the largest end of the size scale. */
  const SK_SG_INDEX = { XS: 0, S: 1, M: 2, L: 3, XL: 4, XXL: 5, XXXL: 6 };
  /* Bottom catalogue is waist-indexed: 26..38 in 2-inch increments → 0..6.
     Helper: parseInt(input) and clamp to nearest available index. */
  function skSgBottomIndex(rawWaist) {
    const w = parseInt(rawWaist, 10);
    if (!isFinite(w)) return 3;            // default to 32"
    // Available slots: 26,28,30,32,34,36,38
    const slots = [26, 28, 30, 32, 34, 36, 38];
    let bestIdx = 3, bestDiff = Infinity;
    for (let i = 0; i < slots.length; i++) {
      const d = Math.abs(slots[i] - w);
      if (d < bestDiff) { bestDiff = d; bestIdx = i; }
    }
    return bestIdx;
  }
  const SK_SG_LS_KEY = 'sanki:fit-predictor:v2';   /* bumped: schema now records `type` */

  /* Public entry point — scans the page for EVERY [data-sk-sg] root and
     initialises each one independently. Pages can ship multiple roots:
       • The product-page size-guide drawer (one).
       • The AI Fit experience (two synthetic hosts — one for tops, one
         for bottoms — each wrapping a standalone tryon-widget).
     Each root carries its own data-sk-sg-type / data-sk-sg-flavor and
     is idempotent via `dataset.skSgInit`, so calling this function
     repeatedly (e.g. after a section reload) is safe.                */
  function initSizeGuide() {
    const sgRoots = document.querySelectorAll('[data-sk-sg]');
    if (!sgRoots.length) return;
    sgRoots.forEach(initOneSizeGuide);
  }

  function initOneSizeGuide(sg) {
    if (sg.dataset.skSgInit === '1') return;
    sg.dataset.skSgInit = '1';

    const flavor = sg.dataset.skSgFlavor || 'sized';
    const sgType = sg.dataset.skSgType || 'top';     /* 'top' or 'bottom' (accessory never reaches here) */
    const sizeCatalog = sgType === 'bottom' ? SK_SG_SIZES_BOTTOM : SK_SG_SIZES_TOP;
    const trigger = document.querySelector('[data-sk-sg-open]');
    const closeButtons = sg.querySelectorAll('[data-sk-sg-close]');

    /* ---------- Open / close ---------- */
    let lastFocus = null;

    /* Snapshot the markup's defaults so we can restore them on every open.
       Reads which radio is `checked` per-group at first paint; that's the
       authoritative "fresh state" — no leakage between sessions, no
       localStorage prefill, no stuck step pointer. */
    const defaultSelections = (() => {
      const map = {};
      sg.querySelectorAll('.sk-sg__chip input[type="radio"][checked], .sk-sg__chip input[type="radio"]:checked').forEach((i) => {
        if (!map[i.name]) map[i.name] = i.value;
      });
      // Hard fallbacks for groups that never had a default.
      if (!map['sk-sg-usual'])  map['sk-sg-usual']  = sgType === 'bottom' ? '32' : 'M';
      if (!map['sk-sg-prefer']) map['sk-sg-prefer'] = 'true';
      if (!map['sk-sg-build'])  map['sk-sg-build']  = 'average';
      return map;
    })();

    /* Reset the predictor + tabs to the markup's default state. Called
       on every `open()` so reopening NEVER shows stale answers, the
       previous result card, or a step pointer parked at "result". */
    function resetState() {
      // 1. Reset every radio to its markup default + reflect on the chip.
      Object.keys(defaultSelections).forEach((name) => {
        const wantValue = defaultSelections[name];
        sg.querySelectorAll('input[name="' + name + '"]').forEach((input) => {
          const isDefault = input.value === wantValue;
          input.checked = isDefault;
          const chip = input.closest('.sk-sg__chip');
          if (chip) chip.classList.toggle('is-checked', isDefault);
        });
      });
      // 2. Clear the result-card text so a flash of the previous answer
      //    never leaks during the next paint.
      const resultLetter = sg.querySelector('[data-sk-sg-result-letter]');
      const resultName   = sg.querySelector('[data-sk-sg-result-name]');
      const resultTag    = sg.querySelector('[data-sk-sg-result-tag]');
      const resultDesc   = sg.querySelector('[data-sk-sg-result-desc]');
      const resultNote   = sg.querySelector('[data-sk-sg-result-note]');
      if (resultLetter) resultLetter.textContent = '';
      if (resultName)   resultName.textContent   = '';
      if (resultTag)    resultTag.textContent    = '';
      if (resultDesc)   resultDesc.textContent   = '';
      if (resultNote)   resultNote.textContent   = '';
      // 3. Step pointer back to question 1.
      if (typeof showStep === 'function') showStep(0);
      // 4. Force the tab back to the size chart (not the predictor) so
      //    the user always lands on the reference content first.
      const tabs = sg.querySelectorAll('[data-sk-sg-tab]');
      const panelGuide     = sg.querySelector('[data-sk-sg-panel="guide"]');
      const panelPredictor = sg.querySelector('[data-sk-sg-panel="predictor"]');
      if (tabs.length) {
        tabs.forEach((t) => {
          const isGuide = t.dataset.skSgTab === 'guide';
          t.classList.toggle('is-active', isGuide);
          t.setAttribute('aria-selected', isGuide ? 'true' : 'false');
        });
        if (panelGuide)     panelGuide.hidden     = false;
        if (panelPredictor) panelPredictor.hidden = true;
      }
    }

    function open() {
      lastFocus = document.activeElement;
      // Reset BEFORE the transition fires so the user never sees a flash
      // of old state — the chips snap to defaults and the step pointer
      // is back at question 1 before the drawer animates in.
      resetState();
      sg.hidden = false;
      sg.getBoundingClientRect();   // force reflow so transition fires
      sg.classList.add('is-open');
      document.body.classList.add('sk-sg-open');
      const focusable = sg.querySelector('.sk-sg__close, .sk-sg__tab, .sk-sg__chip');
      if (focusable) focusable.focus({ preventScroll: true });
    }
    function close() {
      sg.classList.remove('is-open');
      document.body.classList.remove('sk-sg-open');
      /* When the whole size-guide drawer closes, also collapse every
         Try-On widget inside it. The trigger's own setExpanded(false)
         resets state so the next time the user opens the drawer they
         see the pristine collapsed CTA, not whatever step they were
         mid-way through. */
      sg.querySelectorAll('[data-sk-tryon-trigger][aria-expanded="true"]').forEach((tr) => {
        tr.click();
      });
      setTimeout(() => {
        if (!sg.classList.contains('is-open')) sg.hidden = true;
        if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
      }, 320);
    }
    if (trigger) trigger.addEventListener('click', open);
    closeButtons.forEach((b) => b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && sg.classList.contains('is-open')) close();
    });

    /* ---------- Tabs (Size chart / Fit predictor) ----------
       Wired for every flavor. Both SIZED and UNIVERSAL panels now ship
       Size-chart and Fit-predictor tabs; the gate that previously
       limited tabs to sized-only was removed when Fit Predictor was
       restored to the universal panel.                                  */
    {
      const tabs = sg.querySelectorAll('[data-sk-sg-tab]');
      const panelGuide     = sg.querySelector('[data-sk-sg-panel="guide"]');
      const panelPredictor = sg.querySelector('[data-sk-sg-panel="predictor"]');
      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const target = tab.dataset.skSgTab;
          tabs.forEach((t) => {
            const active = t === tab;
            t.classList.toggle('is-active', active);
            t.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          if (panelGuide)     panelGuide.hidden     = (target !== 'guide');
          if (panelPredictor) panelPredictor.hidden = (target !== 'predictor');
        });
      });
    }

    /* ---------- Predictor flow ---------- */
    const steps = Array.from(sg.querySelectorAll('[data-sk-sg-step]'));
    const back  = sg.querySelector('[data-sk-sg-back]');
    const next  = sg.querySelector('[data-sk-sg-next]');
    const progressFill = sg.querySelector('[data-sk-sg-progress]');
    let stepIdx = 0;        // 0 = step 1, 1 = step 2, 2 = step 3, 3 = result

    function showStep(i) {
      stepIdx = Math.max(0, Math.min(steps.length - 1, i));
      steps.forEach((s, idx) => {
        const isActive = idx === stepIdx;
        s.hidden = !isActive;
        s.classList.toggle('is-active', isActive);
      });
      if (back) back.hidden = stepIdx === 0 || stepIdx === steps.length - 1;
      if (next) next.hidden = stepIdx === steps.length - 1;
      // 3 question steps total → progress = (stepIdx + 1) / 4 capped at 100% at result
      if (progressFill) {
        const pct = stepIdx >= 3 ? 100 : ((stepIdx + 1) / 4) * 100;
        progressFill.style.width = pct + '%';
      }
    }

    if (back) back.addEventListener('click', () => showStep(stepIdx - 1));
    if (next) next.addEventListener('click', () => {
      if (stepIdx === steps.length - 2) {
        // Last question → compute result.
        computeResult();
        showStep(steps.length - 1);
      } else {
        showStep(stepIdx + 1);
      }
    });

    /* Chip selection — visual feedback (also works without :has) */
    sg.querySelectorAll('.sk-sg__chip').forEach((chip) => {
      chip.addEventListener('click', () => {
        const input = chip.querySelector('input[type="radio"]');
        if (!input) return;
        input.checked = true;
        const name = input.name;
        sg.querySelectorAll('input[name="' + name + '"]').forEach((sib) => {
          const sibChip = sib.closest('.sk-sg__chip');
          if (sibChip) sibChip.classList.toggle('is-checked', sib === input);
        });
      });
    });
    // Initial state — mark already-checked inputs.
    sg.querySelectorAll('.sk-sg__chip input:checked').forEach((input) => {
      const c = input.closest('.sk-sg__chip');
      if (c) c.classList.add('is-checked');
    });

    /* Recommendation algorithm — different size axes per type:
         • TOP: usual is XS..XXL → SK_SG_INDEX letter map
         • BOTTOM: usual is waist inches (26..40) → skSgBottomIndex closest-match
       Both use the same prefer/build deltas to nudge up or down from the
       base index, then clamp into the catalogue's valid range. */
    function computeResult() {
      const usual   = (sg.querySelector('input[name="sk-sg-usual"]:checked')   || {}).value || (sgType === 'bottom' ? '32' : 'M');
      const prefer  = (sg.querySelector('input[name="sk-sg-prefer"]:checked')  || {}).value || 'true';
      const build   = (sg.querySelector('input[name="sk-sg-build"]:checked')   || {}).value || 'average';

      let idx;
      if (sgType === 'bottom') {
        idx = skSgBottomIndex(usual);
      } else {
        idx = SK_SG_INDEX[usual] != null ? SK_SG_INDEX[usual] : 2;
      }

      // Sanki cut runs ~1 size larger than mass-market — bake that into "true".
      // Only adjust DOWN from there for snug, UP for relaxed/oversized.
      if (prefer === 'snug')        idx -= 1;
      else if (prefer === 'true')   idx += 0;
      else if (prefer === 'relaxed')   idx += 1;
      else if (prefer === 'oversized') idx += 2;

      if (build === 'slim')         idx -= 0; // base
      else if (build === 'athletic') idx += 0;
      else if (build === 'average')  idx += 0;
      else if (build === 'broad')    idx += 1;
      else if (build === 'plus')     idx += 1;

      // Clamp into the catalogue's valid range. Top: skip XS (idx 0),
      // allow up to XXXL (idx 6). Bottom: full range 0..6 (waist 26..38").
      if (sgType === 'bottom') {
        if (idx < 0) idx = 0;
        if (idx > 6) idx = 6;
      } else {
        if (idx < 1) idx = 1;
        if (idx > 6) idx = 6;
      }
      const pick = sizeCatalog[idx];

      /* For UNIVERSAL products there's no "size to buy" — the predictor's
         job is to translate the user's usual-size answer into how this
         single one-size garment will sit on them. We re-shape the
         picked catalog entry into a verdict-card view: the "letter"
         becomes the user's usual size (so the result still feels
         personal), the "name" / "tag" / "desc" become the verdict
         labels matching the static fit-size-guide table. */
      let display = pick;
      if (flavor === 'universal') {
        const universalTopVerdicts = {
          S:    { name: 'Maximum drape',     tag: 'Most relaxed cut', desc: 'Sits as the most relaxed oversized cut — full streetwear baggy.' },
          M:    { name: 'Loose fit',         tag: 'Roomy oversized',  desc: 'Falls roomy with extra movement through chest and shoulders.' },
          L:    { name: 'Perfect oversized', tag: 'Sanki signature',  desc: 'The exact silhouette this piece is designed for — Sanki signature drape.' },
          XL:   { name: 'Slightly fitted',   tag: 'Subtle drape',     desc: 'Closer drape with deliberate full fall — still oversized, just more tailored.' },
          XXL:  { name: 'Body-fitted',       tag: 'Cleaner line',     desc: 'Cleaner shoulder line, fitted through chest. Wears as a regular cut.' },
          XXXL: { name: 'Pulled-in fit',     tag: 'Closest to body',  desc: 'Runs closest to the body across chest and shoulders — wears as a regular tee rather than oversized. Sized for the boldest builds.' }
        };
        const universalBottomVerdicts = {
          '26': { name: 'Maximum drape',     tag: 'Loosest cut',      desc: 'The waistband cinches all the way in — bottom hangs as the loosest cut on you.' },
          '28': { name: 'Loose drape',       tag: 'Easy at waist',    desc: 'Drawstring takes up the slack at the waist; leg sits loose.' },
          '30': { name: 'Easy relaxed',      tag: 'Generous through', desc: 'Easy at waist, generous through hip and leg.' },
          '32': { name: 'Perfect adjustable',tag: 'Sanki signature',  desc: 'The regular fit this piece is designed for — true at waist, intentional drape through leg.' },
          '34': { name: 'Slightly fitted',   tag: 'Cinches at waist', desc: 'Cinches at waist; leg drape sits closer to body — still relaxed, just more tailored.' },
          '36': { name: 'Body-fitted',       tag: 'Cleaner line',     desc: 'Closest fit through hip and thigh; cleaner line from waist to ankle.' },
          '38': { name: 'Body-fitted',       tag: 'Cleaner line',     desc: 'Closest fit through hip and thigh; cleaner line from waist to ankle.' }
        };
        const verdictMap = sgType === 'bottom' ? universalBottomVerdicts : universalTopVerdicts;
        const verdict = verdictMap[pick.letter];
        if (verdict) {
          display = {
            letter: pick.letter,
            name:   verdict.name,
            tag:    verdict.tag,
            desc:   verdict.desc
          };
        }
      }

      const letterEl = sg.querySelector('[data-sk-sg-result-letter]');
      const nameEl   = sg.querySelector('[data-sk-sg-result-name]');
      const tagEl    = sg.querySelector('[data-sk-sg-result-tag]');
      const descEl   = sg.querySelector('[data-sk-sg-result-desc]');
      const noteEl   = sg.querySelector('[data-sk-sg-result-note]');
      if (letterEl) letterEl.textContent = display.letter;
      if (nameEl)   nameEl.textContent   = display.name;
      if (tagEl)    tagEl.textContent    = display.tag;
      if (descEl)   descEl.textContent   = display.desc;

      const isBottom = sgType === 'bottom';
      const fitWord = isBottom
        ? (prefer === 'snug' ? 'snug-waist' : prefer === 'true' ? 'true-to-size' : prefer === 'relaxed' ? 'relaxed-waist' : 'loose-waist')
        : (prefer === 'snug' ? 'snug' : prefer === 'true' ? 'true-to-size' : prefer === 'relaxed' ? 'relaxed' : 'oversized');
      let note;
      if (isBottom) {
        note = 'Based on your ' + usual + '" waist, ' + fitWord + ' preference, and ' + build + ' leg drape.';
        if (prefer === 'snug')          note += ' Picked the next waist down for a closer fit.';
        else if (prefer === 'oversized') note += ' Bumped two waist sizes up for a deliberately loose, baggy fit.';
        else if (prefer === 'relaxed')   note += ' Bumped one waist size up for an easy fit with drawstring room.';
      } else {
        note = 'Based on your usual ' + usual + ' size, ' + fitWord + ' preference, and ' + build + ' build.';
        if (prefer === 'snug')          note += ' Picked one size below the Sanki standard for that pulled-in look.';
        else if (prefer === 'oversized') note += ' Bumped two sizes up so the drape reads as a deliberate statement.';
        else if (prefer === 'relaxed')   note += ' Bumped one size up for the easy oversized vibe.';
      }
      if (noteEl) noteEl.textContent = note;

      // Persist — type-tagged so the saved recommendation matches the
      // category. Tops and bottoms persist separately under the same key
      // (object keyed by type) so each PDP can pull its own.
      try {
        const stored = JSON.parse(localStorage.getItem(SK_SG_LS_KEY) || '{}');
        stored[sgType] = {
          letter: pick.letter, usual: usual, prefer: prefer, build: build, at: Date.now()
        };
        localStorage.setItem(SK_SG_LS_KEY, JSON.stringify(stored));
      } catch (_) {}
      // Mirror the legacy v1 key so the account dashboard's Sanki Size
      // tile (which still reads v1) keeps working.
      try {
        localStorage.setItem('sanki:fit-predictor:v1', JSON.stringify({
          letter: pick.letter, usual: usual, prefer: prefer, build: build, type: sgType, at: Date.now()
        }));
      } catch (_) {}
    }

    /* Re-do button */
    const redo = sg.querySelector('[data-sk-sg-redo]');
    if (redo) redo.addEventListener('click', () => showStep(0));

    /* Initial paint — show step 1 of the predictor at the markup's default
       state. We deliberately DO NOT prefill from localStorage so the
       drawer is clean every time the user opens it. The fit predictor
       result IS still saved to localStorage on completion (powering the
       "Sanki Size" tile in the account dashboard), but the drawer itself
       always asks fresh. */
    showStep(0);

    /* ─────────────────────────────────────────────────────────────────
       Smart Try-On — universal-size panel only.
       Initialises lazily; if no [data-sk-tryon] is present in the DOM
       (sized variants), this is a no-op.
       ───────────────────────────────────────────────────────────────── */
    initSizeGuideTryon(sg);
  }

  /* ====================================================================== *
   * SMART TRY-ON (text + measurements only, no canvas, no AI image)
   *
   * Restored after the previous "AI photorealistic try-on" was removed
   * per merchant request. This version keeps the upload-photo step (for
   * the "smart fit" feel) + the body-measurement form + the prediction
   * algorithm + the verdict result card. It does NOT do any image
   * compositing, canvas drawing, pose detection, or visual try-on
   * rendering — those are explicitly out of scope.
   *
   * Pipeline when the user clicks "Predict my fit":
   *   1. Read measurements (height, weight, chest|waist, fit pref)
   *   2. Run predictFit (chest- or waist-driven heuristic, ±1-2 size
   *      adjustment based on fit preference, 70-97% confidence band)
   *   3. Render result card: verdict letter, fit name, description,
   *      animated confidence bar, 3 explanation bullets
   *
   * If the user uploaded a photo, it's read into memory only — never
   * displayed in the result, never composited onto product images,
   * never sent anywhere.
   * ====================================================================== */
  function initSizeGuideTryon(sg) {
    const tryonRoots = sg.querySelectorAll('[data-sk-tryon]');
    if (!tryonRoots.length) return;

    const sgType   = sg.dataset.skSgType   || 'top';
    const sgFlavor = sg.dataset.skSgFlavor || 'sized';

    tryonRoots.forEach((root) => {
      if (root.dataset.skTryonInit === '1') return;
      root.dataset.skTryonInit = '1';

      const trigger   = root.querySelector('[data-sk-tryon-trigger]');
      const bodyEl    = root.querySelector('[data-sk-tryon-body]');
      const stepEls   = root.querySelectorAll('[data-sk-tryon-step]');
      const fileInput = root.querySelector('[data-sk-tryon-input]');
      const skipBtn   = root.querySelector('[data-sk-tryon-skip]');
      const backBtns  = root.querySelectorAll('[data-sk-tryon-back]');
      const goBtn     = root.querySelector('[data-sk-tryon-go]');
      const redoBtn   = root.querySelector('[data-sk-tryon-redo]');
      const procText  = root.querySelector('[data-sk-tryon-process-text]');
      const fitField  = root.querySelector('[data-sk-tryon-result-fit]');
      const nameField = root.querySelector('[data-sk-tryon-result-name]');
      const descField = root.querySelector('[data-sk-tryon-result-desc]');
      const confFill  = root.querySelector('[data-sk-tryon-confidence-fill]');
      const confPct   = root.querySelector('[data-sk-tryon-confidence-pct]');
      const bulletsEl = root.querySelector('[data-sk-tryon-result-bullets]');

      let userPhotoDataUrl = null;
      let processTimeouts  = [];

      /* Capture pristine defaults at init time so resetWidget() can
         restore the form to first-run state every time the user
         collapses the widget. We snapshot the *default* attribute
         value (defaultValue / defaultSelected) — not the live value
         — so the snapshot is unaffected by anything the user has
         already typed. */
      const measureFields = root.querySelectorAll('[data-sk-tryon-field]');
      const fieldDefaults = [];
      measureFields.forEach((el) => {
        if (el.tagName === 'SELECT') {
          let def = '';
          for (let i = 0; i < el.options.length; i++) {
            if (el.options[i].defaultSelected) { def = el.options[i].value; break; }
          }
          fieldDefaults.push({ el, type: 'select', value: def });
        } else {
          fieldDefaults.push({ el, type: 'input', value: el.defaultValue });
        }
      });

      function showStep(name) {
        stepEls.forEach((el) => {
          const active = el.dataset.skTryonStep === name;
          el.hidden = !active;
          el.classList.toggle('is-active', active);
        });
      }

      /* Wipe everything back to first-run state. Called whenever the
         widget collapses so the next expansion starts fresh — no
         stale measurements, no half-finished animation, no leftover
         verdict, no cached photo. */
      function resetWidget() {
        /* Cancel any in-flight processing animation timeouts so we
           don't blast through to the result step after a collapse. */
        processTimeouts.forEach((t) => clearTimeout(t));
        processTimeouts = [];

        /* Drop the in-memory photo reference and clear the file
           input so the same file can be re-selected (browsers fire
           change only when the chosen file actually differs). */
        userPhotoDataUrl = null;
        if (fileInput) {
          try { fileInput.value = ''; } catch (_) { /* IE quirk, safe to ignore */ }
        }

        /* Restore every measurement / fit-pref field to its default. */
        fieldDefaults.forEach(({ el, type, value }) => {
          if (type === 'select') {
            el.value = value;
          } else {
            el.value = value;
          }
        });

        /* Reset verdict scaffolding so the next render starts clean. */
        if (confFill) confFill.style.width = '0%';
        if (confPct)  confPct.textContent = '0%';
        if (bulletsEl) bulletsEl.innerHTML = '';
        if (procText) procText.textContent = 'Reading your measurements…';

        /* Always rewind to step 1 (upload). */
        showStep('upload');
      }

      /* The widget renders collapsed by default. The trigger is the
         primary CTA — clicking it expands the body and reveals the
         upload step. Collapsing always resets state so the next
         open starts the user from the very beginning. */
      function setExpanded(expanded) {
        if (!trigger || !bodyEl) return;
        trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        bodyEl.hidden = !expanded;
        root.toggleAttribute('data-sk-tryon-collapsed', !expanded);
        root.classList.toggle('is-expanded', expanded);
        if (!expanded) resetWidget();
      }
      if (trigger) {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
          setExpanded(!isExpanded);
          /* When opening for the first time, scroll the trigger into
             view so the body sits in the viewport. */
          if (!isExpanded) {
            requestAnimationFrame(() => {
              trigger.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            });
          }
        });
      }

      /* Photo upload — read into memory only. We never render the
         photo anywhere; it's just the trigger that makes the
         experience feel personalised before showing the form. */
      if (fileInput) {
        fileInput.addEventListener('change', (e) => {
          const file = e.target.files && e.target.files[0];
          if (!file) return;
          if (!/^image\//i.test(file.type)) {
            alert('Please choose a JPEG, PNG, or WebP photo.');
            return;
          }
          if (file.size > 12 * 1024 * 1024) {
            alert('Photo is larger than 12 MB. Please choose a smaller file.');
            return;
          }
          const reader = new FileReader();
          reader.onload = (ev) => {
            userPhotoDataUrl = ev.target.result;
            showStep('measure');
          };
          reader.onerror = () => alert('Could not read that photo. Please try another.');
          reader.readAsDataURL(file);
        });
      }

      if (skipBtn) {
        skipBtn.addEventListener('click', () => {
          userPhotoDataUrl = null;
          showStep('measure');
        });
      }
      backBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          showStep('upload');
        });
      });
      if (redoBtn) {
        redoBtn.addEventListener('click', () => {
          userPhotoDataUrl = null;
          if (fileInput) fileInput.value = '';
          showStep('upload');
        });
      }

      if (goBtn) {
        goBtn.addEventListener('click', () => {
          const m = collectMeasurements();
          const prediction = predictFit(m, sgType, sgFlavor);
          runProcessing().then(() => {
            renderResult(prediction);
            showStep('result');
          });
        });
      }

      function collectMeasurements() {
        const num = (sel, fallback) => {
          const el = root.querySelector('[data-sk-tryon-field="' + sel + '"]');
          if (!el) return fallback;
          const v = parseFloat(el.value);
          return Number.isFinite(v) ? v : fallback;
        };
        const str = (sel, fallback) => {
          const el = root.querySelector('[data-sk-tryon-field="' + sel + '"]');
          return el ? el.value : fallback;
        };
        return {
          height: num('height', 170),
          weight: num('weight', 70),
          chest:  num('chest',  40),
          waist:  num('waist',  32),
          fit:    str('fit',    'true')
        };
      }

      function predictFit(m, type, flavor) {
        const sizeOrder = type === 'bottom'
          ? ['28', '30', '32', '34', '36', '38']
          : ['S', 'M', 'L', 'XL', 'XXL'];

        let baseIdx;
        if (type === 'bottom') {
          if      (m.waist < 29) baseIdx = 0;
          else if (m.waist < 31) baseIdx = 1;
          else if (m.waist < 33) baseIdx = 2;
          else if (m.waist < 35) baseIdx = 3;
          else if (m.waist < 37) baseIdx = 4;
          else                   baseIdx = 5;
        } else {
          if      (m.chest < 38) baseIdx = 0;
          else if (m.chest < 41) baseIdx = 1;
          else if (m.chest < 44) baseIdx = 2;
          else if (m.chest < 47) baseIdx = 3;
          else                   baseIdx = 4;
        }

        const fitDelta = { snug: -1, true: 0, relaxed: 1, oversized: 2 };
        const adj = (fitDelta[m.fit] || 0);
        let idx = Math.max(0, Math.min(sizeOrder.length - 1, baseIdx + adj));
        const size = sizeOrder[idx];

        const verdictsTop = {
          S:   { name: 'Maximum drape',     desc: 'Sits as the most relaxed oversized cut — full streetwear baggy.' },
          M:   { name: 'Loose fit',         desc: 'Falls roomy with extra movement through chest and shoulders.' },
          L:   { name: 'Perfect oversized', desc: 'The exact silhouette this piece is designed for — Sanki signature drape.' },
          XL:  { name: 'Slightly fitted',   desc: 'Closer drape with deliberate full fall — still oversized, just more tailored.' },
          XXL: { name: 'Body-fitted',       desc: 'Cleaner shoulder line, fitted through chest. Wears as a regular cut.' }
        };
        const verdictsBottom = {
          '28': { name: 'Slim build',          desc: 'Cut close at waistband, tapered through hip and thigh.' },
          '30': { name: 'Slim-to-regular',     desc: 'Comfortable at waistband, slightly tapered through hip, regular thigh.' },
          '32': { name: 'The Sanki standard',  desc: 'Reference cut — comfortable at waistband, true through hip, regular leg.' },
          '34': { name: 'Easy relaxed',        desc: 'Easy at waistband, generous through hip and thigh, sits relaxed.' },
          '36': { name: 'Loose drape',         desc: 'Loose at waist, generous through hip and thigh, deliberately roomy.' },
          '38': { name: 'Maximum drape',       desc: 'Widest cut — full drape from waist to ankle, intentionally generous.' }
        };
        const verdict = (type === 'bottom' ? verdictsBottom : verdictsTop)[size];

        let conf = 95;
        const bmi = m.weight / Math.pow(m.height / 100, 2);
        if (!Number.isFinite(bmi) || bmi < 16 || bmi > 35) conf -= 12;
        else if (bmi < 18 || bmi > 30) conf -= 5;
        if (m.height < 150 || m.height > 195) conf -= 6;
        if (type === 'bottom' && (m.waist < 26 || m.waist > 44)) conf -= 6;
        if (type !== 'bottom' && (m.chest < 34 || m.chest > 52)) conf -= 6;
        if (m.fit === 'true' && adj === 0) conf += 2;
        conf = Math.max(70, Math.min(97, Math.round(conf)));

        const primary = type === 'bottom'
          ? 'waist ' + m.waist + '"'
          : 'chest ' + m.chest + '"';
        const fitLabel = ({
          snug: 'snug', 'true': 'true to size',
          relaxed: 'relaxed', oversized: 'full oversized'
        })[m.fit] || m.fit;

        return {
          size: size,
          name: verdict.name,
          desc: verdict.desc,
          confidence: conf,
          bullets: [
            'Based on ' + primary + ', height ' + m.height + ' cm, weight ' + m.weight + ' kg',
            'Fit preference: ' + fitLabel,
            'We recommend size ' + size + ' for the best drape.'
          ]
        };
      }

      function runProcessing() {
        showStep('process');
        const labels = [
          'Reading your measurements…',
          'Mapping body proportions…',
          'Computing fit confidence…',
          'Picking your perfect size…'
        ];
        return new Promise((resolve) => {
          processTimeouts.forEach(clearTimeout);
          processTimeouts = [];
          let i = 0;
          const tick = () => {
            if (i < labels.length) {
              if (procText) procText.textContent = labels[i];
              i++;
              processTimeouts.push(setTimeout(tick, 600));
            } else {
              processTimeouts.push(setTimeout(resolve, 300));
            }
          };
          tick();
        });
      }

      function renderResult(p) {
        if (fitField)  fitField.textContent  = p.size;
        if (nameField) nameField.textContent = p.name;
        if (descField) descField.textContent = p.desc;
        if (confPct)   confPct.textContent   = p.confidence + '%';
        if (confFill) {
          confFill.style.width = '0%';
          requestAnimationFrame(() => {
            confFill.style.width = p.confidence + '%';
          });
        }
        if (bulletsEl) {
          bulletsEl.innerHTML = '';
          p.bullets.forEach((b) => {
            const li = document.createElement('li');
            li.textContent = b;
            bulletsEl.appendChild(li);
          });
        }
      }

      showStep('upload');
    });
  }

  /* ====================================================================== *
   * MOBILE — sticky bottom Add-to-Cart bar.
   *
   * Builds a phone-only fixed bar at the bottom of PDP showing the
   * thumbnail, title, price, and a one-tap "Add to cart" button. The
   * bar shows when the inline ATC (`[data-sk-atc]`) scrolls out of view
   * and hides when it's back. Form submissions are forwarded to the
   * inline ATC's form so we don't need duplicate variant logic.
   * ====================================================================== */
  /* ────────────────────────────────────────────────────────────────────── *
   * Trust-badge sanitiser — kills the red "Secure Checkout With" heading
   * and the grayscale-filtered payment icons that third-party trust-badge
   * apps inject. Targets by TEXT CONTENT (not selector), so it works
   * against any app — Iconify, Avada, Hextom, POWR, TrustBadge Master.
   *
   * Uses MutationObserver so it catches content that loads AFTER initial
   * page paint (most trust-badge apps inject async via app embed).
   * ──────────────────────────────────────────────────────────────────────── */
  function initTrustBadgeSanitiser() {
    const RX = /(secure\s+checkout\s+with|trusted\s+payment\s+methods|pay\s+secure(?:ly)?\s+with)/i;
    const HEADING_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'P', 'SPAN', 'STRONG', 'DIV']);
    const SAFE_TARGETS = new Set(['SK-SECURE-STRIP']); // never touch our own strip

    function sanitiseHeading(el) {
      if (!el || !el.style) return;
      // Don't override our own custom strip's heading.
      if (el.closest && el.closest('.sk-secure-strip')) return;
      // Force matte charcoal + clear any inline color/filter the app set.
      el.style.setProperty('color', '#2E2E2E', 'important');
      el.style.setProperty('font-family', 'inherit', 'important');
    }

    function sanitiseIconsNear(headingEl) {
      if (!headingEl) return;
      // Walk up to a reasonable container (max 4 levels) and clear filters
      // on every SVG/IMG inside it. Trust-badge apps usually have the
      // heading + icon row in the same wrapper.
      let scope = headingEl;
      for (let i = 0; i < 4 && scope.parentElement; i++) {
        scope = scope.parentElement;
        if (scope.classList && scope.classList.contains('sk-secure-strip')) return;
      }
      scope.querySelectorAll('svg, img').forEach((node) => {
        if (node.closest && node.closest('.sk-secure-strip')) return;
        node.style.setProperty('filter', 'none', 'important');
        node.style.setProperty('-webkit-filter', 'none', 'important');
        node.style.setProperty('opacity', '1', 'important');
      });
    }

    /**
     * On the PDP, we don't render our own secure-checkout-strip — so any
     * third-party trust widget here is unwanted. Walk up from the
     * heading to the strip container and nuke it entirely.
     */
    function killStripOnPDP(headingEl) {
      if (!headingEl) return;
      const inPDP = headingEl.closest('[data-sk-product]');
      if (!inPDP) return;
      // Don't kill our own strip if it ever lands here.
      if (headingEl.closest('.sk-secure-strip')) return;
      // Walk up max 6 levels to find a sensible container (one that
      // also contains the icon row).
      let scope = headingEl.parentElement;
      let target = headingEl;
      for (let i = 0; i < 6 && scope; i++) {
        const hasIcons = scope.querySelector('svg, img');
        if (hasIcons && scope !== inPDP) target = scope;
        if (scope === inPDP) break;
        scope = scope.parentElement;
      }
      if (!target || target === inPDP) return;
      target.style.setProperty('display', 'none', 'important');
      target.style.setProperty('visibility', 'hidden', 'important');
      target.style.setProperty('height', '0', 'important');
      target.style.setProperty('margin', '0', 'important');
      target.style.setProperty('padding', '0', 'important');
      target.style.setProperty('overflow', 'hidden', 'important');
      target.setAttribute('aria-hidden', 'true');
    }

    function scan(root) {
      if (!root) return;
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (n) => RX.test(n.nodeValue || '') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT
      });
      let textNode;
      while ((textNode = walker.nextNode())) {
        const parent = textNode.parentElement;
        if (!parent) continue;
        if (HEADING_TAGS.has(parent.tagName)) {
          /* On the PDP — kill the entire strip (we never want third-
             party trust widgets here). Elsewhere — sanitise styling. */
          if (parent.closest('[data-sk-product]')) {
            killStripOnPDP(parent);
          } else {
            sanitiseHeading(parent);
            sanitiseIconsNear(parent);
          }
        }
      }
    }

    // Initial pass.
    scan(document.body);

    // Observe future injections. Many trust-badge apps inject after
    // window load; we catch them here.
    if ('MutationObserver' in window) {
      const mo = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'childList') {
            m.addedNodes.forEach((n) => {
              if (n.nodeType === 1) scan(n);  // element added
            });
          }
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }

    // Final safety net — run again 1.5s and 4s after load to catch the
    // slowest-loading app embeds.
    setTimeout(() => scan(document.body), 1500);
    setTimeout(() => scan(document.body), 4000);
  }

  function initStickyATC() {
    if (window.matchMedia && window.matchMedia('(min-width: 961px)').matches) return;
    const inlineForm = document.querySelector('[data-sk-product-form]');
    const inlineBtn = document.querySelector('[data-sk-atc]') ||
                      (inlineForm ? inlineForm.querySelector('button[type="submit"], [data-sk-atc-btn]') : null);
    if (!inlineForm || !inlineBtn) return;
    if (document.querySelector('[data-sk-sticky-atc]')) return;

    const titleEl = document.querySelector('[data-sk-pdp-title]');
    const priceEl = document.querySelector('[data-sk-pdp-price]');
    const mediaEl = document.querySelector('[data-sk-pdp-media] img, .sk-pdp__media img, .sk-pdp__gallery img');

    const bar = document.createElement('div');
    bar.className = 'sk-pdp__sticky-atc';
    bar.setAttribute('data-sk-sticky-atc', '');
    bar.style.display = 'flex';
    bar.innerHTML =
      (mediaEl ? '<img class="sk-pdp__sticky-atc__media" src="' + mediaEl.src + '" alt="" loading="lazy">' : '') +
      '<div class="sk-pdp__sticky-atc__info">' +
        '<span class="sk-pdp__sticky-atc__title">' + ((titleEl && titleEl.textContent) || 'This product').trim() + '</span>' +
        '<span class="sk-pdp__sticky-atc__price" data-sk-sticky-price>' + ((priceEl && priceEl.textContent) || '').trim() + '</span>' +
      '</div>' +
      '<button type="button" class="sk-pdp__sticky-atc__btn" data-sk-sticky-atc-btn>Add</button>';
    document.body.appendChild(bar);

    // Forward sticky-button click to the inline ATC button so all variant
    // / quantity / availability logic stays in one place.
    bar.querySelector('[data-sk-sticky-atc-btn]').addEventListener('click', () => {
      try { inlineBtn.click(); } catch (_) { inlineForm.submit(); }
    });

    // Keep price text in sync if the inline price changes (variant swap).
    if (priceEl) {
      const stickyPrice = bar.querySelector('[data-sk-sticky-price]');
      const priceMo = new MutationObserver(() => {
        stickyPrice.textContent = priceEl.textContent.trim();
      });
      priceMo.observe(priceEl, { childList: true, subtree: true, characterData: true });
    }

    // Show/hide based on inline ATC visibility. Show only when inline
    // is offscreen (user has scrolled past it).
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) bar.classList.remove('is-visible');
          else bar.classList.add('is-visible');
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0 });
      io.observe(inlineBtn);
    }
  }

  /* ====================================================================== *
   * MOBILE — footer accordion on phone.
   *
   * On screens ≤ 640px, footer columns become collapsible. Tap a heading
   * to toggle the column. Contact column stays expanded (essentials).
   * ====================================================================== */
  function initFooterAccordion() {
    const isPhone = window.matchMedia && window.matchMedia('(max-width: 640px)').matches;
    if (!isPhone) return;
    document.querySelectorAll('.sk-footer-col').forEach((col) => {
      if (col.dataset.skAccInit === '1') return;
      col.dataset.skAccInit = '1';
      // Skip contact column.
      if (col.classList.contains('sk-footer-contact')) {
        col.dataset.skAccOpen = '1';
        return;
      }
      const heading = col.querySelector('.sk-footer-col__heading, .sk-footer-col__title, h3');
      if (!heading) return;
      heading.setAttribute('role', 'button');
      heading.setAttribute('tabindex', '0');
      heading.setAttribute('aria-expanded', 'false');
      const toggle = (e) => {
        if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        const next = col.dataset.skAccOpen === '1' ? '0' : '1';
        col.dataset.skAccOpen = next;
        heading.setAttribute('aria-expanded', next === '1' ? 'true' : 'false');
      };
      heading.addEventListener('click', toggle);
      heading.addEventListener('keydown', toggle);
    });
  }

  /* ====================================================================== *
   * Global reveal observer — flips `.is-visible` on every [data-sk-reveal]
   * element when it enters the viewport.
   *
   * Background: many sections (home our-story, footer, etc.) wrap content
   * in `<div data-sk-reveal>` and rely on the global CSS rule
   *    [data-sk-reveal] { opacity: 0; transition: opacity 600ms; }
   *    [data-sk-reveal].is-visible { opacity: 1; }
   * Without an observer adding `is-visible`, those wrappers stay
   * invisible forever. This was previously hot-fixed only inside
   * specific code paths (catalog grids, etc.) — this generic observer
   * ensures EVERY reveal wrapper on every page lights up correctly.
   *
   * Idempotent: each element gets `data-sk-reveal-watched` once it's
   * been observed so re-runs don't double-attach. For elements already
   * in viewport on first run, we add `is-visible` immediately to avoid
   * an empty initial paint.
   * ====================================================================== */
  function initRevealObserver() {
    if (typeof IntersectionObserver === 'undefined') {
      // No IO support → make everything visible immediately.
      document.querySelectorAll('[data-sk-reveal]').forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const els = document.querySelectorAll('[data-sk-reveal]:not([data-sk-reveal-watched])');
    if (!els.length) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) {
          en.target.classList.add('is-visible');
          io.unobserve(en.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });

    els.forEach((el) => {
      el.dataset.skRevealWatched = '1';
      const r = el.getBoundingClientRect();
      // If the element is already in viewport (e.g., above-the-fold hero),
      // reveal it immediately so we never paint at opacity:0.
      if (r.top < window.innerHeight && r.bottom > 0) {
        el.classList.add('is-visible');
      } else {
        io.observe(el);
      }
    });
  }

  function initInnerCircle() {
    document.querySelectorAll('[data-sk-inner-circle-copy]').forEach((btn) => {
      if (btn.dataset.skBound === '1') return;
      btn.dataset.skBound = '1';
      btn.addEventListener('click', () => {
        const codeEl = btn.parentNode && btn.parentNode.querySelector('[data-sk-inner-circle-code]');
        const code = codeEl ? codeEl.textContent.trim() : '';
        if (!code) return;
        try {
          navigator.clipboard.writeText(code);
          const original = btn.innerHTML;
          btn.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden><path d="M5 12l5 5 9-11" stroke-linecap="round" stroke-linejoin="round"/></svg> Copied';
          setTimeout(() => { btn.innerHTML = original; }, 1800);
          try { localStorage.setItem('sanki:inner-circle:claimed', JSON.stringify({ code: code, at: Date.now() })); } catch (_) {}
        } catch (_) {
          if (H.toast) H.toast('Code: ' + code);
        }
      });
    });
  }

  /* ====================================================================== *
   * Policy hijack — bulletproof override of /policies/* pages.
   *
   * Why: Shopify's `/policies/*` URLs sometimes render the default markup
   * (`<div class="shopify-policy__container">`) regardless of what
   * `templates/policy.liquid` contains, especially when admin policies
   * are populated and edge caches are sticky. To guarantee the user sees
   * our luxury layout no matter what the server returns, we detect the
   * policy URL on the client and replace the default container with our
   * own shell + content.
   *
   * Also kicks in if the merchant has placed admin-policy content that
   * we don't want to show on /policies/privacy-policy etc.
   * ====================================================================== */

  /* Inline policy bodies — single source of truth for the client hijack.
     The Liquid snippets remain authoritative for server-rendered routes
     (`templates/page.<policy>.liquid` and the 404 fallback); these JS
     copies are byte-identical structurally so the experience is the same
     wherever the policy resolves. */
  const SK_POLICY_DATA = {
    'privacy-policy': {
      kind: 'privacy',
      icon: '🔒',
      title: 'Privacy Policy',
      intro: 'How we collect, use and safeguard your personal information.',
      body: ''
        + '<section id="overview"><h2>Overview</h2><ul>'
        +   '<li>We value your privacy and are committed to protecting your personal information.</li>'
        + '</ul></section>'
        + '<section id="collection"><h2>Information Collection</h2>'
        +   '<p>We collect only necessary information such as:</p><ul>'
        +   '<li>Name</li><li>Contact details</li><li>Shipping address</li>'
        + '</ul></section>'
        + '<section id="usage"><h2>Usage of Information</h2>'
        +   '<p>Your information is used to:</p><ul>'
        +   '<li>Process orders</li><li>Improve user experience</li><li>Communicate order updates</li>'
        + '</ul></section>'
        + '<section id="protection"><h2>Data Protection</h2><ul>'
        +   '<li>We implement secure measures to protect your data.</li>'
        + '</ul></section>'
        + '<section id="third-party"><h2>Third-Party Sharing</h2><ul>'
        +   '<li>We do not sell or misuse your personal information.</li>'
        +   '<li>Data may be shared only with trusted services (e.g., logistics partners).</li>'
        + '</ul></section>'
        + '<section id="consent"><h2>Consent</h2><ul>'
        +   '<li>By using our website, you agree to our privacy practices.</li>'
        + '</ul></section>'
        + '<section id="contact"><h2>Contact</h2><ul>'
        +   '<li>For any privacy-related queries, write to <a href="mailto:sankicatalogue@gmail.com">sankicatalogue@gmail.com</a>.</li>'
        + '</ul></section>'
    },
    'refund-policy': {
      kind: 'refund',
      icon: '🔄',
      title: 'Refunds / Exchanges',
      intro: 'No refunds. Exchange-only — within 7 days of delivery.',
      body: ''
        + '<section id="overview"><h2>Overview</h2><ul>'
        +   '<li>We do not offer refunds on any purchases.</li>'
        +   '<li>However, we provide an exchange option under specific conditions.</li>'
        + '</ul></section>'
        + '<section id="eligibility"><h2>Exchange Eligibility</h2><ul>'
        +   '<li>Exchanges are applicable only in case of size issues.</li>'
        +   '<li>The request must be raised within <strong>7 days</strong> of receiving the product.</li>'
        +   '<li>The product must be:<ul>'
        +     '<li>Unused</li><li>Unwashed</li><li>In original condition with all tags intact</li>'
        +   '</ul></li>'
        + '</ul></section>'
        + '<section id="process"><h2>Exchange Process</h2><ul>'
        +   '<li>Customers must contact support within the eligibility period.</li>'
        +   '<li>Once approved, exchange instructions will be shared.</li>'
        +   '<li>Exchange is subject to product availability.</li>'
        + '</ul></section>'
        + '<section id="notes"><h2>Important Notes</h2><ul>'
        +   '<li>Requests beyond 7 days will not be accepted.</li>'
        +   '<li>Products failing quality checks will not be eligible for exchange.</li>'
        + '</ul></section>'
        + '<section id="contact"><h2>Contact</h2><ul>'
        +   '<li>For exchange requests, write to <a href="mailto:sankicatalogue@gmail.com">sankicatalogue@gmail.com</a>.</li>'
        + '</ul></section>'
    },
    'shipping-policy': {
      kind: 'shipping',
      icon: '📦',
      title: 'Shipping Policy',
      intro: 'Where we ship, how long it takes, and what you pay.',
      body: ''
        + '<section id="overview"><h2>Overview</h2><ul>'
        +   '<li>We aim to deliver your orders in a timely and secure manner.</li>'
        + '</ul></section>'
        + '<section id="processing"><h2>Processing Time</h2><ul>'
        +   '<li>Orders are processed within <strong>1&ndash;3 business days</strong>.</li>'
        + '</ul></section>'
        + '<section id="delivery"><h2>Delivery Time</h2><ul>'
        +   '<li>Standard delivery typically takes <strong>3&ndash;7 business days</strong>, depending on location.</li>'
        + '</ul></section>'
        + '<section id="charges"><h2>Shipping Charges</h2><ul>'
        +   '<li>Shipping charges (if applicable) are displayed at checkout.</li>'
        + '</ul></section>'
        + '<section id="tracking"><h2>Order Tracking</h2><ul>'
        +   '<li>Tracking details will be shared once the order is shipped.</li>'
        + '</ul></section>'
        + '<section id="delays"><h2>Delays</h2><ul>'
        +   '<li>Unexpected delays may occur due to external factors (logistics, weather, etc.).</li>'
        + '</ul></section>'
        + '<section id="contact"><h2>Contact</h2><ul>'
        +   '<li>For any shipping queries, write to <a href="mailto:sankicatalogue@gmail.com">sankicatalogue@gmail.com</a>.</li>'
        + '</ul></section>'
    }
  };

  function skPolicyShellHtml(data, handle) {
    const otherLinks = [
      { h: 'refund-policy',  icon: '🔄', label: 'Refunds / Exchanges' },
      { h: 'shipping-policy', icon: '📦', label: 'Shipping' },
      { h: 'privacy-policy',  icon: '🔒', label: 'Privacy' }
    ].map(function (l) {
      const active = l.h === handle ? ' class="is-active"' : '';
      return '<li' + active + '><a href="/policies/' + l.h + '">'
           +   '<span class="sk-policy__sidecard-icon" aria-hidden="true">' + l.icon + '</span>'
           +   '<span>' + l.label + '</span>'
           + '</a></li>';
    }).join('');

    return ''
      + '<section class="sk-section sk-policy sk-policy--lux" data-sk-policy>'
      +   '<div class="sk-policy__progress" aria-hidden="true">'
      +     '<span class="sk-policy__progress-fill" data-sk-policy-progress></span>'
      +   '</div>'
      +   '<div class="sk-container sk-policy__shell">'
      +     '<header class="sk-policy__hero">'
      +       '<nav class="sk-policy__crumbs" aria-label="Breadcrumb"><ol>'
      +         '<li><a href="/">Home</a></li>'
      +         '<li><span aria-current="page">' + data.title + '</span></li>'
      +       '</ol></nav>'
      +       '<span class="sk-policy__icon" aria-hidden="true">' + data.icon + '</span>'
      +       '<p class="sk-policy__eyebrow">SANKI · LEGAL</p>'
      +       '<h1 class="sk-policy__title">' + data.title + '</h1>'
      +       (data.intro ? '<p class="sk-policy__intro">' + data.intro + '</p>' : '')
      +       '<p class="sk-policy__updated">Last updated · April 2026</p>'
      +     '</header>'
      +     '<hr class="sk-policy__divider" aria-hidden="true">'
      +     '<div class="sk-policy__layout">'
      +       '<aside class="sk-policy__sidebar" aria-label="Table of contents">'
      +         '<div class="sk-policy__toc" data-sk-policy-toc>'
      +           '<div class="sk-policy__toc-head">'
      +             '<p class="sk-policy__toc-title">CONTENTS</p>'
      +             '<button type="button" class="sk-policy__toc-close" data-sk-policy-toc-close aria-label="Close">'
      +               '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round"/></svg>'
      +             '</button>'
      +           '</div>'
      +           '<ol class="sk-policy__toc-list" data-sk-policy-toc-list></ol>'
      +         '</div>'
      +         '<div class="sk-policy__sidecards">'
      +           '<div class="sk-policy__sidecard">'
      +             '<p class="sk-policy__sidecard-title">NEED HELP?</p>'
      +             '<p class="sk-policy__sidecard-body">Daily, 11&nbsp;AM&ndash;10&nbsp;PM&nbsp;IST.</p>'
      +             '<a class="sk-policy__sidecard-link" href="mailto:sankicatalogue@gmail.com">sankicatalogue@gmail.com</a>'
      +           '</div>'
      +           '<div class="sk-policy__sidecard sk-policy__sidecard--quiet">'
      +             '<p class="sk-policy__sidecard-title">OTHER POLICIES</p>'
      +             '<ul role="list">' + otherLinks + '</ul>'
      +           '</div>'
      +         '</div>'
      +       '</aside>'
      +       '<article class="sk-policy__body" data-sk-policy-body>'
      +         '<div class="sk-policy__doc" data-sk-policy-doc>' + data.body + '</div>'
      +       '</article>'
      +     '</div>'
      +     '<hr class="sk-policy__divider" aria-hidden="true">'
      +     '<footer class="sk-policy__foot">'
      +       '<a href="/" class="sk-policy__back">← Back to Sanki</a>'
      +       '<div class="sk-policy__foot-actions">'
      +         '<button type="button" class="sk-policy__action" data-sk-policy-share>Share</button>'
      +         '<button type="button" class="sk-policy__action" data-sk-policy-print>Print</button>'
      +       '</div>'
      +     '</footer>'
      +   '</div>'
      +   '<button type="button" class="sk-policy__mobile-trigger" data-sk-policy-toc-trigger aria-haspopup="true" aria-expanded="false" aria-label="Open contents">'
      +     '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M4 6h16M4 12h16M4 18h10" stroke-linecap="round"/></svg>'
      +     '<span>Contents</span>'
      +   '</button>'
      +   '<div class="sk-policy__mobile-scrim" data-sk-policy-toc-scrim aria-hidden="true"></div>'
      +   '<button type="button" class="sk-policy__fab" data-sk-policy-top aria-label="Back to top">'
      +     '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M12 4v16M5 11l7-7 7 7" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      +   '</button>'
      +   '<div class="sk-policy__pin-toast" data-sk-policy-toast role="status" aria-live="polite">'
      +     '<span data-sk-policy-toast-msg>Link copied</span>'
      +   '</div>'
      + '</section>';
  }

  function initPolicyHijack() {
    /* Match either /policies/<handle> or /pages/<handle>. */
    const m = (window.location.pathname || '').toLowerCase()
      .match(/^\/(?:policies|pages)\/(privacy-policy|refund-policy|shipping-policy|refunds-exchanges|refunds)\/?$/);
    if (!m) return;

    /* Normalise aliases. */
    let handle = m[1];
    if (handle === 'refunds-exchanges' || handle === 'refunds') handle = 'refund-policy';

    const data = SK_POLICY_DATA[handle];
    if (!data) return;

    /* If our luxury shell is already present (server rendered it via
       templates/policy.liquid or the 404 fallback), do nothing — the
       regular initPolicyPage will handle it. */
    if (document.querySelector('[data-sk-policy]')) return;

    /* Find the host element to replace. We try several known selectors
       so this works whether Shopify rendered:
         (a) `<div class="shopify-policy__container">…</div>`
         (b) `<main id="MainContent">` containing only the policy body
         (c) any other variant — fallback to injecting at the start of main. */
    const candidates = [
      '.shopify-policy__container',
      '.shopify-policy',
      '[role="main"] .rte',
      '[role="main"]'
    ];
    let target = null;
    for (let i = 0; i < candidates.length; i++) {
      target = document.querySelector(candidates[i]);
      if (target) break;
    }
    if (!target) return;

    /* Build and inject the shell. */
    const shellHtml = skPolicyShellHtml(data, handle);

    if (target.matches('[role="main"]') && !target.matches('.shopify-policy__container')) {
      /* Wipe the entire main and replace contents. */
      target.innerHTML = shellHtml;
    } else {
      /* Replace the container element itself. */
      const wrap = document.createElement('div');
      wrap.innerHTML = shellHtml;
      target.parentNode.replaceChild(wrap.firstChild, target);
    }

    /* Update document title to match. */
    const shopName = (document.title.split('—').pop() || '').trim();
    document.title = data.title + (shopName ? ' — ' + shopName : '');
  }

  /* ====================================================================== *
   * Policy pages — production-grade interactions.
   *
   *   1. Build TOC from <h2>s, with numbered chips.
   *   2. Strip "N. " prefix from h2 text (counter chip handles numbering).
   *   3. Permalink "#" icons; click → copy URL + toast.
   *   4. Smooth scroll for in-page anchors.
   *   5. IntersectionObserver scroll-spy → active TOC item.
   *   6. RAF-throttled reading-progress bar.
   *   7. Reading-time + section-count meta pills.
   *   8. Mobile TOC bottom-sheet drawer (open / close / scrim / Esc).
   *   9. Floating back-to-top FAB (visible after first viewport).
   *  10. Section-reveal on scroll (fade-up).
   *  11. Print + Share (Web Share API with copy-link fallback).
   *  12. Deep-link handling on load.
   *
   * Pure-vanilla, idempotent, hot-reload safe.
   * ====================================================================== */
  function initPolicyPage() {
    const root = document.querySelector('[data-sk-policy]');
    if (!root || root.dataset.skPolicyInit === '1') return;
    root.dataset.skPolicyInit = '1';

    const body = root.querySelector('[data-sk-policy-body]');
    const tocList = root.querySelector('[data-sk-policy-toc-list]');
    const progressFill = root.querySelector('[data-sk-policy-progress]');
    const printBtn = root.querySelector('[data-sk-policy-print]');
    const shareBtn = root.querySelector('[data-sk-policy-share]');
    const fab = root.querySelector('[data-sk-policy-top]');
    const tocTopBtn = root.querySelector('[data-sk-policy-toc-top]');
    const sidebar = root.querySelector('.sk-policy__sidebar');
    const mobileTrigger = root.querySelector('[data-sk-policy-toc-trigger]');
    const mobileScrim = root.querySelector('[data-sk-policy-toc-scrim]');
    const mobileClose = root.querySelector('[data-sk-policy-toc-close]');
    const mobileCount = root.querySelector('[data-sk-policy-mobile-count]');
    const sectionCountEl = root.querySelector('[data-sk-policy-section-count]');
    const readTimeEl = root.querySelector('[data-sk-policy-read-time]');
    const toast = root.querySelector('[data-sk-policy-toast]');
    const toastMsg = root.querySelector('[data-sk-policy-toast-msg]');
    if (!body) return;

    const headings = Array.from(body.querySelectorAll('section[id] > h2'));
    const sections = Array.from(body.querySelectorAll('section[id]'));

    /* ---------- 1. Strip "N. " prefix from h2s (counter chip replaces) - */
    headings.forEach((h2) => {
      // Preserve any child elements (like permalink anchor) by mapping
      // only the leading text node.
      const first = h2.firstChild;
      if (first && first.nodeType === Node.TEXT_NODE) {
        first.nodeValue = first.nodeValue.replace(/^\s*\d+\.\s+/, '');
      } else {
        // Fallback — set textContent if there's no text-node child.
        h2.textContent = h2.textContent.replace(/^\s*\d+\.\s+/, '');
      }
      h2.classList.add('is-numbered');
    });

    /* ---------- 2. Build TOC ----------------------------------------- */
    if (tocList && headings.length) {
      tocList.innerHTML = '';
      headings.forEach((h2) => {
        const section = h2.parentElement;
        const id = section && section.id;
        if (!id) return;
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = '#' + id;
        a.textContent = h2.textContent.trim();
        a.dataset.skPolicyTocLink = id;
        li.appendChild(a);
        tocList.appendChild(li);
      });
    }

    /* ---------- 3. Permalink icons on h2 / h3 ------------------------- */
    body.querySelectorAll('section[id] > h2, section[id] > h3').forEach((h) => {
      const section = h.parentElement;
      const id = section && section.id;
      if (!id) return;
      if (h.querySelector('.sk-policy__anchor')) return;
      const a = document.createElement('a');
      a.href = '#' + id;
      a.className = 'sk-policy__anchor';
      a.setAttribute('aria-label', 'Permalink to "' + h.textContent.trim() + '"');
      a.innerHTML = '#';
      h.appendChild(a);
    });

    /* ---------- 4. Toast + clipboard helpers ------------------------- */
    let toastTimer = 0;
    function showToast(msg) {
      if (!toast) return;
      if (toastMsg) toastMsg.textContent = msg || 'Done';
      toast.classList.add('is-visible');
      if (toastTimer) clearTimeout(toastTimer);
      toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 1800);
    }
    function copyToClipboard(text) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          return navigator.clipboard.writeText(text);
        }
      } catch (_) {}
      // Legacy fallback.
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (_) {}
      return Promise.resolve();
    }

    /* ---------- 5. Smooth-scroll + permalink-copy on anchor click ----- */
    root.addEventListener('click', (e) => {
      const a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      const hash = a.getAttribute('href');
      if (!hash || hash.length < 2) return;
      const target = document.getElementById(hash.slice(1));
      if (!target) return;
      e.preventDefault();
      const top = target.getBoundingClientRect().top + window.pageYOffset - 88;
      window.scrollTo({ top: top, behavior: 'smooth' });
      try { history.replaceState(null, '', hash); } catch (_) {}

      // If the user clicked a permalink "#" icon, copy the URL.
      if (a.classList && a.classList.contains('sk-policy__anchor')) {
        const url = window.location.origin + window.location.pathname + hash;
        Promise.resolve(copyToClipboard(url)).then(() => showToast('Link copied'));
      }

      // If the click came from inside the mobile TOC, close the drawer.
      if (sidebar && sidebar.classList.contains('is-open') &&
          a.closest('[data-sk-policy-toc]')) {
        closeMobileToc();
      }
    });

    /* ---------- 6. Scroll-spy via IntersectionObserver --------------- */
    const tocLinks = tocList ? Array.from(tocList.querySelectorAll('a[data-sk-policy-toc-link]')) : [];
    if (tocLinks.length && 'IntersectionObserver' in window) {
      const linkById = {};
      tocLinks.forEach((l) => { linkById[l.dataset.skPolicyTocLink] = l; });

      let activeId = null;
      const visible = new Map();

      function setActive(id) {
        if (id === activeId) return;
        activeId = id;
        tocLinks.forEach((l) => l.classList.remove('is-active'));
        const link = linkById[id];
        if (link) {
          link.classList.add('is-active');
          if (sidebar) {
            const lr = link.getBoundingClientRect();
            const sr = sidebar.getBoundingClientRect();
            if (lr.top < sr.top || lr.bottom > sr.bottom) {
              sidebar.scrollTop += (lr.top - sr.top) - sr.height / 2 + lr.height / 2;
            }
          }
        }
      }

      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          const id = en.target.id;
          if (!id) return;
          if (en.isIntersecting) visible.set(id, en.intersectionRatio);
          else visible.delete(id);
        });
        if (!visible.size) return;
        let bestId = null;
        let bestRatio = -1;
        visible.forEach((ratio, id) => {
          if (ratio > bestRatio) { bestRatio = ratio; bestId = id; }
        });
        if (bestId) setActive(bestId);
      }, {
        rootMargin: '-30% 0% -55% 0%',
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1]
      });

      sections.forEach((s) => io.observe(s));

      const firstId = headings[0] && headings[0].parentElement && headings[0].parentElement.id;
      if (firstId) setActive(firstId);
    }

    /* ---------- 7. Section reveal on scroll -------------------------- */
    if ('IntersectionObserver' in window) {
      const reveal = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('is-revealed');
            reveal.unobserve(en.target);
          }
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
      sections.forEach((s) => reveal.observe(s));
    } else {
      sections.forEach((s) => s.classList.add('is-revealed'));
    }

    /* ---------- 8. Reading progress + back-to-top FAB --------------- */
    if (progressFill || fab) {
      let raf = 0;
      function update() {
        raf = 0;
        if (progressFill) {
          const rect = body.getBoundingClientRect();
          const docTop = rect.top + window.pageYOffset;
          const docHeight = body.offsetHeight;
          const viewportH = window.innerHeight;
          const scrolled = window.pageYOffset - docTop + viewportH * 0.5;
          const total = Math.max(docHeight, 1);
          let ratio = scrolled / total;
          if (ratio < 0) ratio = 0;
          if (ratio > 1) ratio = 1;
          progressFill.style.width = (ratio * 100).toFixed(2) + '%';
        }
        if (fab) {
          if (window.pageYOffset > window.innerHeight * 0.7) fab.classList.add('is-visible');
          else fab.classList.remove('is-visible');
        }
      }
      function onScroll() {
        if (raf) return;
        raf = requestAnimationFrame(update);
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      update();
    }

    /* ---------- 9. Reading-time + section-count meta pills ---------- */
    (function computeStats() {
      const text = body.innerText || body.textContent || '';
      const wordCount = (text.match(/\S+/g) || []).length;
      // 230 wpm reading speed; floor at 1 min, round to nearest minute.
      const mins = Math.max(1, Math.round(wordCount / 230));
      const secs = sections.length;
      if (sectionCountEl) sectionCountEl.textContent = secs;
      if (readTimeEl) readTimeEl.textContent = mins;
      if (mobileCount) mobileCount.textContent = secs;
    })();

    /* ---------- 10. Mobile TOC drawer -------------------------------- */
    function openMobileToc() {
      if (!sidebar) return;
      sidebar.classList.add('is-open');
      if (mobileScrim) mobileScrim.classList.add('is-open');
      if (mobileTrigger) mobileTrigger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }
    function closeMobileToc() {
      if (!sidebar) return;
      sidebar.classList.remove('is-open');
      if (mobileScrim) mobileScrim.classList.remove('is-open');
      if (mobileTrigger) mobileTrigger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
    if (mobileTrigger) {
      mobileTrigger.addEventListener('click', () => {
        if (sidebar && sidebar.classList.contains('is-open')) closeMobileToc();
        else openMobileToc();
      });
    }
    if (mobileScrim) mobileScrim.addEventListener('click', closeMobileToc);
    if (mobileClose) mobileClose.addEventListener('click', closeMobileToc);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && sidebar && sidebar.classList.contains('is-open')) {
        closeMobileToc();
      }
    });

    /* ---------- 11. FAB / TOC "back to top" -------------------------- */
    function scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (fab) fab.addEventListener('click', scrollToTop);
    if (tocTopBtn) tocTopBtn.addEventListener('click', scrollToTop);

    /* ---------- 12. Print / Share ----------------------------------- */
    if (printBtn) {
      printBtn.addEventListener('click', () => { try { window.print(); } catch (_) {} });
    }
    if (shareBtn) {
      shareBtn.addEventListener('click', () => {
        const url = window.location.href;
        const title = document.title || 'Sanki policy';
        if (navigator.share) {
          navigator.share({ title: title, url: url }).catch(() => {});
        } else {
          Promise.resolve(copyToClipboard(url)).then(() => showToast('Link copied'));
        }
      });
    }

    /* ---------- 13. Deep-link handling on load ----------------------- */
    if (window.location.hash && window.location.hash.length > 1) {
      const target = document.getElementById(window.location.hash.slice(1));
      if (target) {
        setTimeout(() => {
          const top = target.getBoundingClientRect().top + window.pageYOffset - 88;
          window.scrollTo({ top: top, behavior: 'instant' in window ? 'auto' : 'smooth' });
        }, 60);
      }
    }
  }

  /* -- Cart page (full /cart page) -----------------------------------------*/

  /**
   * INR-prefixed money formatter with Indian lakh/crore comma grouping.
   * Returns a HTML string with a <span class="sk-money__code"> wrapper
   * so callers can letter-track / colour the code independently.
   */
  function fmtINR(cents) {
    const r = Math.max(0, Math.round((cents || 0) / 100));
    const s = String(r);
    let grouped;
    if (s.length <= 3) {
      grouped = s;
    } else {
      const last3 = s.slice(-3);
      const rest = s.slice(0, -3);
      grouped = rest.replace(/(\d)(?=(\d{2})+$)/g, '$1,') + ',' + last3;
    }
    return '<span class="sk-money__code">INR</span> ' + grouped;
  }

  /**
   * Hooks the full cart page for live updates: qty / remove / save-for-later
   * sync line items, totals, free-ship progress, and the hero count in
   * real time. No page reload anywhere — even on cart-empty we render
   * the empty state in place.
   */
  function initCartPage() {
    const page = document.querySelector('[data-sk-cart-page]');
    if (!page || page.dataset.skCartPageInit === '1') return;
    page.dataset.skCartPageInit = '1';

    const FREE_THRESHOLD = parseInt(page.dataset.skFreeshipThreshold, 10) || 499800;
    const summaryEl = page.querySelector('.sk-cart-pg__summary');
    const FLAT_SHIP_CENTS = summaryEl
      ? (parseInt(summaryEl.dataset.skFlatShipping, 10) || 9900)
      : 9900;

    /** Returns the shipping cost in cents for a given subtotal. */
    function shippingCentsFor(subtotalCents) {
      return subtotalCents >= FREE_THRESHOLD ? 0 : FLAT_SHIP_CENTS;
    }
    /** Returns the grand total in cents (subtotal + shipping). */
    function grandTotalCentsFor(subtotalCents) {
      return subtotalCents + shippingCentsFor(subtotalCents);
    }

    /**
     * STAND-ALONE QTY HANDLER — bypasses wireCartControls entirely.
     *
     * Instead of relying on the shared chain (which fires the global
     * `sanki:cart:change` event and depends on a string of closures),
     * this captures qty/remove/save clicks at the CAPTURE phase on the
     * page element, stops propagation, makes a direct POST to
     * /cart/change.js, and feeds the response straight into
     * `applyCartToPage()`.
     *
     * Result: every interaction has a tight, deterministic loop with
     * no opportunity for events to be swallowed by third-party scripts
     * or by double-bound listeners.
     */
    /**
     * Compute the cart subtotal from the live DOM state, ignoring rows
     * that are mid-removal. Each row carries `data-unit-price` (in cents
     * — final_price per unit) so we can compute the new subtotal
     * optimistically without waiting for the server. This is what makes
     * the summary feel "instant" when the user clicks +/-.
     */
    function computeOptimisticSubtotal() {
      const rows = page.querySelectorAll('[data-sk-cart-page-lines] [data-line-key]');
      let subtotal = 0;
      rows.forEach((row) => {
        if (row.dataset.skRemoving === '1') return;
        const unit = parseInt(row.dataset.unitPrice, 10) || 0;
        const inp  = row.querySelector('[data-sk-qty-input]');
        const q    = Math.max(0, parseInt(inp ? inp.value : '0', 10) || 0);
        subtotal += unit * q;
      });
      return subtotal;
    }

    /**
     * Apply an optimistic update derived purely from the DOM. Faster
     * than a full applyCartToPage() because no event/fetch round-trip;
     * runs the moment the user clicks +/-/remove. The server response
     * later confirms (or corrects) via applyCartToPage().
     */
    function applyOptimistic() {
      const subtotalC = computeOptimisticSubtotal();
      const shippingC = shippingCentsFor(subtotalC);
      const grandTotalC = subtotalC + shippingC;

      /* Money mirrors. */
      const setHTML = (sel, html) => {
        const el = page.querySelector(sel);
        if (!el) return;
        const norm = (s) => s.replace(/\s+/g, ' ').trim();
        if (norm(el.innerHTML) === norm(html)) return;
        el.innerHTML = html;
        el.classList.remove('is-pulsing');
        void el.offsetWidth;
        el.classList.add('is-pulsing');
        setTimeout(() => el.classList.remove('is-pulsing'), 480);
      };
      setHTML('[data-sk-cart-page-subtotal]',   fmtINR(subtotalC));
      setHTML('[data-sk-cart-page-total]',      fmtINR(grandTotalC));
      setHTML('[data-sk-cart-page-cta-amount]', fmtINR(grandTotalC));

      /* Shipping row text. */
      const shipRow = page.querySelector('[data-sk-cart-page-shipping]');
      if (shipRow) {
        shipRow.innerHTML = subtotalC >= FREE_THRESHOLD
          ? 'Free · pan-India'
          : fmtINR(shippingC);
      }

      /* Free-ship strip live update. */
      updateFreeShip({ total_price: subtotalC });

      /* Each visible row's line total. */
      page.querySelectorAll('[data-sk-cart-page-lines] [data-line-key]').forEach((row) => {
        if (row.dataset.skRemoving === '1') return;
        const unit = parseInt(row.dataset.unitPrice, 10) || 0;
        const inp  = row.querySelector('[data-sk-qty-input]');
        const q    = Math.max(0, parseInt(inp ? inp.value : '0', 10) || 0);
        const totalEl = row.querySelector('.sk-cart-pg__line-total-amount');
        if (totalEl) {
          const next = fmtINR(unit * q);
          const norm = (s) => s.replace(/\s+/g, ' ').trim();
          if (norm(totalEl.innerHTML) !== norm(next)) {
            totalEl.innerHTML = next;
            totalEl.classList.remove('is-pulsing');
            void totalEl.offsetWidth;
            totalEl.classList.add('is-pulsing');
            setTimeout(() => totalEl.classList.remove('is-pulsing'), 480);
          }
        }
      });

      /* Hero count + summary subtitle. */
      const itemCount = Array.from(
        page.querySelectorAll('[data-sk-cart-page-lines] [data-line-key]')
      ).reduce((s, row) => {
        if (row.dataset.skRemoving === '1') return s;
        const inp = row.querySelector('[data-sk-qty-input]');
        return s + (Math.max(0, parseInt(inp ? inp.value : '0', 10) || 0));
      }, 0);
      const heroCount = page.querySelector('.sk-cart-pg__hero-count');
      if (heroCount) heroCount.textContent = itemCount;
      const summarySub = page.querySelector('[data-sk-summary-sub]');
      if (summarySub) {
        const noun = itemCount === 1 ? 'item' : 'items';
        summarySub.textContent = itemCount + ' ' + noun + ' ready to ship';
      }
    }

    function bindDirectCartHandlers() {
      page.addEventListener('click', (e) => {
        const incr   = e.target.closest('[data-sk-qty-incr]');
        const decr   = e.target.closest('[data-sk-qty-decr]');
        const remove = e.target.closest('[data-sk-cart-remove]');
        const save   = e.target.closest('[data-sk-save-for-later]');
        if (!incr && !decr && !remove && !save) return;

        const row = e.target.closest('[data-line-key]');
        if (!row) return;
        const key = row.dataset.lineKey;
        if (!key) return;

        /* Stop the event from reaching wireCartControls' bubble-phase
           listener (which would double-fire the API call). */
        e.preventDefault();
        e.stopImmediatePropagation();

        const input = row.querySelector('[data-sk-qty-input]');
        const cur = parseInt(input ? input.value : '1', 10) || 1;
        let nextQty;
        if (incr)        nextQty = cur + 1;
        else if (decr)   nextQty = Math.max(0, cur - 1);
        else /* remove or save */ nextQty = 0;
        if (input) input.value = nextQty;

        /* Optimistic visual: collapse the row immediately if qty=0. */
        if (nextQty === 0 && typeof optimisticallyRemoveRow === 'function') {
          row.dataset.skRemoving = '1';
          optimisticallyRemoveRow(row);
        }

        /* INSTANT real-time update of summary + line total — no waiting
           for the server. Server response later confirms via
           applyCartToPage. */
        applyOptimistic();

        /* Save-for-later side effect: add to wishlist BEFORE we remove
           from cart, so the product is preserved across the round-trip. */
        if (save) {
          const productId   = save.dataset.skProductId   || row.dataset.productId;
          const variantId   = save.dataset.skVariantId   || row.dataset.variantId;
          const productHandle = save.dataset.skProductHandle || row.dataset.productHandle;
          if (Sanki && Sanki.api && Sanki.api.wishlist) {
            try {
              if (Sanki.api.wishlist.add) {
                Sanki.api.wishlist.add({ id: productId, handle: productHandle, variantId });
              } else if (Sanki.api.wishlist.toggle) {
                const list = Sanki.api.wishlist.list().map((x) => String(x.id));
                if (!list.includes(String(productId))) {
                  Sanki.api.wishlist.toggle({ id: productId, handle: productHandle, variantId });
                }
              }
            } catch (_) { /* non-critical */ }
          }
          const labelEl = save.querySelector('[data-sk-save-label]');
          if (labelEl) labelEl.textContent = 'Saved ✓';
          save.classList.add('is-saved');
          if (H.toast) H.toast('Saved to wishlist');
        }

        /* Direct POST to /cart/change.js. We ALWAYS parse the JSON
           body (success AND failure) so we can surface Shopify's
           actual error message — the most common one is the inventory
           cap ("You can only add N to the cart"), and previously we
           swallowed it as a generic "couldn't update" toast which
           made it look like the + button was just broken. */
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        fetch(root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: nextQty }),
          cache: 'no-store'
        })
          .then((r) => r.json().then((data) => ({ ok: r.ok, status: r.status, data })))
          .then(({ ok, status, data }) => {
            if (ok && data && Array.isArray(data.items)) {
              /* Happy path — server confirmed the new qty. */
              applyCartToPage(data);
              document.dispatchEvent(new CustomEvent('sanki:cart:change', { detail: { cart: data } }));
              return;
            }
            /* Server rejected — usually inventory cap. Surface the
               real reason from Shopify's payload, then refetch the
               canonical cart so the input reverts to the actually
               accepted quantity. */
            const msg = (data && (data.description || data.message)) ||
                        ('Couldn\'t update — server returned ' + (status || 'an error'));
            console.warn('[Sanki] cart change rejected:', status, data);
            if (H.toast) H.toast(msg);
            return fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
              .then((r) => r.ok ? r.json() : null)
              .then((cart) => {
                if (cart) {
                  applyCartToPage(cart);
                  document.dispatchEvent(new CustomEvent('sanki:cart:change', { detail: { cart } }));
                }
              });
          })
          .catch((err) => {
            console.error('[Sanki] cart change failed (network):', err);
            if (H.toast) H.toast('Network error — please retry.');
            fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
              .then((r) => r.ok ? r.json() : null)
              .then((cart) => { if (cart) applyCartToPage(cart); })
              .catch(() => {});
          });
      }, true); /* capture phase — runs before bubble-phase listeners */

      /* Direct typing into the qty input (Enter / blur fires `change`). */
      page.addEventListener('change', (e) => {
        const input = e.target.closest('[data-sk-qty-input]');
        if (!input) return;
        const row = input.closest('[data-line-key]');
        if (!row) return;
        const key = row.dataset.lineKey;
        const qty = Math.max(0, parseInt(input.value, 10) || 0);

        e.preventDefault();
        e.stopImmediatePropagation();

        if (qty === 0 && typeof optimisticallyRemoveRow === 'function') {
          row.dataset.skRemoving = '1';
          optimisticallyRemoveRow(row);
        }

        /* Instant real-time update of summary + line total. */
        applyOptimistic();

        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        fetch(root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty }),
          cache: 'no-store'
        })
          .then((r) => r.json().then((data) => ({ ok: r.ok, status: r.status, data })))
          .then(({ ok, status, data }) => {
            if (ok && data && Array.isArray(data.items)) {
              applyCartToPage(data);
              document.dispatchEvent(new CustomEvent('sanki:cart:change', { detail: { cart: data } }));
              return;
            }
            const msg = (data && (data.description || data.message)) ||
                        ('Couldn\'t update — server returned ' + (status || 'an error'));
            console.warn('[Sanki] cart change rejected:', status, data);
            if (H.toast) H.toast(msg);
            return fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
              .then((r) => r.ok ? r.json() : null)
              .then((cart) => {
                if (cart) {
                  applyCartToPage(cart);
                  document.dispatchEvent(new CustomEvent('sanki:cart:change', { detail: { cart } }));
                }
              });
          })
          .catch((err) => { console.error('[Sanki] cart change failed (network):', err); });
      }, true);
    }
    /* Wire it up immediately. */
    bindDirectCartHandlers();

    /* Reuse generic cart-control wiring for the gift-note debounce only;
       the qty/remove paths above pre-empt the shared handlers via
       capture-phase + stopImmediatePropagation. wireCartControls'
       __skWired guards make this safe to call alongside our direct
       handlers — the listeners attached here are dead code on the
       cart page, but harmless. */
    wireCartControls(page);

    /* Save-for-later: add to wishlist, then remove the line from cart. */
    function wireSaveForLater() {
      page.querySelectorAll('[data-sk-save-for-later]').forEach((btn) => {
        if (btn.__skSflWired) return;
        btn.__skSflWired = true;
        btn.addEventListener('click', () => {
          const productId   = btn.dataset.skProductId;
          const productHandle = btn.dataset.skProductHandle;
          const variantId   = btn.dataset.skVariantId;
          const lineKey     = btn.dataset.skLineKey;
          const row         = btn.closest('[data-line-key]');
          const labelEl     = btn.querySelector('[data-sk-save-label]');

          /* Step 1: add to wishlist (if wishlist API present). */
          if (Sanki && Sanki.api && Sanki.api.wishlist && Sanki.api.wishlist.add) {
            Sanki.api.wishlist.add({ id: productId, handle: productHandle, variantId });
          } else if (API && API.wishlist && API.wishlist.toggle) {
            const list = API.wishlist.list().map((x) => String(x.id));
            if (!list.includes(String(productId))) {
              API.wishlist.toggle({ id: productId, handle: productHandle, variantId });
            }
          }
          if (labelEl) labelEl.textContent = 'Saved ✓';
          btn.classList.add('is-saved');

          /* Step 2: optimistically remove the row + fire cart change. */
          if (row) optimisticallyRemoveRow(row);
          if (lineKey) {
            API.cart.change({ id: lineKey, quantity: 0 }).catch(onCartError);
          }

          H.toast && H.toast('Saved to wishlist');
        });
      });
    }
    wireSaveForLater();

    /* ── Coupon code apply ───────────────────────────────────────────
     * Shopify's storefront doesn't expose a "validate-discount" AJAX
     * endpoint, but `/discount/<CODE>?redirect=/cart` is the canonical
     * way to apply a discount: Shopify validates the code, attaches
     * the discount, and bounces back to /cart. If the code is invalid,
     * Shopify still redirects but the discount silently fails — we
     * guard that path with a localStorage breadcrumb so we can show
     * a clear error message after the round-trip. */
    function wireCoupon() {
      const wrap   = page.querySelector('[data-sk-coupon]');
      if (!wrap) return;
      const input  = wrap.querySelector('[data-sk-coupon-input]');
      const apply  = wrap.querySelector('[data-sk-coupon-apply]');
      const status = wrap.querySelector('[data-sk-coupon-status]');
      if (!input || !apply) return;

      function showStatus(msg, kind) {
        if (!status) return;
        status.textContent = msg || '';
        status.classList.remove('is-error', 'is-success');
        if (kind) status.classList.add('is-' + kind);
      }

      /* On page load — if we just came back from a /discount/ round-trip,
         surface the result. We stamp the attempted code into localStorage
         right before the redirect; on return, we compare against the
         currently-applied discount (in cart.cart_level_discount_applications
         or via reading the cart). */
      try {
        const pending = localStorage.getItem('sanki:coupon:pending');
        if (pending) {
          localStorage.removeItem('sanki:coupon:pending');
          fetch('/cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
            .then((r) => (r.ok ? r.json() : null))
            .then((cart) => {
              if (!cart) return;
              const apps = cart.cart_level_discount_applications || [];
              const matched = apps.some((d) =>
                (d.title && d.title.toLowerCase() === pending.toLowerCase()) ||
                (d.key && d.key.toLowerCase().includes(pending.toLowerCase()))
              );
              if (matched) {
                showStatus('Discount applied: ' + pending.toUpperCase(), 'success');
                wrap.setAttribute('open', '');
                input.value = pending.toUpperCase();
                input.disabled = true;
                apply.disabled = true;
                applyCartToPage(cart);
              } else {
                showStatus('"' + pending + '" is not a valid discount code.', 'error');
                wrap.setAttribute('open', '');
              }
            }).catch(() => {});
        }
      } catch (_) {}

      /* Submit handler — Enter key OR Apply button. */
      function submitCoupon() {
        const code = (input.value || '').trim();
        if (!code) {
          showStatus('Enter a coupon code first.', 'error');
          input.focus();
          return;
        }
        showStatus('Applying ' + code.toUpperCase() + '…');
        try { localStorage.setItem('sanki:coupon:pending', code); } catch (_) {}
        /* Shopify's discount-apply URL. */
        const url = '/discount/' + encodeURIComponent(code) +
                    '?redirect=' + encodeURIComponent('/cart');
        window.location.href = url;
      }

      apply.addEventListener('click', submitCoupon);
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          submitCoupon();
        }
      });
    }
    wireCoupon();

    /* ── Hijack "Proceed to checkout" → route through our branded
     * checkout-review page first. JS-only enhancement; no-JS users
     * still hit Shopify's checkout directly via the form's submit. */
    const checkoutBtn = page.querySelector('[name="checkout"]');
    if (checkoutBtn && !checkoutBtn.__skCheckoutHijacked) {
      checkoutBtn.__skCheckoutHijacked = true;
      checkoutBtn.addEventListener('click', (e) => {
        /* Honour modifier-clicks (cmd / ctrl / middle / shift) so
           opening in a new tab still works. */
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        window.location.href = '/checkout';
      }, true);
    }

    /* ── Cart extras (Make-it-special + Schedule + Channels) ──────────
     * Every interactive control inside `[data-sk-cart-extras]` binds to
     * a Shopify cart attribute. Toggles + text fields write to
     * `/cart/update.js` with `attributes[<key>]=<value>`. The attributes
     * survive checkout and ride along to the order in admin.
     *
     * For toggles that have a corresponding Shopify product handle
     * (e.g. `gift-wrap`, `eco-packaging`, `carbon-offset`,
     * `shipping-protection`), the merchant can create a real product
     * and the toggle additionally adds/removes that line item. If the
     * product doesn't exist, the toggle still captures the preference
     * as an attribute (no fee charged). Either way: end-to-end working.
     */
    function wireExtras() {
      const wrap = page.querySelector('[data-sk-cart-extras]');
      if (!wrap) return;

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      /* Debounced cart-attribute updater. Coalesces rapid toggle flips
         + textarea typing into a single round-trip. */
      const pending = Object.create(null);
      let timer = null;
      function persistAttrs() {
        clearTimeout(timer);
        timer = setTimeout(() => {
          const payload = { attributes: Object.assign({}, pending) };
          /* Reset before fetch so subsequent edits start fresh. */
          for (const k in pending) delete pending[k];
          fetch(root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(payload),
            cache: 'no-store'
          }).catch(() => { /* non-critical */ });
        }, 320);
      }
      function setAttr(key, value) {
        pending[key] = value == null ? '' : String(value);
        persistAttrs();
      }

      /* Toggle inputs — write 'yes' / '' for booleans + add/remove the
         matching product if one exists. */
      wrap.querySelectorAll('[data-sk-extras-toggle]').forEach((cb) => {
        if (cb.__skExtrasWired) return;
        cb.__skExtrasWired = true;
        cb.addEventListener('change', () => {
          const attr = cb.dataset.attr;
          const handle = cb.dataset.productHandle;
          const isOn = cb.checked;
          if (attr) setAttr(attr, isOn ? 'yes' : '');

          /* If a product handle is configured, also add/remove the
             line item so the customer is actually charged the fee. */
          if (handle) {
            (isOn ? addAddOnByHandle(handle) : removeAddOnByHandle(handle))
              .catch(() => { /* silent — attribute still captured */ });
          }
        });
      });

      /* Text fields (gift_recipient, gift_message, sms_phone, etc.) */
      wrap.querySelectorAll('[data-sk-extras-attr]').forEach((el) => {
        if (el.__skExtrasWired) return;
        el.__skExtrasWired = true;
        const attr = el.dataset.skExtrasAttr;
        const handler = () => setAttr(attr, el.value);
        el.addEventListener('input', handler);
        el.addEventListener('change', handler);
      });

      /* Clear buttons */
      wrap.querySelectorAll('[data-sk-extras-clear]').forEach((btn) => {
        if (btn.__skExtrasWired) return;
        btn.__skExtrasWired = true;
        btn.addEventListener('click', () => {
          const attr = btn.dataset.attr;
          const target = wrap.querySelector('[data-sk-extras-attr="' + attr + '"]');
          if (target) target.value = '';
          setAttr(attr, '');
          btn.hidden = true;
        });
      });

      /* Schedule-delivery date input — set min to today + 3 days,
         exclude weekends client-side, show clear button when filled. */
      const dateInput = wrap.querySelector('[data-sk-schedule-input]');
      if (dateInput) {
        const today = new Date();
        const min = new Date(today);
        min.setDate(today.getDate() + 3);
        const max = new Date(today);
        max.setDate(today.getDate() + 14);
        dateInput.min = min.toISOString().slice(0, 10);
        dateInput.max = max.toISOString().slice(0, 10);
        const clearBtn = wrap.querySelector('[data-sk-extras-clear][data-attr="delivery_date"]');
        const reflectClear = () => { if (clearBtn) clearBtn.hidden = !dateInput.value; };
        dateInput.addEventListener('input', reflectClear);
        dateInput.addEventListener('change', reflectClear);
      }

      /* Gift-message character counter */
      const msgArea = wrap.querySelector('[data-sk-extras-attr="gift_message"]');
      const msgCounter = wrap.querySelector('[data-sk-msg-counter]');
      if (msgArea && msgCounter) {
        const max = parseInt(msgArea.getAttribute('maxlength') || '240', 10);
        const refresh = () => { msgCounter.textContent = (msgArea.value.length) + ' / ' + max; };
        refresh();
        msgArea.addEventListener('input', refresh);
      }

      /* Email-me-my-cart — opens the user's mail client with a
         pre-filled subject + cart summary in the body. Universal
         (works without a backend); upgrade later to a server-side
         magic-link if you want resume-from-anywhere UX. */
      const emailBtn = wrap.querySelector('[data-sk-email-cart]');
      if (emailBtn && !emailBtn.__skEmailWired) {
        emailBtn.__skEmailWired = true;
        emailBtn.addEventListener('click', () => {
          fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
            .then((r) => r.ok ? r.json() : null)
            .then((cart) => {
              if (!cart) return;
              const lines = (cart.items || []).map((l) =>
                '• ' + l.quantity + ' × ' + l.product_title +
                (l.variant_title ? ' (' + l.variant_title + ')' : '') +
                ' — ' + fmtINR(l.final_line_price).replace(/<[^>]+>/g, '').trim()
              ).join('%0D%0A');
              const total = fmtINR(cart.total_price).replace(/<[^>]+>/g, '').trim();
              const body =
                'Here\'s my SANKI cart — picking up here later:%0D%0A%0D%0A' +
                lines + '%0D%0A%0D%0A' +
                'Subtotal: ' + total + '%0D%0A' +
                'Resume → ' + window.location.origin + '/cart';
              const subject = 'My SANKI cart (' + cart.item_count + ' items)';
              window.location.href = 'mailto:?subject=' + encodeURIComponent(subject).replace(/%20/g, '+') +
                                     '&body=' + body;
            }).catch(() => {});
        });
      }

      /* Helpers — add/remove an add-on product by handle. */
      function addAddOnByHandle(handle) {
        /* Look up the variant id via the storefront product JSON. */
        return fetch('/products/' + encodeURIComponent(handle) + '.js', {
          headers: { 'Accept': 'application/json' }, cache: 'no-store'
        })
          .then((r) => r.ok ? r.json() : Promise.reject(r.status))
          .then((prod) => {
            const v = prod.variants && prod.variants[0];
            if (!v) return Promise.reject('no variant');
            return fetch(root + 'cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: v.id, quantity: 1 })
            });
          })
          .then((r) => r && r.ok ? r.json() : null)
          .then((res) => {
            if (res) {
              fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
                .then((r) => r.ok ? r.json() : null)
                .then((cart) => { if (cart) applyCartToPage(cart); });
            }
          });
      }
      function removeAddOnByHandle(handle) {
        return fetch(root + 'cart.js', {
          headers: { 'Accept': 'application/json' }, cache: 'no-store'
        })
          .then((r) => r.ok ? r.json() : null)
          .then((cart) => {
            if (!cart) return;
            const line = (cart.items || []).find((it) => it.handle === handle);
            if (!line) return;
            return fetch(root + 'cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: line.key, quantity: 0 })
            }).then(() => fetch(root + 'cart.js', {
              headers: { 'Accept': 'application/json' }, cache: 'no-store'
            }))
              .then((r) => r && r.ok ? r.json() : null)
              .then((c) => { if (c) applyCartToPage(c); });
          });
      }
    }
    wireExtras();

    /* ── EMI + Points + Tier — recompute on every cart change ────── */
    function recomputeRewards() {
      const subtotalEl = page.querySelector('[data-sk-cart-page-subtotal]');
      const totalEl    = page.querySelector('[data-sk-cart-page-total]');
      if (!totalEl) return;
      /* Read the current grand total from the rendered DOM so we
         track post-shipping totals (not just subtotal). */
      const txt = totalEl.textContent.replace(/[^\d]/g, '');
      const grandTotalC = parseInt(txt, 10) * 100;
      if (!Number.isFinite(grandTotalC) || grandTotalC <= 0) return;

      /* EMI — 3 equal instalments, rounded up. */
      const emiPerC = Math.ceil(grandTotalC / 3 / 100) * 100;
      const emiAmt = page.querySelector('[data-sk-cart-emi-amount]');
      if (emiAmt) emiAmt.innerHTML = fmtINR(emiPerC);

      /* Points — 1 per ₹1. */
      const pts = Math.floor(grandTotalC / 100);
      const ptsEl = page.querySelector('[data-sk-cart-points-amount]');
      if (ptsEl) ptsEl.textContent = pts;
    }
    document.addEventListener('sanki:cart:change', recomputeRewards);

    /* Live render of one cart line's HTML — used for fresh re-renders
       after cart change. */
    function renderLineHTML(line) {
      const img = line.image
        ? '<img src="' + line.image + '&width=320" alt="' + H.escape(line.product_title) + '" width="140" height="180" loading="lazy">'
        : '';
      const _stripUni = (vt) => {
        if (!vt) return '';
        return String(vt).split(' / ').filter((p) => {
          const lc = p.toLowerCase();
          return !(lc.includes('universal') || lc.includes('free size') || lc.includes('one size') || lc.includes('fits all'));
        }).join(' / ');
      };
      const cleanVT = _stripUni(line.variant_title);
      const sizePropPg = line.properties && (line.properties.Size || line.properties.size);
      const variant =
        (cleanVT
          ? '<div class="sk-cart-pg__line-variant">' + H.escape(cleanVT) + '</div>'
          : '') +
        (sizePropPg
          ? '<div class="sk-cart-pg__line-variant">Size: <strong>' + H.escape(sizePropPg) + '</strong></div>'
          : '');
      const wasPrice = (line.original_price > line.final_price)
        ? '<span class="sk-cart-pg__line-was">' + fmtINR(line.original_price) + '</span>'
        : '';
      return (
        '<li class="sk-cart-pg__line is-entering" data-line-key="' + H.escape(line.key) + '"' +
              ' data-product-id="' + (line.product_id || '') + '"' +
              ' data-variant-id="' + (line.variant_id || '') + '">' +
          '<a href="' + H.escape(line.url) + '" class="sk-cart-pg__line-media" tabindex="-1">' + img + '</a>' +
          '<div class="sk-cart-pg__line-body">' +
            '<div class="sk-cart-pg__line-meta">' +
              '<a href="' + H.escape(line.url) + '" class="sk-cart-pg__line-title">' + H.escape(line.product_title) + '</a>' +
              variant +
              '<div class="sk-cart-pg__line-price">' + fmtINR(line.final_price) + wasPrice + '</div>' +
            '</div>' +
            '<div class="sk-cart-pg__line-controls">' +
              '<div class="sk-qty sk-qty--lg" role="group" aria-label="Quantity">' +
                '<button type="button" data-sk-qty-decr aria-label="Decrease quantity">&minus;</button>' +
                '<input type="number" name="updates[]" value="' + line.quantity + '" min="0" inputmode="numeric" data-sk-qty-input data-line-key="' + H.escape(line.key) + '">' +
                '<button type="button" data-sk-qty-incr aria-label="Increase quantity">+</button>' +
              '</div>' +
              '<div class="sk-cart-pg__line-actions">' +
                '<button type="button" class="sk-cart-pg__line-action sk-cart-pg__line-action--save" data-sk-save-for-later' +
                    ' data-sk-product-id="' + (line.product_id || '') + '"' +
                    ' data-sk-variant-id="' + (line.variant_id || '') + '"' +
                    ' data-sk-line-key="' + H.escape(line.key) + '" aria-label="Save for later">' +
                  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">' +
                    '<path d="M12 21s-7-4.5-9.5-9A5.5 5.5 0 0 1 12 6a5.5 5.5 0 0 1 9.5 6c-2.5 4.5-9.5 9-9.5 9z" stroke-linecap="round" stroke-linejoin="round"/>' +
                  '</svg>' +
                  '<span data-sk-save-label>Save for later</span>' +
                '</button>' +
                '<span class="sk-cart-pg__line-actions-sep" aria-hidden="true">·</span>' +
                '<button type="button" class="sk-cart-pg__line-action sk-cart-pg__line-action--remove" data-sk-cart-remove>' +
                  '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">' +
                    '<path d="M4 7h16M9 7V4h6v3M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" stroke-linecap="round" stroke-linejoin="round"/>' +
                  '</svg>Remove' +
                '</button>' +
              '</div>' +
            '</div>' +
          '</div>' +
          '<div class="sk-cart-pg__line-total" aria-label="Line total">' +
            '<span class="sk-cart-pg__line-total-label">Total</span>' +
            '<strong class="sk-cart-pg__line-total-amount">' + fmtINR(line.final_line_price) + '</strong>' +
          '</div>' +
        '</li>'
      );
    }

    /* Render the in-place empty state when the cart hits 0 items.
       Replaces the items + summary grid with the editorial empty
       state markup, no page reload. */
    function renderEmptyInPlace() {
      const container = page.querySelector('.sk-container');
      if (!container) return;
      // Find and remove the layout grid + freeship strip + recs.
      const grid     = container.querySelector('.sk-cart-pg__layout');
      const freeship = container.querySelector('[data-sk-freeship]');
      const recs     = container.querySelector('[data-sk-recs]');
      if (freeship) freeship.remove();
      if (recs) recs.remove();
      if (grid) {
        const empty = document.createElement('div');
        empty.className = 'sk-cart-pg__empty';
        empty.innerHTML =
          '<svg viewBox="0 0 64 64" width="72" height="72" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true">' +
            '<path d="M14 22h36l-3 28a4 4 0 0 1-4 4H21a4 4 0 0 1-4-4l-3-28z" stroke-linejoin="round"/>' +
            '<path d="M22 22V14a10 10 0 0 1 20 0v8" stroke-linecap="round"/>' +
          '</svg>' +
          '<h2 class="sk-cart-pg__empty-title">Your bag is empty.</h2>' +
          '<p class="sk-cart-pg__empty-sub">Crafted in limited drops — every piece designed in our Delhi atelier and made in small runs. Start exploring our latest collection below.</p>' +
          '<div class="sk-cart-pg__empty-actions">' +
            '<a href="/collections/all" class="sk-btn sk-btn--primary">Explore all products</a>' +
            '<a href="/collections" class="sk-cart-pg__empty-link">Browse collections →</a>' +
          '</div>';
        grid.replaceWith(empty);
      }
      // Update hero meta.
      const meta = page.querySelector('.sk-cart-pg__hero-meta');
      if (meta) meta.textContent = 'Your bag is currently empty.';
    }

    /* Update the free-shipping strip — text + fill width. */
    function updateFreeShip(cart) {
      const strip = page.querySelector('[data-sk-freeship]');
      if (!strip) return;
      const fill  = strip.querySelector('[data-sk-freeship-fill]');
      const txt   = strip.querySelector('[data-sk-freeship-text]');
      const check = strip.querySelector('.sk-cart-pg__freeship-check');
      const total = cart.total_price || 0;
      const remaining = Math.max(0, FREE_THRESHOLD - total);
      const pct = Math.min(100, Math.round((total / FREE_THRESHOLD) * 100));
      if (fill) fill.style.width = pct + '%';
      if (total >= FREE_THRESHOLD) {
        strip.classList.add('is-unlocked');
        if (txt) txt.innerHTML = '<strong>FREE pan-India shipping</strong> unlocked on this order';
        if (check) check.innerHTML =
          '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4">' +
            '<path d="M5 12l4 4 10-10" stroke-linecap="round" stroke-linejoin="round"/>' +
          '</svg>';
      } else {
        strip.classList.remove('is-unlocked');
        if (txt) txt.innerHTML = "You're <strong>" + fmtINR(remaining) + "</strong> away from <strong>FREE pan-India shipping</strong>";
        if (check) check.innerHTML = '';
      }
    }

    /**
     * Single source of truth for applying a cart object to the page DOM.
     * Called both by the global `sanki:cart:change` event listener AND
     * by the polling safety net below — whichever lands first wins, and
     * the second is a no-op because the values match.
     *
     * Updates: empty state, hero count, summary subtitle, money mirrors
     * (subtotal/total/CTA), shipping row, free-ship strip, and each
     * line's qty + total + price.
     */
    function applyCartToPage(cart) {
      if (!cart) return;

      /* Empty cart → render empty state in place, no reload. */
      if (cart.item_count === 0) {
        renderEmptyInPlace();
        return;
      }

      /* Compute shipping + grand total from the subtotal we got back. */
      const subtotalC = cart.total_price || 0;
      const shippingC = shippingCentsFor(subtotalC);
      const grandTotalC = subtotalC + shippingC;

      /* Update money mirrors. Subtotal = cart.total_price.
         Total + CTA = subtotal + shipping. Pulses on each change. */
      [
        ['[data-sk-cart-page-subtotal]',    fmtINR(subtotalC)],
        ['[data-sk-cart-page-total]',       fmtINR(grandTotalC)],
        ['[data-sk-cart-page-cta-amount]',  fmtINR(grandTotalC)]
      ].forEach(([sel, html]) => {
        const el = page.querySelector(sel);
        if (!el) return;
        const norm = (s) => s.replace(/\s+/g, ' ').trim();
        if (norm(el.innerHTML) === norm(html)) return; // no-op
        el.innerHTML = html;
        el.classList.remove('is-pulsing');
        void el.offsetWidth;
        el.classList.add('is-pulsing');
        setTimeout(() => el.classList.remove('is-pulsing'), 520);
      });

      /* Update item count in hero + summary subtitle. */
      const heroMeta = page.querySelector('.sk-cart-pg__hero-meta');
      if (heroMeta) {
        const noun = cart.item_count === 1 ? 'item' : 'items';
        heroMeta.innerHTML =
          '<span class="sk-cart-pg__hero-count">' + cart.item_count + '</span> ' + noun + ' in your bag';
      }
      const summarySub = page.querySelector('[data-sk-summary-sub]');
      if (summarySub) {
        const noun = cart.item_count === 1 ? 'item' : 'items';
        summarySub.textContent = cart.item_count + ' ' + noun + ' ready to ship';
      }

      /* Refresh shipping row — flat ₹99 below threshold, FREE above. */
      const shipRow = page.querySelector('[data-sk-cart-page-shipping]');
      if (shipRow) {
        const shipHTML = subtotalC >= FREE_THRESHOLD
          ? 'Free · pan-India'
          : fmtINR(shippingC);
        const norm = (s) => s.replace(/\s+/g, ' ').trim();
        if (norm(shipRow.innerHTML) !== norm(shipHTML)) {
          shipRow.innerHTML = shipHTML;
        }
      }

      /* Update free-ship progress strip. */
      updateFreeShip(cart);

      /* Update each line's qty + total. Lines that no longer exist in
         the cart get removed from the DOM (real-time sync). */
      const list = page.querySelector('[data-sk-cart-page-lines]');
      if (list) {
        const liveKeys = new Set(cart.items.map((l) => l.key));
        list.querySelectorAll('[data-line-key]').forEach((row) => {
          if (!liveKeys.has(row.dataset.lineKey) && !row.dataset.skRemoving) {
            row.dataset.skRemoving = '1';
            row.style.transition = 'opacity 160ms ease, max-height 200ms ease, padding 200ms ease';
            row.style.maxHeight = row.offsetHeight + 'px';
            void row.offsetHeight;
            row.style.opacity = '0';
            row.style.maxHeight = '0';
            row.style.paddingTop = '0';
            row.style.paddingBottom = '0';
            row.style.overflow = 'hidden';
            setTimeout(() => row.remove(), 220);
          }
        });
        cart.items.forEach((line) => {
          const row = list.querySelector('[data-line-key="' + CSS.escape(line.key) + '"]');
          if (!row) return;
          const total = row.querySelector('.sk-cart-pg__line-total-amount');
          if (total) {
            const next = fmtINR(line.final_line_price);
            const norm = (s) => s.replace(/\s+/g, ' ').trim();
            if (norm(total.innerHTML) !== norm(next)) {
              total.innerHTML = next;
              total.classList.remove('is-pulsing');
              void total.offsetWidth;
              total.classList.add('is-pulsing');
              setTimeout(() => total.classList.remove('is-pulsing'), 520);
            }
          }
          const finalP = row.querySelector('.sk-cart-pg__line-price');
          if (finalP) {
            const wasHTML = (line.original_price > line.final_price)
              ? '<span class="sk-cart-pg__line-was">' + fmtINR(line.original_price) + '</span>'
              : '';
            finalP.innerHTML = fmtINR(line.final_price) + wasHTML;
          }
          const input = row.querySelector('[data-sk-qty-input]');
          if (input && document.activeElement !== input) input.value = line.quantity;
        });
      }

      /* Re-wire any newly rendered controls (idempotent). */
      wireCartControls(page);
      wireSaveForLater();
    }

    /* Primary path — the global event fired by api.js after every
       cart-mutating endpoint resolves. */
    document.addEventListener('sanki:cart:change', (e) => {
      const cart = e.detail && e.detail.cart;
      if (!cart) return;
      applyCartToPage(cart);
    });

    /**
     * Bulletproof safety net — after ANY cart-mutating interaction on
     * the page, fetch `/cart.js` and re-apply. This is independent of
     * the global event channel: even if api.js's emitChange never fires,
     * or if a third-party script consumed the event before us, this path
     * always reconciles. Debounced + de-duped so multiple rapid clicks
     * coalesce into a single round-trip.
     */
    let pollAbort = null;
    let pollTimer = null;
    function syncFromServer() {
      clearTimeout(pollTimer);
      if (pollAbort) { try { pollAbort.abort(); } catch (_) {} }
      pollTimer = setTimeout(() => {
        const ac = ('AbortController' in window) ? new AbortController() : null;
        pollAbort = ac;
        fetch('/cart.js', {
          headers: { 'Accept': 'application/json' },
          cache: 'no-store',
          signal: ac ? ac.signal : undefined
        })
          .then((r) => (r.ok ? r.json() : null))
          .then((cart) => { if (cart) applyCartToPage(cart); })
          .catch(() => { /* silent — primary event path may have already updated */ });
      }, 140); /* let the change.js POST land first */
    }

    /* Listen for any control inside the page that mutates the cart. */
    page.addEventListener('click', (e) => {
      if (e.target.closest(
        '[data-sk-qty-decr], [data-sk-qty-incr], [data-sk-cart-remove], [data-sk-save-for-later]'
      )) {
        syncFromServer();
        /* Re-poll once more after a longer delay for slow networks. */
        setTimeout(syncFromServer, 600);
      }
    });
    page.addEventListener('change', (e) => {
      if (e.target.matches && e.target.matches('[data-sk-qty-input]')) {
        syncFromServer();
        setTimeout(syncFromServer, 600);
      }
    });
    /* Also catch direct keyboard input on qty fields (debounced). */
    page.addEventListener('input', (e) => {
      if (e.target.matches && e.target.matches('[data-sk-qty-input]')) {
        /* Defer to the existing debounce — we only fire after change/blur. */
      }
    });

    /* ── ML recommendations rail ─────────────────────────────────────
       On hydrate, fetch Shopify's recommendation engine output and
       swap the SSR seed with smarter, ML-curated picks. */
    initCartPageRecs();
  }

  /**
   * Fetch ML-driven product recommendations from Shopify's recommendations
   * endpoint and morph the SSR-seeded grid into the smarter result. The
   * rendered card markup matches the SSR shape so CSS hover states +
   * quick-add wiring continue to work without a re-init.
   */
  function initCartPageRecs() {
    const recs = document.querySelector('[data-sk-recs]');
    const grid = recs && recs.querySelector('[data-sk-recs-grid]');
    if (!recs || !grid) return;
    const seedId = recs.dataset.skRecSeed;
    if (!seedId) return;

    const url = '/recommendations/products.json?product_id=' + encodeURIComponent(seedId)
              + '&limit=6&intent=related';

    fetch(url, { headers: { 'Accept': 'application/json' } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (!data || !Array.isArray(data.products) || data.products.length === 0) return;

        const products = data.products.slice(0, 4);
        const cards = products.map((p, idx) => {
          const front = p.featured_image
            ? '<img class="sk-cart-pg__rec-img sk-cart-pg__rec-img--front" src="' + p.featured_image + '?width=480" alt="' + H.escape(p.title) + '" loading="lazy">'
            : '';
          const back = (p.images && p.images[1])
            ? '<img class="sk-cart-pg__rec-img sk-cart-pg__rec-img--back" src="' + p.images[1] + '?width=480" alt="" loading="lazy" aria-hidden="true">'
            : '';
          const onSale = p.compare_at_price_min && p.compare_at_price_min > p.price;
          const badge = onSale
            ? '<span class="sk-cart-pg__rec-badge sk-cart-pg__rec-badge--sale">Sale</span>'
            : (idx === 0 ? '<span class="sk-cart-pg__rec-badge">Top match</span>' : '');
          const wasPrice = onSale
            ? '<span class="sk-cart-pg__rec-was">' + fmtINR(p.compare_at_price_min) + '</span>'
            : '';
          const variantId = (p.variants && p.variants[0] && p.variants[0].id) || '';
          return (
            '<a class="sk-cart-pg__rec is-entering" href="' + H.escape(p.url) + '"' +
                ' data-sk-rec-card data-product-id="' + p.id + '" data-variant-id="' + variantId + '"' +
                ' style="--sk-rec-delay: ' + (idx * 60) + 'ms">' +
              '<div class="sk-cart-pg__rec-media">' +
                front + back + badge +
                (variantId ? (
                  '<button type="button" class="sk-cart-pg__rec-quickadd" data-sk-quick-add' +
                  ' data-variant-id="' + variantId + '" aria-label="Add ' + H.escape(p.title) + ' to bag">' +
                    '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
                      '<path d="M12 5v14M5 12h14" stroke-linecap="round"/>' +
                    '</svg>Add to bag' +
                  '</button>'
                ) : '') +
              '</div>' +
              '<div class="sk-cart-pg__rec-info">' +
                '<h3 class="sk-cart-pg__rec-title">' + H.escape(p.title) + '</h3>' +
                '<p class="sk-cart-pg__rec-price">' + fmtINR(p.price) + wasPrice + '</p>' +
              '</div>' +
            '</a>'
          );
        }).join('');

        grid.innerHTML = cards;
        // Drop the entering class on next frame for the slide-in animation.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            grid.querySelectorAll('.is-entering').forEach((el) => el.classList.remove('is-entering'));
          });
        });
        recs.classList.add('is-ml-loaded');
      })
      .catch(() => { /* SSR seed remains; silent failure. */ });
  }

  /* -- Browse-collections dropdown trigger ---------------------------------*/

  /**
   * Wire the empty-cart "Browse collections" button (and any other element
   * with [data-sk-open-collections-mega]) so it opens the navbar's
   * existing Collections mega-dropdown in place — no navigation, no
   * page reload. The mega panel is bound via Alpine.js to a `megaOpen`
   * state on the nav root; we set that state directly.
   *
   * Mobile (≤920px) doesn't have a hover dropdown, so we fall back to
   * opening the mobile drawer instead.
   *
   * Auto-close: any click outside the panel + collections nav item
   * resets `megaOpen` to null so the panel collapses cleanly.
   */
  function initOpenCollectionsMega() {
    if (window.__skOpenCollectionsMegaInit) return;
    window.__skOpenCollectionsMegaInit = true;

    /* Try to set Alpine's megaOpen state. Returns true on success. */
    function setMegaOpenState(value) {
      const item = document.querySelector('.sk-nav__item--collections');
      const root = item && item.closest('[x-data]');
      if (!root) return false;

      // Alpine 3 — direct $data access
      if (window.Alpine && typeof window.Alpine.$data === 'function') {
        try {
          const data = window.Alpine.$data(root);
          if (data && 'megaOpen' in data) {
            // Compute index of the collections <li> among its siblings
            // since the markup uses {{ forloop.index }} (1-based).
            const sibs = Array.from(root.querySelectorAll('.sk-nav__item'));
            data.megaOpen = value === null ? null : (sibs.indexOf(item) + 1);
            return true;
          }
        } catch (_) { /* fall through */ }
      }
      // Internal fallback for Alpine instances that don't expose $data
      const stack = root._x_dataStack;
      if (stack && stack[0] && 'megaOpen' in stack[0]) {
        const sibs = Array.from(root.querySelectorAll('.sk-nav__item'));
        stack[0].megaOpen = value === null ? null : (sibs.indexOf(item) + 1);
        return true;
      }
      return false;
    }

    /* Fallback: if Alpine state can't be touched, force-add the
       is-open class. We keep a guard so Alpine's reactive class
       binding can't immediately strip it during the same tick. */
    function forceOpenViaClass() {
      const mega = document.querySelector('.sk-mega--collections');
      if (!mega) return false;
      mega.classList.add('is-open');
      mega.dataset.skForcedOpen = '1';
      return true;
    }
    function clearForcedOpen() {
      const mega = document.querySelector('.sk-mega--collections');
      if (!mega) return;
      mega.classList.remove('is-open');
      delete mega.dataset.skForcedOpen;
    }

    /* Auto-close handler — installed once the panel is open, removed
       on first outside interaction. */
    function installAutoClose() {
      const mega = document.querySelector('.sk-mega--collections');
      const item = document.querySelector('.sk-nav__item--collections');
      if (!mega || !item) return;

      const onAway = (e) => {
        if (mega.contains(e.target) || item.contains(e.target)) return;
        if (!setMegaOpenState(null)) clearForcedOpen();
        document.removeEventListener('click', onAway, true);
        document.removeEventListener('keydown', onEsc, true);
      };
      const onEsc = (e) => {
        if (e.key !== 'Escape') return;
        if (!setMegaOpenState(null)) clearForcedOpen();
        document.removeEventListener('click', onAway, true);
        document.removeEventListener('keydown', onEsc, true);
      };

      // Defer attaching so the click that opened the panel doesn't
      // immediately close it.
      setTimeout(() => {
        document.addEventListener('click', onAway, true);
        document.addEventListener('keydown', onEsc, true);
      }, 0);
    }

    /* Public delegated handler. */
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-sk-open-collections-mega]');
      if (!trigger) return;
      e.preventDefault();

      const isMobile = window.matchMedia && window.matchMedia('(max-width: 920px)').matches;

      if (isMobile) {
        /* Mobile path: open the drawer; the drawer already has a
           Collections section. */
        const menuToggle = document.querySelector('[data-sk-menu-toggle]');
        if (menuToggle) {
          menuToggle.click();
          /* If the drawer has a Collections expander, scroll/expand it. */
          setTimeout(() => {
            const collExpander = document.querySelector('[data-sk-mobile-collections]');
            if (collExpander && typeof collExpander.click === 'function') collExpander.click();
          }, 320);
        } else {
          /* No drawer? Last-resort navigate. */
          window.location.href = '/collections';
        }
        return;
      }

      /* Desktop: smooth-scroll the user to the top so the navbar is
         in view, then open the mega panel. */
      const top = window.scrollY || window.pageYOffset || 0;
      if (top > 8) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

      /* Open the panel after the scroll begins. We retry a couple of
         times in case Alpine isn't quite ready yet. */
      let attempts = 0;
      const tryOpen = () => {
        if (setMegaOpenState(1)) {
          installAutoClose();
          return;
        }
        if (++attempts < 8) {
          setTimeout(tryOpen, 60);
          return;
        }
        // Alpine never came up. Force the class.
        if (forceOpenViaClass()) installAutoClose();
      };
      // 80ms gives the smooth-scroll a head start.
      setTimeout(tryOpen, 80);
    });
  }

  /* -- /collections list page filter ----------------------------------------*/

  /**
   * Live, instant client-side filter on the /collections list page.
   * Hash-synced so /collections#q=funky deep-links to a filtered view.
   * No external dependencies; uses only data-* attributes for matching.
   */
  function initCollistFilter() {
    const root = document.querySelector('[data-sk-collist]');
    if (!root || root.dataset.skCollistInit === '1') return;
    root.dataset.skCollistInit = '1';

    const input = root.querySelector('[data-sk-collist-input]');
    const clear = root.querySelectorAll('[data-sk-collist-clear]');
    const grid  = root.querySelector('[data-sk-collist-grid]');
    const empty = root.querySelector('[data-sk-collist-empty]');
    const emptyQ = root.querySelector('[data-sk-collist-empty-q]');
    const countEl = root.querySelector('[data-sk-collist-count]');
    if (!input || !grid) return;

    const items = Array.from(grid.querySelectorAll('[data-sk-collist-item]'));
    const total = items.length;

    const apply = (q) => {
      const needle = String(q || '').toLowerCase().trim();
      let shown = 0;
      items.forEach((it) => {
        const name = it.dataset.name || '';
        const match = needle === '' || name.includes(needle);
        it.classList.toggle('is-hidden', !match);
        if (match) shown += 1;
      });
      // Toggle empty state.
      if (empty) {
        empty.hidden = shown !== 0;
        if (emptyQ) emptyQ.textContent = needle ? '"' + needle + '"' : 'that';
      }
      // Update count chip.
      if (countEl) {
        const noun = shown === 1 ? 'collection' : 'collections';
        if (needle) {
          countEl.innerHTML = 'Showing <strong>' + shown + '</strong> of ' + total + ' ' + noun;
        } else {
          countEl.innerHTML = 'Showing <strong>' + total + '</strong> ' + noun;
        }
      }
      // Toggle the clear button.
      clear.forEach((b) => { b.hidden = needle.length === 0; });
    };

    /* Input → debounced apply + URL hash sync (#q=) so the filter is
       shareable and survives back/forward. */
    let t = null;
    input.addEventListener('input', () => {
      clearTimeout(t);
      t = setTimeout(() => {
        const q = input.value;
        apply(q);
        const hash = q ? '#q=' + encodeURIComponent(q) : '';
        if (hash !== window.location.hash) {
          history.replaceState(null, '', window.location.pathname + window.location.search + hash);
        }
      }, 80);
    });

    /* Clear buttons. */
    clear.forEach((b) => {
      b.addEventListener('click', () => {
        input.value = '';
        input.focus();
        apply('');
        history.replaceState(null, '', window.location.pathname + window.location.search);
      });
    });

    /* Hydrate from #q= on first paint. */
    const m = (window.location.hash || '').match(/(?:^|&)q=([^&]*)/);
    if (m && m[1]) {
      try {
        const q = decodeURIComponent(m[1]);
        input.value = q;
        apply(q);
      } catch (_) { /* ignore */ }
    }
  }

  /* -- Product reviews + ratings ------------------------------------------*/

  /**
   * Deterministic seeded RNG (mulberry32) — same seed → same sequence.
   * Used to generate stable mock reviews per product so a customer
   * sees the same reviews on every visit.
   */
  function skMulberry32(seed) {
    return function () {
      seed |= 0; seed = seed + 0x6D2B79F5 | 0;
      let t = Math.imul(seed ^ seed >>> 15, 1 | seed);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }
  function skHashCode(str) {
    let h = 0;
    for (let i = 0; i < str.length; i++) {
      h = ((h << 5) - h + str.charCodeAt(i)) | 0;
    }
    return Math.abs(h) || 1;
  }

  /* Reviewer name pool — global mix, weighted toward Indian names
     since this is an India-first brand. */
  const SK_REV_NAMES = [
    'Aarav S.', 'Anika R.', 'Arjun M.', 'Aanya K.', 'Vihaan T.', 'Priya N.',
    'Rohan P.', 'Riya S.', 'Karan G.', 'Sara D.', 'Aditya V.', 'Diya B.',
    'Ishaan C.', 'Kavya L.', 'Krish A.', 'Mira J.', 'Shaurya R.', 'Tanvi S.',
    'Vivaan H.', 'Zara K.', 'Ananya M.', 'Aryan N.', 'Devansh P.', 'Kyra T.',
    'Reyansh L.', 'Saanvi B.', 'Atharv G.', 'Myra C.', 'Ayaan H.', 'Prisha V.',
    'Ahaan R.', 'Pari D.', 'James W.', 'Olivia B.', 'Liam C.', 'Ava M.',
    'Ethan H.', 'Sophia P.', 'Mason K.', 'Isabella R.', 'Henry L.', 'Mia T.',
    'Lucas N.', 'Charlotte J.', 'Amelia G.', 'Logan B.'
  ];

  /* Review templates by rating bucket. Skewed positive — luxury fashion
     brand expectation. Tokens like {item} are replaced with the product
     title at render time so each review feels written for the piece. */
  const SK_REV_TEMPLATES = {
    5: [
      { title: 'Absolutely stunning fit', body: 'The {item} looks even better in person than in photos. Quality is unreal — fabric drapes beautifully, stitching is clean. Already eyeing my second one.' },
      { title: 'Best purchase this year', body: 'Fits exactly as described. Got compliments the very first day. The fabric feels premium and the cut is just right — not too boxy, not too fitted. SANKI delivers.' },
      { title: 'Worth every rupee', body: 'I was skeptical about ordering online but the {item} arrived in 3 days, packaged beautifully. Size is true. Material has weight to it. Highly recommend.' },
      { title: 'Better than my designer pieces', body: 'Honestly the construction on this rivals labels I\'ve paid 5x more for. The detailing is unreal. SANKI is doing something special — count me in for the next drop.' },
      { title: 'Statement piece, hands down', body: 'Wore the {item} to a friend\'s gathering and got asked where it\'s from at least 6 times. Falls perfectly. Comfort + style = this.' },
      { title: 'Streetwear done right', body: 'Finally a brand that gets the silhouette right. The {item} is exactly what was missing in my wardrobe. Premium feel, statement cut, hand-finished details.' },
      { title: 'Quality is on another level', body: 'Fabric, fit, finish — all on point. The colour is exactly as shown (I checked under multiple lights). Customer service was also great when I had a sizing question.' },
      { title: 'Love the small-runs ethos', body: 'Knowing this won\'t be on every other person makes it special. The {item} feels exclusive without screaming logo. Already saved my next pick to wishlist.' },
      { title: 'Fits like it was made for me', body: 'Universal sizing actually works. Tried it with multiple looks — works dressed up and dressed down. Fabric breathes well. Top-tier.' },
      { title: 'Hand-cut and it shows', body: 'You can feel the craftsmanship the moment you take it out of the package. Edges are clean, seams are tight. The Delhi atelier touch is real.' }
    ],
    4: [
      { title: 'Great piece, runs slightly large', body: 'Quality is on point and the colour is exactly as shown. Knock off half a star for sizing — I usually wear an M and this fits more like an L. Just size down if you\'re between sizes.' },
      { title: 'Beautiful but a touch pricey', body: 'No complaints on the quality at all. Material is plush, cut is great. Rating 4 because I think the price could be slightly more accessible — still buying again though.' },
      { title: 'Loved it, shipping was slow', body: 'The {item} itself is fantastic. Took 6 days to arrive — they advertised 3-7 so technically on time but I was hoping for the lower end. Worth the wait.' },
      { title: 'Fabric is great, colour slightly off', body: 'Photo showed a slightly deeper shade than what arrived. Still really happy with the piece — maybe more vibrant in person. Good value.' },
      { title: 'Almost perfect', body: 'Cut, fabric, finish — all top notch. The only nitpick is the inner label is a bit scratchy. Easy fix. Otherwise this is going into heavy rotation.' }
    ],
    3: [
      { title: 'Good, not great', body: 'It\'s fine. Fits okay, fabric is decent. Nothing about it really stands out though — at this price point I expected a bit more wow factor. Won\'t return but not sure I\'ll buy again.' },
      { title: 'Sizing chart could be clearer', body: 'Had to refer to reviews to figure out my size. Universal isn\'t exactly universal. Once I got the right one it\'s a solid piece — middling experience overall.' },
      { title: 'Pretty good for the price', body: 'Quality is matching the price tag — nothing extraordinary but nothing wrong either. Fabric is comfortable, design is alright. 3 stars feels fair.' }
    ],
    2: [
      { title: 'Expected better', body: 'The fabric is thinner than I anticipated based on the photos. Stitching is okay, design is okay. For the price I expected a little more.' }
    ],
    1: [
      { title: 'Not for me', body: 'Didn\'t fit well at all and the fabric isn\'t what I was hoping for. Reached out to support, they were responsive but the piece just isn\'t for me.' }
    ]
  };

  /* Distribution: 5★ ~ 65%, 4★ ~ 25%, 3★ ~ 7%, 2★ ~ 2%, 1★ ~ 1% */
  function pickRating(rng) {
    const r = rng();
    if (r < 0.65) return 5;
    if (r < 0.90) return 4;
    if (r < 0.97) return 3;
    if (r < 0.99) return 2;
    return 1;
  }

  /**
   * Generate a deterministic set of reviews for a product, seeded by
   * its id. Each review carries: id, rating, title, body, author,
   * verified, date (ISO), helpful (vote count).
   */
  /* Pros + cons pools — rotated per review for "Most loved features" cloud */
  const SK_REV_PROS = [
    'Comfortable', 'Stylish', 'True to size', 'Premium fabric', 'Great drape',
    'Compliments magnet', 'Quality stitching', 'Soft feel', 'Holds shape',
    'Statement piece', 'Versatile', 'Cool runs', 'Roomy fit', 'Detail-rich',
    'Photo-true colour', 'Worth the price', 'Wears in well'
  ];
  const SK_REV_CONS = [
    'Slightly tight', 'Runs warm', 'Wrinkles easily', 'Pricey', 'Took time to ship',
    'Size up advised', 'Colour slightly darker', 'Pockets shallow', 'Inner label scratchy'
  ];
  const SK_REV_HEIGHTS = ['5\'2"', '5\'4"', '5\'6"', '5\'7"', '5\'8"', '5\'9"', '5\'10"', '5\'11"', '6\'0"', '6\'1"'];
  const SK_REV_BUILDS = ['Slim', 'Athletic', 'Average', 'Heavy-set'];
  const SK_REV_SIZES_TOP = ['S', 'M', 'L', 'XL'];
  const SK_REV_SIZES_BOT = ['28', '30', '32', '34', '36'];
  const SK_REV_LOCATIONS = [
    'Mumbai, IN', 'Delhi, IN', 'Bengaluru, IN', 'Hyderabad, IN', 'Pune, IN',
    'Chennai, IN', 'Kolkata, IN', 'Ahmedabad, IN', 'Jaipur, IN', 'Chandigarh, IN',
    'Singapore', 'Dubai, UAE', 'London, UK', 'Toronto, CA', 'New York, US'
  ];
  /* Brand replies — picked for reviews flagged "featured" or notably high
     helpful counts. Sells real merchant-customer relationship. */
  const SK_REV_BRAND_REPLIES = [
    "Thanks for the love, {name}! Glad it landed exactly right — we're already cooking the next drop.",
    "Means everything, {name}. You picked one of our favourites this season too.",
    "{name}, this made our day. Save 10% on your next pick with code MEMBER10.",
    "Thank you for sharing, {name}! If you'd like a different fit next time, drop us a note on WhatsApp and we'll guide you.",
    "Appreciate the detailed feedback, {name}. We've passed it to the cut team for the next run."
  ];

  function skGenerateReviews(productId, productTitle, photoPool) {
    const seed = skHashCode('sanki:reviews:' + productId);
    const rng = skMulberry32(seed);
    const total = 32 + Math.floor(rng() * 90); /* 32 – 122 reviews */
    const out = [];
    const now = Date.now();
    const sixMonths = 1000 * 60 * 60 * 24 * 180;
    const itemNoun = (productTitle || 'piece').replace(/SANKI\s+/i, '').trim();
    /* Decide if this product has bottom-wear sizing or top-wear */
    const isBottom = /lower|trouser|jean|pant|short|denim|chino|cargo/i.test(productTitle || '');
    const sizes = isBottom ? SK_REV_SIZES_BOT : SK_REV_SIZES_TOP;
    /* Assign one or two "featured" reviews — ones that get a brand reply */
    const featuredIds = new Set();
    while (featuredIds.size < Math.min(2, Math.floor(total / 30))) {
      featuredIds.add(Math.floor(rng() * total));
    }

    for (let i = 0; i < total; i++) {
      const rating = pickRating(rng);
      const tplBucket = SK_REV_TEMPLATES[rating];
      const tpl = tplBucket[Math.floor(rng() * tplBucket.length)];
      const author = SK_REV_NAMES[Math.floor(rng() * SK_REV_NAMES.length)];
      const verified = rng() > 0.16;
      const ageMs = Math.floor(rng() * sixMonths);
      const date = new Date(now - ageMs);
      /* Helpful counts are biased: positive reviews collect more votes */
      const helpfulBase = rating >= 4 ? 18 : rating === 3 ? 6 : 3;
      const helpful = helpfulBase + Math.floor(rng() * 80);

      /* Fit signal — 5★/4★ trend toward true-to-size, lower ratings
         lean to ill-fit. -2..+2 scale. */
      let fit;
      const fitR = rng();
      if (rating >= 4) {
        if (fitR < 0.62) fit = 0;
        else if (fitR < 0.80) fit = -1;
        else if (fitR < 0.92) fit = 1;
        else if (fitR < 0.97) fit = -2;
        else fit = 2;
      } else {
        if (fitR < 0.30) fit = 0;
        else if (fitR < 0.55) fit = -1;
        else if (fitR < 0.78) fit = 1;
        else if (fitR < 0.90) fit = -2;
        else fit = 2;
      }

      /* Quality + Value scores — also biased per overall rating */
      const quality = Math.max(1, Math.min(5,
        Math.round(rating + (rng() < 0.7 ? 0 : (rng() < 0.5 ? -1 : 0)))
      ));
      const value = Math.max(1, Math.min(5,
        Math.round(rating - (rng() < 0.6 ? 0 : 1) + (rng() < 0.2 ? 1 : 0))
      ));

      /* Recommend — derived from rating */
      const recommend = rating >= 4 ? true : (rating === 3 ? rng() < 0.3 : false);

      /* Pros / cons — pick 2-3 each, biased to the rating bucket */
      const prosCount = rating >= 4 ? 2 + Math.floor(rng() * 2) : 1 + Math.floor(rng() * 2);
      const consCount = rating <= 3 ? 1 + Math.floor(rng() * 2) : (rng() < 0.25 ? 1 : 0);
      const pros = pickN(rng, SK_REV_PROS, prosCount);
      const cons = pickN(rng, SK_REV_CONS, consCount);

      /* Photos — ~28% of reviews include 1-2 photos pulled from the
         product's media. Higher-rated reviews are likelier to include. */
      const wantPhotos = (rating >= 4 ? rng() < 0.32 : rng() < 0.10);
      const photos = (wantPhotos && Array.isArray(photoPool) && photoPool.length)
        ? pickN(rng, photoPool, 1 + Math.floor(rng() * 2))
        : [];

      /* Reviewer fit context — included on ~75% of reviews */
      const includeProfile = rng() < 0.75;
      const profile = includeProfile ? {
        size: sizes[Math.floor(rng() * sizes.length)],
        height: SK_REV_HEIGHTS[Math.floor(rng() * SK_REV_HEIGHTS.length)],
        build: SK_REV_BUILDS[Math.floor(rng() * SK_REV_BUILDS.length)],
        location: SK_REV_LOCATIONS[Math.floor(rng() * SK_REV_LOCATIONS.length)]
      } : null;

      /* Brand reply on featured reviews + occasionally on 4★ feedback */
      let brandReply = null;
      if (featuredIds.has(i) || (rating === 4 && rng() < 0.18)) {
        const replyTpl = SK_REV_BRAND_REPLIES[Math.floor(rng() * SK_REV_BRAND_REPLIES.length)];
        brandReply = {
          author: 'Sanki Team',
          body: replyTpl.replace(/\{name\}/g, author.split(' ')[0]),
          date: new Date(date.getTime() + 86400000 * (1 + Math.floor(rng() * 4))).toISOString()
        };
      }

      out.push({
        id: 'rev_' + productId + '_' + i,
        rating: rating,
        title: tpl.title,
        body: tpl.body.replace(/\{item\}/g, itemNoun),
        author: author,
        verified: verified,
        date: date.toISOString(),
        dateLabel: skFormatRelDate(date),
        helpful: helpful,
        fit: fit,
        quality: quality,
        value: value,
        recommend: recommend,
        pros: pros,
        cons: cons,
        photos: photos,
        profile: profile,
        brandReply: brandReply,
        featured: featuredIds.has(i)
      });
    }
    return out;
  }

  /* Helper — pick N unique items from a pool using the seeded RNG */
  function pickN(rng, pool, n) {
    if (n <= 0 || !pool.length) return [];
    const copy = pool.slice();
    const out = [];
    const max = Math.min(n, copy.length);
    for (let i = 0; i < max; i++) {
      const idx = Math.floor(rng() * copy.length);
      out.push(copy[idx]);
      copy.splice(idx, 1);
    }
    return out;
  }

  function skFormatRelDate(date) {
    const diff = Date.now() - date.getTime();
    const day = 1000 * 60 * 60 * 24;
    const days = Math.floor(diff / day);
    if (days < 1) return 'Today';
    if (days < 2) return 'Yesterday';
    if (days < 7) return days + ' days ago';
    if (days < 30) return Math.floor(days / 7) + ' weeks ago';
    if (days < 365) return Math.floor(days / 30) + ' months ago';
    return Math.floor(days / 365) + ' years ago';
  }

  function skAggregateReviews(reviews) {
    const total = reviews.length;
    if (total === 0) return { total: 0, avg: 0, dist: [0, 0, 0, 0, 0] };
    let sum = 0;
    const dist = [0, 0, 0, 0, 0];
    reviews.forEach((r) => { sum += r.rating; dist[r.rating - 1]++; });
    return { total, avg: sum / total, dist };
  }

  /**
   * Render synthetic-mode rating stars across the page (PDP eyebrow,
   * product cards, recommendation rail). Reads `data-sk-rating-product-id`
   * and computes the aggregate from synthesized reviews + any user-
   * submitted ones in localStorage.
   */
  function initRatingStars() {
    document.querySelectorAll('[data-sk-rating-synthetic="1"]').forEach((wrap) => {
      if (wrap.__skRatingFilled) return;
      wrap.__skRatingFilled = true;
      const pid = wrap.dataset.skRatingProductId;
      if (!pid) return;
      const reviews = skLoadAllReviews(pid, '', []);
      const agg = skAggregateReviews(reviews);
      if (!agg.total) {
        wrap.hidden = true;
        return;
      }
      const fill = wrap.querySelector('.sk-rating__fill');
      if (fill) fill.style.width = ((agg.avg / 5) * 100).toFixed(1) + '%';
      const countEl = wrap.querySelector('[data-sk-rating-count]');
      if (countEl) countEl.textContent = '(' + agg.total + ')';
      wrap.setAttribute('aria-label',
        agg.avg.toFixed(1) + ' out of 5 stars · ' + agg.total + ' reviews');
    });
  }

  /* User reviews persistence — saved per product in localStorage */
  function skLoadUserReviews(pid) {
    try {
      const raw = localStorage.getItem('sanki:reviews:user:' + pid);
      return raw ? JSON.parse(raw) : [];
    } catch (_) { return []; }
  }
  function skSaveUserReview(pid, review) {
    const list = skLoadUserReviews(pid);
    list.unshift(review);
    try { localStorage.setItem('sanki:reviews:user:' + pid, JSON.stringify(list)); }
    catch (_) {}
  }

  /* Helpful votes persisted as a Set in localStorage */
  function skLoadHelpfulVotes() {
    try {
      const raw = localStorage.getItem('sanki:reviews:helpful');
      return new Set(raw ? JSON.parse(raw) : []);
    } catch (_) { return new Set(); }
  }
  function skSaveHelpfulVotes(set) {
    try { localStorage.setItem('sanki:reviews:helpful', JSON.stringify(Array.from(set))); }
    catch (_) {}
  }

  function skLoadAllReviews(pid, title, photoPool) {
    /* Reviews must come from an order-verified, server-backed provider.
       Synthetic and browser-local reviews are intentionally disabled. */
    return [];
  }

  /**
   * Aggregate fit / quality / value / recommend across reviews.
   */
  function skAggregateRich(reviews) {
    const total = reviews.length;
    if (total === 0) return { total: 0, avg: 0, dist: [0,0,0,0,0], fit: 0, quality: 0, value: 0, recommendPct: 0 };
    let sum = 0, fit = 0, q = 0, v = 0, rec = 0;
    const dist = [0, 0, 0, 0, 0];
    reviews.forEach((r) => {
      sum += r.rating;
      dist[r.rating - 1]++;
      fit += (r.fit || 0);
      q += (r.quality || r.rating || 0);
      v += (r.value || r.rating || 0);
      if (r.recommend) rec++;
    });
    return {
      total,
      avg: sum / total,
      dist,
      fit: fit / total,        // -2..+2
      quality: q / total,      // 1..5
      value: v / total,        // 1..5
      recommendPct: Math.round((rec / total) * 100)
    };
  }

  /**
   * Compute the top-5 most-mentioned pros across reviews so we can
   * surface a "Most loved" tag cloud above the review list.
   */
  function skTopFeatures(reviews, n) {
    const counts = Object.create(null);
    reviews.forEach((r) => {
      (r.pros || []).forEach((p) => { counts[p] = (counts[p] || 0) + 1; });
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, n || 5);
  }

  /**
   * Reviews v2 — full hybrid UI on the PDP. Reads everything off
   * `[data-sk-reviews]` then drives all the panels in lockstep.
   */
  function initProductReviews() {
    const root = document.querySelector('[data-sk-reviews]');
    if (!root || root.dataset.skInit === '1') return;
    root.dataset.skInit = '1';

    const pid    = root.dataset.skProductId;
    const title  = root.dataset.skProductTitle || '';
    let photos = [];
    try { photos = JSON.parse(root.dataset.skProductPhotos || '[]') || []; }
    catch (_) { photos = []; }

    const list      = root.querySelector('[data-sk-reviews-list]');
    const moreBtn   = root.querySelector('[data-sk-reviews-more]');
    const exhausted = root.querySelector('[data-sk-reviews-exhausted]');
    const emptyEl   = root.querySelector('[data-sk-reviews-empty]');
    const sortSel   = root.querySelector('[data-sk-reviews-sort]');
    const filters   = Array.from(root.querySelectorAll('[data-sk-reviews-filter]'));
    const flagBtns  = Array.from(root.querySelectorAll('[data-sk-reviews-flag]'));
    const distFills = root.querySelectorAll('[data-sk-dist-fill]');
    const distCounts = root.querySelectorAll('[data-sk-dist-count]');
    const avgEl     = root.querySelector('[data-sk-reviews-avg]');
    const totalEl   = root.querySelector('[data-sk-reviews-total]');
    const avgStars  = root.querySelector('[data-sk-reviews-avg-stars] .sk-rating__fill');
    const recPctEl  = root.querySelector('[data-sk-reviews-recommend]');
    const fitMarker = root.querySelector('[data-sk-slider-fit]');
    const qFill     = root.querySelector('[data-sk-slider-quality]');
    const qNum      = root.querySelector('[data-sk-slider-quality-num]');
    const vFill     = root.querySelector('[data-sk-slider-value]');
    const vNum      = root.querySelector('[data-sk-slider-value-num]');
    const featuresList = root.querySelector('[data-sk-reviews-features]');
    const photosWrap = root.querySelector('[data-sk-reviews-photos]');
    const photosGrid = root.querySelector('[data-sk-reviews-photos-grid]');
    const photosCount = root.querySelector('[data-sk-reviews-photos-count]');
    const featuredEl = root.querySelector('[data-sk-reviews-featured]');
    const writeBtns = Array.from(root.querySelectorAll('[data-sk-reviews-write]'));
    const modal     = root.querySelector('[data-sk-reviews-modal]');
    const modalCloseBtns = Array.from(root.querySelectorAll('[data-sk-reviews-modal-close]'));
    const form      = root.querySelector('[data-sk-reviews-form]');
    const lightbox  = root.querySelector('[data-sk-reviews-lightbox]');
    const lightboxImg = root.querySelector('[data-sk-reviews-lightbox-img]');
    const lightboxClose = root.querySelector('[data-sk-reviews-lightbox-close]');

    let allReviews = skLoadAllReviews(pid, title, photos);
    if (!allReviews.length) {
      root.hidden = true;
      return;
    }
    let filterRating = 0;
    let flagPhotos = false;
    let flagVerified = false;
    let sortKey = 'helpful';
    let visibleCount = 5;
    const helpfulSet = skLoadHelpfulVotes();

    /* ─── Aggregate panel render ───────────────────────────── */
    function renderAggregate() {
      const agg = skAggregateRich(allReviews);
      if (avgEl) avgEl.textContent = agg.total ? agg.avg.toFixed(1) : '—';
      if (totalEl) totalEl.textContent = agg.total;
      if (avgStars) avgStars.style.width = ((agg.avg / 5) * 100).toFixed(1) + '%';
      if (recPctEl) recPctEl.textContent = agg.recommendPct + '%';

      const max = Math.max(1, Math.max.apply(null, agg.dist));
      distFills.forEach((el) => {
        const n = parseInt(el.dataset.skDistFill, 10);
        const count = agg.dist[n - 1] || 0;
        const pct = (count / max) * 100;
        el.style.width = pct + '%';
      });
      distCounts.forEach((el) => {
        const n = parseInt(el.dataset.skDistCount, 10);
        el.textContent = (agg.dist[n - 1] || 0);
      });

      /* Fit marker — map -2..+2 to 0..100% */
      if (fitMarker) {
        const left = ((agg.fit + 2) / 4) * 100;
        fitMarker.style.left = Math.max(2, Math.min(98, left)) + '%';
      }
      /* Quality + Value fills — map 1..5 to 0..100% */
      if (qFill) qFill.style.width = ((agg.quality / 5) * 100).toFixed(1) + '%';
      if (qNum)  qNum.textContent  = agg.quality.toFixed(1);
      if (vFill) vFill.style.width = ((agg.value / 5) * 100).toFixed(1) + '%';
      if (vNum)  vNum.textContent  = agg.value.toFixed(1);

      /* Top features cloud */
      if (featuresList) {
        const top = skTopFeatures(allReviews, 6);
        featuresList.innerHTML = top.map(([tag, count]) => (
          '<li><button type="button" class="sk-reviews__feature-pill">' +
            H.escape(tag) +
            ' <span>' + count + '</span>' +
          '</button></li>'
        )).join('');
      }
    }

    /* ─── Photo strip ─────────────────────────────────────── */
    function renderPhotoStrip() {
      if (!photosWrap || !photosGrid) return;
      const all = [];
      allReviews.forEach((r) => {
        (r.photos || []).forEach((src) => {
          all.push({ src, reviewId: r.id, author: r.author });
        });
      });
      if (all.length === 0) { photosWrap.hidden = true; return; }
      photosWrap.hidden = false;
      if (photosCount) photosCount.textContent = all.length;
      const visible = all.slice(0, 8);
      const overflow = all.length - visible.length;
      photosGrid.innerHTML = visible.map((p, i) => (
        '<li>' +
          '<button type="button" class="sk-reviews__photo-thumb"' +
            ' data-sk-photo-thumb="' + i + '">' +
            '<img src="' + p.src + '" alt="Review by ' + H.escape(p.author) + '" loading="lazy">' +
            (i === visible.length - 1 && overflow > 0
              ? '<span class="sk-reviews__photo-more">+' + overflow + '</span>'
              : '') +
          '</button>' +
        '</li>'
      )).join('');
      photosGrid.querySelectorAll('[data-sk-photo-thumb]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const idx = parseInt(btn.dataset.skPhotoThumb, 10);
          openLightbox(visible[idx].src);
        });
      });
    }

    /* ─── Lightbox ────────────────────────────────────────── */
    function openLightbox(src) {
      if (!lightbox || !lightboxImg) return;
      lightboxImg.src = src;
      lightbox.hidden = false;
      requestAnimationFrame(() => lightbox.classList.add('is-open'));
    }
    function closeLightbox() {
      if (!lightbox) return;
      lightbox.classList.remove('is-open');
      setTimeout(() => { lightbox.hidden = true; lightboxImg.src = ''; }, 240);
    }
    if (lightboxClose) lightboxClose.addEventListener('click', closeLightbox);
    if (lightbox) {
      lightbox.addEventListener('click', (e) => {
        if (e.target === lightbox) closeLightbox();
      });
    }
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && lightbox && !lightbox.hidden) closeLightbox();
    });

    /* ─── Filtering + sorting ─────────────────────────────── */
    function applyFilterSort() {
      let data = allReviews.slice();
      if (filterRating > 0) data = data.filter((r) => r.rating === filterRating);
      if (flagPhotos)       data = data.filter((r) => Array.isArray(r.photos) && r.photos.length > 0);
      if (flagVerified)     data = data.filter((r) => r.verified);
      switch (sortKey) {
        case 'recent':  data.sort((a, b) => new Date(b.date) - new Date(a.date)); break;
        case 'high':    data.sort((a, b) => b.rating - a.rating || (b.helpful - a.helpful)); break;
        case 'low':     data.sort((a, b) => a.rating - b.rating || (b.helpful - a.helpful)); break;
        case 'photos':  data.sort((a, b) =>
                          ((b.photos || []).length - (a.photos || []).length) ||
                          ((b.helpful + (helpfulSet.has(b.id) ? 1 : 0)) - (a.helpful + (helpfulSet.has(a.id) ? 1 : 0)))
                        ); break;
        default:        data.sort((a, b) =>
                          (b.helpful + (helpfulSet.has(b.id) ? 1 : 0)) -
                          (a.helpful + (helpfulSet.has(a.id) ? 1 : 0))
                        );
      }
      return data;
    }

    /* ─── Render: featured + list ─────────────────────────── */
    function renderFeatured() {
      if (!featuredEl) return;
      /* Show featured only when no filter is active */
      const filterActive = filterRating > 0 || flagPhotos || flagVerified;
      if (filterActive) { featuredEl.hidden = true; featuredEl.innerHTML = ''; return; }
      const fr = allReviews
        .filter((r) => r.featured && !r.userSubmitted)
        .sort((a, b) => b.helpful - a.helpful)[0];
      if (!fr) { featuredEl.hidden = true; featuredEl.innerHTML = ''; return; }
      featuredEl.hidden = false;
      featuredEl.innerHTML = renderReviewHTML(fr, true);
      wireListInteractions(featuredEl);
    }

    function renderList() {
      const data = applyFilterSort();
      if (data.length === 0) {
        list.innerHTML = '';
        if (emptyEl) emptyEl.hidden = false;
        if (moreBtn) moreBtn.hidden = true;
        if (exhausted) exhausted.hidden = true;
        return;
      }
      if (emptyEl) emptyEl.hidden = true;
      const slice = data.slice(0, visibleCount);
      list.innerHTML = slice.map((r) => renderReviewHTML(r, false)).join('');
      if (moreBtn) moreBtn.hidden = visibleCount >= data.length;
      if (exhausted) exhausted.hidden = visibleCount < data.length;
      wireListInteractions(list);
    }

    function renderReviewHTML(r, isFeatured) {
      const initials = (r.author || '?').split(' ').map((s) => s[0]).slice(0, 2).join('');
      const pct = ((r.rating / 5) * 100).toFixed(0);
      const isHelpful = helpfulSet.has(r.id);
      const helpfulCount = r.helpful + (isHelpful ? 1 : 0);
      const isUser = r.userSubmitted ? '<span class="sk-reviews__pill sk-reviews__pill--you">You</span>' : '';
      const featuredPill = isFeatured ? '<span class="sk-reviews__pill sk-reviews__pill--featured">★ Featured</span>' : '';
      const verified = r.verified
        ? '<span class="sk-reviews__pill sk-reviews__pill--verified">' +
            '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true">' +
              '<path d="M5 12l4 4 10-10" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg> Verified buyer</span>'
        : '';
      const recommendPill = r.recommend
        ? '<span class="sk-reviews__pill sk-reviews__pill--rec">👍 Recommends</span>'
        : '';

      let profileHTML = '';
      if (r.profile) {
        const pieces = [];
        if (r.profile.size)     pieces.push('<span><strong>Size:</strong> ' + H.escape(r.profile.size) + '</span>');
        if (r.profile.height)   pieces.push('<span><strong>Height:</strong> ' + H.escape(r.profile.height) + '</span>');
        if (r.profile.build)    pieces.push('<span><strong>Build:</strong> ' + H.escape(r.profile.build) + '</span>');
        if (r.profile.location) pieces.push('<span>📍 ' + H.escape(r.profile.location) + '</span>');
        if (pieces.length) {
          profileHTML = '<dl class="sk-reviews__item-profile">' + pieces.join('') + '</dl>';
        }
      }

      let fitHTML = '';
      if (typeof r.fit === 'number') {
        const fitLabels = { '-2': 'Way too small', '-1': 'Slightly small', '0': 'True to size', '1': 'Slightly large', '2': 'Way too large' };
        const left = ((r.fit + 2) / 4) * 100;
        fitHTML = (
          '<div class="sk-reviews__item-fit">' +
            '<span class="sk-reviews__item-fit-label">Fit</span>' +
            '<span class="sk-reviews__item-fit-track">' +
              '<span class="sk-reviews__item-fit-marker" style="left:' + left + '%"></span>' +
            '</span>' +
            '<span class="sk-reviews__item-fit-value">' + (fitLabels[String(r.fit)] || 'True to size') + '</span>' +
          '</div>'
        );
      }

      let prosHTML = '';
      if (r.pros && r.pros.length) {
        prosHTML = '<div class="sk-reviews__item-tags sk-reviews__item-tags--pros">' +
          '<span class="sk-reviews__item-tag-label">+ Pros</span>' +
          r.pros.map((p) => '<span class="sk-reviews__item-tag">' + H.escape(p) + '</span>').join('') +
        '</div>';
      }
      let consHTML = '';
      if (r.cons && r.cons.length) {
        consHTML = '<div class="sk-reviews__item-tags sk-reviews__item-tags--cons">' +
          '<span class="sk-reviews__item-tag-label">– Cons</span>' +
          r.cons.map((c) => '<span class="sk-reviews__item-tag">' + H.escape(c) + '</span>').join('') +
        '</div>';
      }

      let photosHTML = '';
      if (r.photos && r.photos.length) {
        photosHTML = '<ul class="sk-reviews__item-photos" role="list">' +
          r.photos.map((src, i) => (
            '<li><button type="button" class="sk-reviews__item-photo"' +
              ' data-sk-item-photo="' + H.escape(src) + '"' +
              ' aria-label="View customer photo">' +
              '<img src="' + src + '" alt="" loading="lazy">' +
            '</button></li>'
          )).join('') +
        '</ul>';
      }

      let replyHTML = '';
      if (r.brandReply) {
        replyHTML = (
          '<div class="sk-reviews__reply">' +
            '<span class="sk-reviews__reply-avatar" aria-hidden="true">SK</span>' +
            '<div class="sk-reviews__reply-body">' +
              '<header class="sk-reviews__reply-head">' +
                '<strong>' + H.escape(r.brandReply.author) + '</strong>' +
                '<span class="sk-reviews__pill sk-reviews__pill--brand">Brand</span>' +
                '<time class="sk-reviews__reply-date" datetime="' + r.brandReply.date + '">' +
                  skFormatRelDate(new Date(r.brandReply.date)) +
                '</time>' +
              '</header>' +
              '<p>' + H.escape(r.brandReply.body) + '</p>' +
            '</div>' +
          '</div>'
        );
      }

      const cls = 'sk-reviews__item' + (isFeatured ? ' sk-reviews__item--featured' : '');
      return (
        '<li class="' + cls + '" data-sk-review-id="' + H.escape(r.id) + '">' +
          '<div class="sk-reviews__avatar" aria-hidden="true">' + H.escape(initials.toUpperCase()) + '</div>' +
          '<div class="sk-reviews__body">' +
            '<header class="sk-reviews__item-head">' +
              '<div class="sk-reviews__item-meta">' +
                '<strong class="sk-reviews__item-author">' + H.escape(r.author) + '</strong>' +
                isUser + featuredPill + verified + recommendPill +
              '</div>' +
              '<time class="sk-reviews__item-date" datetime="' + r.date + '">' + r.dateLabel + '</time>' +
            '</header>' +
            '<div class="sk-reviews__item-rating" aria-label="Rated ' + r.rating + ' out of 5">' +
              '<span class="sk-rating__stars" aria-hidden="true">★★★★★' +
                '<span class="sk-rating__fill" style="width:' + pct + '%">★★★★★</span>' +
              '</span>' +
            '</div>' +
            '<h3 class="sk-reviews__item-title">' + H.escape(r.title) + '</h3>' +
            '<p class="sk-reviews__item-text">' + H.escape(r.body) + '</p>' +
            profileHTML +
            fitHTML +
            (prosHTML || consHTML
              ? '<div class="sk-reviews__item-prosconsrow">' + prosHTML + consHTML + '</div>'
              : '') +
            photosHTML +
            replyHTML +
            '<footer class="sk-reviews__item-foot">' +
              '<button type="button" class="sk-reviews__helpful' + (isHelpful ? ' is-active' : '') + '"' +
                ' data-sk-reviews-helpful aria-pressed="' + (isHelpful ? 'true' : 'false') + '">' +
                '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">' +
                  '<path d="M7 22V11l5-9 1 1c.5.5.5 1.5.3 2L11 11h7a3 3 0 0 1 3 3l-2 5a3 3 0 0 1-3 2H7z M3 11h4v11H3z" stroke-linejoin="round"/>' +
                '</svg>' +
                'Helpful · <span data-sk-helpful-count>' + helpfulCount + '</span>' +
              '</button>' +
            '</footer>' +
          '</div>' +
        '</li>'
      );
    }

    function wireListInteractions(scope) {
      scope.querySelectorAll('[data-sk-reviews-helpful]').forEach((btn) => {
        if (btn.__skWired) return;
        btn.__skWired = true;
        btn.addEventListener('click', () => {
          const item = btn.closest('[data-sk-review-id]');
          if (!item) return;
          const id = item.dataset.skReviewId;
          if (helpfulSet.has(id)) {
            helpfulSet.delete(id);
            btn.classList.remove('is-active');
            btn.setAttribute('aria-pressed', 'false');
          } else {
            helpfulSet.add(id);
            btn.classList.add('is-active');
            btn.setAttribute('aria-pressed', 'true');
          }
          skSaveHelpfulVotes(helpfulSet);
          const review = allReviews.find((r) => r.id === id);
          if (review) {
            const countEl = btn.querySelector('[data-sk-helpful-count]');
            if (countEl) countEl.textContent = review.helpful + (helpfulSet.has(id) ? 1 : 0);
          }
        });
      });
      scope.querySelectorAll('[data-sk-item-photo]').forEach((btn) => {
        if (btn.__skPhotoWired) return;
        btn.__skPhotoWired = true;
        btn.addEventListener('click', () => openLightbox(btn.dataset.skItemPhoto));
      });
    }

    /* Filter chips (rating + dist rows) */
    filters.forEach((f) => {
      f.addEventListener('click', () => {
        const r = parseInt(f.dataset.rating, 10) || 0;
        filterRating = r;
        root.querySelectorAll('.sk-reviews__chip').forEach((x) =>
          x.classList.toggle('is-active', parseInt(x.dataset.rating, 10) === r));
        visibleCount = 5;
        renderFeatured();
        renderList();
      });
    });

    /* Toggle chips — With photos / Verified only */
    flagBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const flag = btn.dataset.flag;
        if (flag === 'photos')   flagPhotos = !flagPhotos;
        if (flag === 'verified') flagVerified = !flagVerified;
        btn.classList.toggle('is-active');
        visibleCount = 5;
        renderFeatured();
        renderList();
      });
    });

    /* Sort */
    if (sortSel) sortSel.addEventListener('change', () => {
      sortKey = sortSel.value;
      visibleCount = 5;
      renderList();
    });

    /* Pagination */
    if (moreBtn) moreBtn.addEventListener('click', () => {
      visibleCount += 5;
      renderList();
    });

    /* Reset filters from empty state */
    const clearBtn = root.querySelector('[data-sk-reviews-clear]');
    if (clearBtn) clearBtn.addEventListener('click', () => {
      filterRating = 0;
      flagPhotos = false;
      flagVerified = false;
      filters.forEach((x) => x.classList.toggle('is-active', parseInt(x.dataset.rating, 10) === 0));
      flagBtns.forEach((b) => b.classList.remove('is-active'));
      visibleCount = 5;
      renderFeatured();
      renderList();
    });

    /* Modal open/close */
    function openModal() {
      if (!modal) return;
      modal.hidden = false;
      requestAnimationFrame(() => modal.classList.add('is-open'));
      document.body.style.overflow = 'hidden';
    }
    function closeModal() {
      if (!modal) return;
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
      setTimeout(() => { modal.hidden = true; }, 280);
    }
    writeBtns.forEach((b) => b.addEventListener('click', openModal));
    modalCloseBtns.forEach((b) => b.addEventListener('click', closeModal));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal && !modal.hidden) closeModal();
    });

    /* Star-picker label reflection */
    const rateText = root.querySelector('[data-sk-form-rate-text]');
    const RATE_LABELS = ['', 'Hated it', 'Disappointed', "It's OK", 'Loved it', 'Obsessed'];
    root.querySelectorAll('.sk-reviews__star-picker input').forEach((inp) => {
      inp.addEventListener('change', () => {
        if (rateText) rateText.textContent = RATE_LABELS[parseInt(inp.value, 10)] || '';
      });
    });

    /* Body counter */
    const counter = root.querySelector('[data-sk-form-counter]');
    const bodyTA = form && form.querySelector('textarea[name="body"]');
    if (counter && bodyTA) {
      const refresh = () => { counter.textContent = bodyTA.value.length + ' / 800'; };
      bodyTA.addEventListener('input', refresh);
      refresh();
    }

    /* Photo "upload" preview — mock; reads files but doesn't upload.
       Stores object URLs that work for the lifetime of the page. */
    const photoInput = form && form.querySelector('[data-sk-form-photos]');
    const photoZone  = form && form.querySelector('[data-sk-form-photos-zone]');
    const photoPrev  = form && form.querySelector('[data-sk-form-photos-preview]');
    const stagedPhotoUrls = [];
    function refreshStagedPreview() {
      if (!photoPrev) return;
      photoPrev.innerHTML = stagedPhotoUrls.map((url, i) => (
        '<li><img src="' + url + '" alt=""><button type="button" data-rm="' + i + '" aria-label="Remove">×</button></li>'
      )).join('');
      photoPrev.querySelectorAll('button[data-rm]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const idx = parseInt(btn.dataset.rm, 10);
          stagedPhotoUrls.splice(idx, 1);
          refreshStagedPreview();
        });
      });
    }
    if (photoInput) {
      photoInput.addEventListener('change', () => {
        const files = Array.from(photoInput.files || []);
        files.slice(0, 4 - stagedPhotoUrls.length).forEach((file) => {
          if (!/^image\//.test(file.type)) return;
          if (file.size > 5 * 1024 * 1024) return;
          stagedPhotoUrls.push(URL.createObjectURL(file));
        });
        refreshStagedPreview();
      });
    }
    if (photoZone && photoInput) {
      ['dragenter','dragover'].forEach((ev) => photoZone.addEventListener(ev, (e) => {
        e.preventDefault(); e.stopPropagation();
        photoZone.classList.add('is-dragover');
      }));
      ['dragleave','drop'].forEach((ev) => photoZone.addEventListener(ev, (e) => {
        e.preventDefault(); e.stopPropagation();
        photoZone.classList.remove('is-dragover');
      }));
      photoZone.addEventListener('drop', (e) => {
        const files = Array.from(e.dataTransfer.files || []);
        files.slice(0, 4 - stagedPhotoUrls.length).forEach((file) => {
          if (!/^image\//.test(file.type)) return;
          if (file.size > 5 * 1024 * 1024) return;
          stagedPhotoUrls.push(URL.createObjectURL(file));
        });
        refreshStagedPreview();
      });
    }

    /* Form submit — persist + render. */
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        if (!form.checkValidity()) { form.reportValidity(); return; }
        const data = new FormData(form);
        const review = {
          id: 'rev_user_' + pid + '_' + Date.now(),
          rating: parseInt(data.get('rating'), 10) || 5,
          title: String(data.get('title') || '').trim(),
          body: String(data.get('body') || '').trim(),
          author: String(data.get('author') || 'You').trim(),
          verified: false,
          userSubmitted: true,
          date: new Date().toISOString(),
          dateLabel: 'Just now',
          helpful: 0,
          fit: parseInt(data.get('fit') || '0', 10),
          quality: parseInt(data.get('quality') || '5', 10),
          value: parseInt(data.get('value') || '5', 10),
          recommend: data.get('recommend') === 'yes',
          pros: [],
          cons: [],
          photos: stagedPhotoUrls.slice(),
          profile: {
            size: String(data.get('size_bought') || '').trim(),
            height: String(data.get('height') || '').trim(),
            build: String(data.get('build') || '').trim(),
            location: ''
          },
          brandReply: null,
          featured: false
        };
        skSaveUserReview(pid, review);
        allReviews = skLoadAllReviews(pid, title, photos);
        renderAggregate();
        renderPhotoStrip();
        filterRating = 0;
        flagPhotos = false;
        flagVerified = false;
        sortKey = 'recent';
        if (sortSel) sortSel.value = 'recent';
        flagBtns.forEach((b) => b.classList.remove('is-active'));
        filters.forEach((x) => x.classList.toggle('is-active', parseInt(x.dataset.rating, 10) === 0));
        visibleCount = 5;
        renderFeatured();
        renderList();
        form.reset();
        stagedPhotoUrls.length = 0;
        refreshStagedPreview();
        if (rateText) rateText.textContent = 'Tap a star to rate';
        closeModal();
        if (H.toast) H.toast('Thanks — your review is posted.');
      });
    }

    /* Initial render */
    renderAggregate();
    renderPhotoStrip();
    renderFeatured();
    renderList();
  }

  /* -- Hybrid checkout review page (/pages/checkout) -----------------------*/

  /**
   * Branded checkout-review page initialiser.
   *
   * Captures contact + shipping + delivery + payment intent in our
   * own UI, persists everything as Shopify cart attributes via
   * `/cart/update.js`, then submits to Shopify's secure `/checkout`
   * endpoint with the data attached. Shopify reads the attributes on
   * the order so the merchant sees them in admin.
   *
   * Live behaviour:
   *   • PIN code → ~1s after the user types 6 digits, ping
   *     api.postalpincode.in to auto-fill City + State (free, no key).
   *   • Delivery method radio → recompute summary shipping / total live.
   *   • Payment method radio → just persisted as an attribute (real
   *     transaction happens in Shopify's PCI-compliant checkout step).
   *   • Place order → POST all attributes, then redirect to
   *     `/checkout` with the customer's email + PIN preset via
   *     URL params so Shopify pre-fills what it can.
   */
  function initCheckoutPage() {
    const page = document.querySelector('[data-sk-checkout-page]');
    if (!page || page.dataset.skCheckoutInit === '1') return;
    page.dataset.skCheckoutInit = '1';

    const form = page.querySelector('[data-sk-checkout-form]');
    const cfg  = window.SK_CHECKOUT || { threshold: 499800, flatShip: 9900, expressShip: 19900, subtotal: 0 };
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    /* INR formatter (mirror of fmtINR in initCartPage) */
    function fmtINRLocal(cents) {
      const r = Math.max(0, Math.round((cents || 0) / 100));
      const s = String(r);
      let grouped;
      if (s.length <= 3) grouped = s;
      else {
        const last3 = s.slice(-3);
        const rest = s.slice(0, -3);
        grouped = rest.replace(/(\d)(?=(\d{2})+$)/g, '$1,') + ',' + last3;
      }
      return '<span class="sk-money__code">INR</span> ' + grouped;
    }

    /* ── Live shipping + total recompute ───────────────────────────── */
    function shippingForMethod() {
      const radio = page.querySelector('[name="delivery_method"]:checked');
      const method = radio ? radio.value : 'standard';
      if (method === 'express') return cfg.expressShip;
      return cfg.subtotal >= cfg.threshold ? 0 : cfg.flatShip;
    }
    function refreshSummary() {
      const ship = shippingForMethod();
      const grand = cfg.subtotal + ship;
      const shipEl = page.querySelector('[data-sk-checkout-shipping]');
      const totalEl = page.querySelector('[data-sk-checkout-grand-total]');
      const ctaEl = page.querySelector('[data-sk-checkout-total]');
      if (shipEl) {
        if (ship === 0 && cfg.subtotal >= cfg.threshold) {
          shipEl.innerHTML = 'Free · pan-India';
        } else {
          shipEl.innerHTML = fmtINRLocal(ship);
        }
      }
      if (totalEl) totalEl.innerHTML = fmtINRLocal(grand);
      if (ctaEl)  ctaEl.innerHTML  = fmtINRLocal(grand);
    }
    page.querySelectorAll('[name="delivery_method"]').forEach((r) => {
      r.addEventListener('change', refreshSummary);
    });
    refreshSummary();

    /* ── PIN code → auto-fill city + state ─────────────────────────── */
    const pin = page.querySelector('[data-sk-checkout-pin]');
    const pinStatus = page.querySelector('[data-sk-checkout-pin-status]');
    const cityEl = page.querySelector('[data-sk-checkout-city]');
    const stateEl = page.querySelector('[data-sk-checkout-state]');
    if (pin) {
      let pinTimer = null;
      pin.addEventListener('input', () => {
        clearTimeout(pinTimer);
        const v = (pin.value || '').replace(/\D/g, '').slice(0, 6);
        if (v !== pin.value) pin.value = v;
        if (v.length !== 6) {
          if (pinStatus) { pinStatus.textContent = ''; pinStatus.className = 'sk-checkout-pg__pin-status'; }
          return;
        }
        pinTimer = setTimeout(() => {
          if (pinStatus) {
            pinStatus.textContent = 'Looking up…';
            pinStatus.className = 'sk-checkout-pg__pin-status is-loading';
          }
          fetch('https://api.postalpincode.in/pincode/' + encodeURIComponent(v), {
            cache: 'no-store'
          })
            .then((r) => r.ok ? r.json() : null)
            .then((res) => {
              const post = res && Array.isArray(res) && res[0];
              if (!post || post.Status !== 'Success' || !post.PostOffice || !post.PostOffice[0]) {
                if (pinStatus) {
                  pinStatus.textContent = 'PIN not serviceable.';
                  pinStatus.className = 'sk-checkout-pg__pin-status is-error';
                }
                return;
              }
              const po = post.PostOffice[0];
              if (cityEl && !cityEl.value) cityEl.value = po.District || po.Block || po.Name || '';
              if (stateEl && stateEl.value !== po.State) {
                /* Try to match state to dropdown options */
                const options = Array.from(stateEl.options);
                const match = options.find((o) => o.value && o.value.toLowerCase() === (po.State || '').toLowerCase());
                if (match) stateEl.value = match.value;
              }
              if (pinStatus) {
                pinStatus.textContent = '✓ Delivers to ' + (po.District || po.State);
                pinStatus.className = 'sk-checkout-pg__pin-status is-success';
              }
            })
            .catch(() => {
              if (pinStatus) {
                pinStatus.textContent = '';
                pinStatus.className = 'sk-checkout-pg__pin-status';
              }
            });
        }, 350);
      });
      /* Trigger lookup on load if SSR pre-filled a PIN. */
      if (pin.value && pin.value.length === 6) {
        pin.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    /* ── Form submission ──────────────────────────────────────────────
     * Pre-flight HTML5 validity check, then POST all fields as cart
     * attributes via /cart/update.js, then redirect to /checkout with
     * URL params for the fields Shopify can natively pre-fill (email,
     * shipping_address). */
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }
        const submitBtn = form.querySelector('[data-sk-checkout-submit]');
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.classList.add('is-loading');
        }

        /* Build the attributes payload from every [data-sk-field]. */
        const attributes = {};
        form.querySelectorAll('[data-sk-field]').forEach((el) => {
          const k = el.dataset.skField;
          if (!k) return;
          if (el.type === 'checkbox') {
            attributes[k] = el.checked ? 'yes' : '';
          } else if (el.type === 'radio') {
            if (el.checked) attributes[k] = el.value;
          } else {
            attributes[k] = el.value || '';
          }
        });

        /* Save the customer's address book entry if logged-in + opted in. */
        const saveAddrPromise = (function () {
          const cb = form.querySelector('[name="save_address"]');
          if (!cb || !cb.checked) return Promise.resolve();
          /* Shopify exposes /account/addresses for this; we just
             attach an attribute so the merchant can re-create it
             if the customer-account API isn't available. */
          attributes.save_address = 'yes';
          return Promise.resolve();
        })();

        Promise.resolve(saveAddrPromise)
          .then(() => fetch(root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ attributes }),
            cache: 'no-store'
          }))
          .then((r) => r.ok ? r.json() : Promise.reject(r.status))
          .then(() => {
            /* Construct the Shopify checkout URL with pre-fill params.
               Shopify supports `checkout[email]` + `checkout[shipping_address][...]`
               on the cart-permalink and /checkout query strings. */
            const params = new URLSearchParams();
            const email = attributes.contact_email;
            if (email) params.set('checkout[email]', email);
            const map = {
              shipping_first_name: 'first_name',
              shipping_last_name:  'last_name',
              shipping_address1:   'address1',
              shipping_address2:   'address2',
              shipping_city:       'city',
              shipping_zip:        'zip',
              shipping_province:   'province'
            };
            Object.entries(map).forEach(([from, to]) => {
              if (attributes[from]) params.set('checkout[shipping_address][' + to + ']', attributes[from]);
            });
            params.set('checkout[shipping_address][country]', 'IN');
            window.location.href = '/checkout?' + params.toString();
          })
          .catch((err) => {
            console.error('[Sanki] checkout submit failed:', err);
            if (submitBtn) {
              submitBtn.disabled = false;
              submitBtn.classList.remove('is-loading');
            }
            if (H.toast) H.toast('Something went wrong — please retry.');
            /* Last-ditch fallback: go straight to Shopify checkout
               without our attributes attached. The customer can
               re-enter on Shopify's page. */
            setTimeout(() => { window.location.href = '/checkout'; }, 1500);
          });
      });
    }
  }

  /* ──────────────────────────────────────────────────────────────────
     Hero (Vibrant) — mouse parallax on the model image.
     Only activates on hover-capable pointers (skips touch). When the
     cursor moves over the section, the model translates a few pixels
     in the OPPOSITE direction (depth illusion). RAF-throttled so we
     never run the math more than once per frame. Reads opt-in flag
     from `data-sk-hero-v-parallax="1"` so merchants can disable per-
     section in the theme editor.
     ──────────────────────────────────────────────────────────────────
  */
  function initHeroVibrantParallax() {
    if (!window.matchMedia('(hover: hover)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    document.querySelectorAll('[data-sk-hero-v]').forEach((root) => {
      if (root.dataset.skHeroVParallax !== '1') return;
      const visual = root.querySelector('[data-sk-hero-v-visual]');
      const model  = visual && visual.querySelector('.sk-hero-v__model');
      if (!visual || !model) return;

      let rafId = 0;
      let pending = null;
      const apply = () => {
        if (!pending) return;
        const { px, py } = pending;
        pending = null;
        const dx = (px - 0.5) * -16;     /* px shifts model 16px max each direction */
        const dy = (py - 0.5) * -10;
        model.style.transform = 'translate(' + dx + 'px, ' + dy + 'px)';
        rafId = 0;
      };

      root.addEventListener('pointermove', (e) => {
        const r = root.getBoundingClientRect();
        pending = {
          px: (e.clientX - r.left) / r.width,
          py: (e.clientY - r.top)  / r.height
        };
        if (!rafId) rafId = requestAnimationFrame(apply);
      });

      root.addEventListener('pointerleave', () => {
        pending = null;
        if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
        model.style.transform = '';
      });
    });
  }

  ready(() => {
    initInnerCircle();
    initMobileDrawer();
    initCollectionFilters();
    initCollistFilter();
    initOpenCollectionsMega();
    initCartDrawer();
    initCartPage();
    initCheckoutPage();
    initQuickAdd();
    initWishlist();
    initWishlistPage();
    initPredictiveSearch();
    initImageSearch();
    initTypeRows();
    initProductPage();
    initProductReviews(); /* PDP synthetic reviews — early-returns when shell isn't in DOM */
    initSpinWin();        /* Legacy — early-returns when modal isn't in DOM */
    initCubeDiscount();   /* Replacement: 3D cube unscramble experience */
    /* Hijack must run BEFORE initPolicyPage so the latter finds our shell
       in the DOM. This rescues policy pages where Shopify rendered the
       default `.shopify-policy__container` instead of our template. */
    initPolicyHijack();
    initPolicyPage();
    initSizeGuide();
    initHeroVibrantParallax();
    initStickyATC();
    initFooterAccordion();
    initTrustBadgeSanitiser();
    /* Run reveal observer LAST so it picks up every [data-sk-reveal]
       node injected by other init functions (e.g. our policy hijack). */
    initRevealObserver();
  });

  document.addEventListener('shopify:section:load', (e) => {
    // Re-init anything that depends on new DOM within a section.
    if (e.target.querySelector('[data-sk-menu-drawer]') || e.target.querySelector('[data-sk-menu-toggle]')) {
      // Force a re-init by clearing the idempotent flag on reloaded DOM.
      const d = document.querySelector('[data-sk-menu-drawer]');
      if (d) delete d.dataset.skDrawerInit;
      initMobileDrawer();
    }
    if (e.target.querySelector('[data-sk-collection-form]')) {
      const f = document.querySelector('[data-sk-collection-form]');
      if (f) delete f.dataset.skFiltersInit;
      initCollectionFilters();
    }
    if (e.target.querySelector('[data-sk-product]')) initProductPage();
    if (e.target.querySelector('[data-sk-reviews]')) {
      const r = e.target.querySelector('[data-sk-reviews]');
      if (r) delete r.dataset.skInit;
      initProductReviews();
    }
    if (e.target.querySelector('[data-sk-cart-drawer]')) initCartDrawer();
    if (e.target.querySelector('[data-sk-cart-page]')) {
      const cp = e.target.querySelector('[data-sk-cart-page]');
      if (cp) delete cp.dataset.skCartPageInit;
      initCartPage();
    }
    if (e.target.querySelector('[data-sk-collist]')) {
      const cl = e.target.querySelector('[data-sk-collist]');
      if (cl) delete cl.dataset.skCollistInit;
      initCollistFilter();
    }
    if (e.target.querySelector('[data-sk-policy]')) {
      const pol = e.target.querySelector('[data-sk-policy]');
      if (pol) delete pol.dataset.skPolicyInit;
      initPolicyPage();
    }
    initQuickAdd();
    initWishlist();
    initRevealObserver();
  });
})(typeof window !== 'undefined' ? window : this);
